'use client'
import {
  PasswordBullets_default
} from "./chunk-LQCB7533.mjs";
import {
  getDefaultUser
} from "./chunk-BPVXCQOF.mjs";
import {
  LENGTH_8_PATTERN,
  ONE_LETTER_PATTERN,
  ONE_NUMBER_PATTERN,
  isPasswordValid
} from "./chunk-SQJGLUQL.mjs";
import {
  getSocialRedirect
} from "./chunk-5KLY43LU.mjs";
import {
  MIN_AGE_ALLOWED
} from "./chunk-Z6DGEBFH.mjs";
import {
  COOKIE_REFRESH_TOKEN,
  COOKIE_TOKEN,
  COOKIE_TOKEN_EXPIRES_IN
} from "./chunk-5KF4VIOG.mjs";
import {
  context_default,
  useAuth
} from "./chunk-IOW6DR7W.mjs";
import {
  useEmailSpelling
} from "./chunk-AA6G4D5D.mjs";
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/checkpoint/fanId/FanIdCheckpoint.tsx
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AnimatePresence, motion } from "motion/react";
import { twMerge } from "tailwind-merge";
import { Button, Input, OtpInputs, Paper, Typography } from "@wbk/ui";
import { WithGrecaptcha } from "@wbk/api/grecaptcha";
import { useRouter, useTranslation } from "@wbk/config";
import { useSendOtpToFanId, useVerifyFanIdOtp } from "@wbk/api/hooks";

// src/components/countdown/WithCountDown.tsx
import { useCountDown } from "@wbk/hooks";
var WithCountDown = ({ render, seconds }) => {
  const { count, reset, set } = useCountDown({ id: "withcount-down", initialSeconds: seconds });
  return render({ count, reset, set });
};
var WithCountDown_default = WithCountDown;

// src/components/checkpoint/fanId/FanIdCheckpoint.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var FanIdCheckPoint = ({ className }) => {
  const { t } = useTranslation(["auth_common"]);
  const { locale } = useRouter();
  const [sentFanId, setSentFanId] = useState(null);
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: { fan_id: "", otp: "", mode: "fan-id" }
  });
  const mode = watch("mode");
  const { mutateAsync: sendOtp, isPending: sending, error: sendError } = useSendOtpToFanId();
  const {
    mutateAsync: verifyOtp,
    isPending: verifying,
    error: verifyError
  } = useVerifyFanIdOtp(locale);
  const handleSendOtp = (data) => __async(null, null, function* () {
    const ok = yield sendOtp({ fan_id: data.fan_id });
    if (ok) {
      setSentFanId(data.fan_id);
      setValue("mode", "otp");
    }
  });
  const handleVerifyOtp = (data) => __async(null, null, function* () {
    yield verifyOtp({ fan_id: data.fan_id, otp: data.otp });
  });
  const handleResendOtp = () => __async(null, null, function* () {
    if (!sentFanId) return;
    yield sendOtp({ fan_id: sentFanId });
  });
  return /* @__PURE__ */ jsx(WithGrecaptcha, { children: /* @__PURE__ */ jsx(Paper, { className: twMerge("bg-paper m-4 overflow-hidden", className), children: /* @__PURE__ */ jsx(AnimatePresence, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx(
    motion.div,
    {
      initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
      transition: { duration: 0.25 },
      children: mode === "fan-id" ? /* @__PURE__ */ jsxs("form", { className: "space-y-6", onSubmit: handleSubmit(handleSendOtp), children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-XS", children: [
          /* @__PURE__ */ jsx(Typography, { variant: "heading-M", strong: true, children: t("auth_common:fan_id.verify_your_fan_id") }),
          /* @__PURE__ */ jsx(Typography, { className: "pb-2", children: t("auth_common:fan_id.link_fan_id_note") })
        ] }),
        /* @__PURE__ */ jsx(
          Controller,
          {
            control,
            name: "fan_id",
            rules: {
              required: t("auth_common:validation.required"),
              minLength: {
                value: 8,
                message: t("auth_common:validation.min_length", { min: 8 })
              }
            },
            render: ({ field }) => {
              var _a;
              return /* @__PURE__ */ jsx(
                Input,
                __spreadProps(__spreadValues({}, field), {
                  label: t("auth_common:fan_id.fan_id_number"),
                  placeholder: "XXXXXXXX",
                  className: "text-text",
                  error: (_a = errors.fan_id) == null ? void 0 : _a.message
                })
              );
            }
          }
        ),
        (sendError == null ? void 0 : sendError.message) && /* @__PURE__ */ jsx("p", { className: "text-error", children: sendError.message }),
        /* @__PURE__ */ jsx(Button, { className: "w-full", loading: sending, type: "submit", children: /* @__PURE__ */ jsx(Typography, { variant: "heading-XS", strong: true, children: t("auth_common:send_verification_code") }) })
      ] }) : /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-XS", children: [
          /* @__PURE__ */ jsx(Typography, { variant: "heading-M", strong: true, children: t("auth_common:fan_id.verify_otp") }),
          /* @__PURE__ */ jsx(Typography, { className: "pb-2", children: t("auth_common:fan_id.enter_code_sent_to_email") })
        ] }),
        /* @__PURE__ */ jsx(
          OtpInputs,
          {
            numOfInput: 6,
            formId: "fan-id-verify-form",
            onChange: (otp) => setValue("otp", otp),
            onSubmit: (otp) => __async(null, null, function* () {
              setValue("otp", otp);
              handleVerifyOtp({ fan_id: sentFanId || "", otp });
            }),
            onlyNumbers: true,
            renderButton: ({ resetState }) => /* @__PURE__ */ jsx("div", { className: "mt-L text-center text-sm", children: /* @__PURE__ */ jsx(
              WithCountDown_default,
              {
                seconds: 60,
                render: ({ count, reset }) => /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "button",
                    shape: "text",
                    theme: "action",
                    className: "shrink-0 !bg-transparent px-2 py-1 text-sm underline",
                    disabled: count > 0 || sending || verifying,
                    onClick: () => __async(null, null, function* () {
                      yield handleResendOtp();
                      reset();
                      resetState();
                    }),
                    children: `${t("auth_common:resend_otp")} ${count > 0 ? `(${count})` : ""}`
                  }
                )
              }
            ) })
          }
        ),
        (verifyError == null ? void 0 : verifyError.message) && /* @__PURE__ */ jsx("p", { className: "text-error", children: verifyError.message }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-S", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              form: "fan-id-verify-form",
              className: "w-full",
              loading: verifying,
              children: /* @__PURE__ */ jsx(Typography, { variant: "heading-XS", strong: true, children: t("auth_common:verify") })
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              theme: "black",
              shape: "outlined",
              className: "w-full",
              onClick: () => {
                setValue("mode", "fan-id");
                setSentFanId(null);
              },
              children: /* @__PURE__ */ jsx(Typography, { variant: "heading-XS", strong: true, children: t("auth_common:change_id") })
            }
          )
        ] })
      ] })
    },
    `${mode}-${locale}`
  ) }) }) });
};

// src/components/checkpoint/global/index.tsx
import { useEffect as useEffect10, useRef as useRef4, useState as useState7 } from "react";
import { Button as Button25, ResponsiveModal as ResponsiveModal3, Typography as Typography20 } from "@wbk/ui";
import { FormProvider as FormProvider3, useForm as useForm4 } from "react-hook-form";
import { AnimatePresence as AnimatePresence9, motion as motion9 } from "motion/react";
import { useRouter as useRouter29, useTranslation as useTranslation35 } from "@wbk/config";
import { ExclamationTriangleIcon } from "@wbk/svg";
import { useAnalytics as useAnalytics18 } from "@wbk/analytics";

// src/context/provider.tsx
import { useEffect as useEffect8, useMemo as useMemo7, useState as useState6 } from "react";
import { ApiConfig } from "@wbk/api";
import { useGetUser as useGetUser2, useGetUserSubscriptions, useRefreshTokens } from "@wbk/api/hooks";
import { createCookie, eraseCookie, readCookie } from "@wbk/utils";
import { useAnalytics as useAnalytics17 } from "@wbk/analytics";
import { useRouter as useRouter27, useSubscriptionConfig } from "@wbk/config";
import { useQueue } from "@wbk/queue";

// src/components/form/AuthForm.tsx
import { FormProvider, useForm as useForm2 } from "react-hook-form";
import { AnimatePresence as AnimatePresence8, motion as motion8 } from "motion/react";
import { WithGrecaptcha as WithGrecaptcha2 } from "@wbk/api/grecaptcha";
import { Paper as Paper3 } from "@wbk/ui";
import { useAuthConfig, useGeolocation as useGeolocation5, useRouter as useRouter19 } from "@wbk/config";
import { twMerge as twMerge7 } from "tailwind-merge";

// src/components/form/engines/AuthV2Engine.tsx
import { useEffect as useEffect5, useMemo as useMemo2 } from "react";
import { useAnalytics as useAnalytics9 } from "@wbk/analytics";
import { useRouter as useRouter12 } from "@wbk/config";

// src/components/form/v2/SigninEmail.tsx
import { useEffect as useEffect3 } from "react";
import { useFormContext as useFormContext2 } from "react-hook-form";
import { Typography as Typography7 } from "@wbk/ui";
import { useRouter as useRouter5, useTranslation as useTranslation7 } from "@wbk/config";
import { EMAIL_PATTERN as EMAIL_PATTERN2 } from "@wbk/utils";
import { useCheckEmail } from "@wbk/api/hooks";
import { useAnalytics as useAnalytics3 } from "@wbk/analytics";

// src/components/form/v2/EmailSection.tsx
import { Controller as Controller2, useFormContext } from "react-hook-form";
import { Button as Button4, Input as Input2, Paper as Paper2, Typography as Typography5 } from "@wbk/ui";
import { EMAIL_PATTERN } from "@wbk/utils";
import { Link, Trans, useGeolocation as useGeolocation2, useRouter as useRouter4, useTranslation as useTranslation5 } from "@wbk/config";
import { AnimatePresence as AnimatePresence2, motion as motion2 } from "motion/react";

// src/components/form/SocialLogin.tsx
import { Divider, Typography as Typography4 } from "@wbk/ui";
import { useGeolocation, useTranslation as useTranslation4 } from "@wbk/config";
import { useSocialEnabled } from "@wbk/api/hooks";

// src/components/form/GoogleLogin.tsx
import { useCallback, useEffect, useRef, useState as useState2 } from "react";
import { loadScriptAsync } from "@wbk/utils";
import { useRouter as useRouter2, useTranslation as useTranslation2 } from "@wbk/config";
import { GoogleIcon } from "@wbk/svg";
import { Button as Button2, Typography as Typography2 } from "@wbk/ui";
import { useAnalytics } from "@wbk/analytics";
import { Exception } from "@wbk/logger";
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
var SCRIPT_ID = "google-login-sdk";
var CAN_RUN = typeof window !== "undefined";
var GOOGLE_SCOPE = "openid email profile";
var GoogleLogin = ({ className = "", onError, onClick, disabled }) => {
  var _a, _b, _c, _d;
  const { t } = useTranslation2(["auth_common"]);
  const { locale, push, search, pathname } = useRouter2();
  const { trackEvent } = useAnalytics();
  const { social } = useAuth();
  const clientId = (_b = (_a = social == null ? void 0 : social.google) == null ? void 0 : _a.clientId) != null ? _b : "";
  const redirectUri = (_d = (_c = social == null ? void 0 : social.google) == null ? void 0 : _c.redirectUri) != null ? _d : "/auth/google/callback";
  const redirect = getSocialRedirect(search, pathname);
  const codeClientRef = useRef(null);
  const [sdkReady, setSdkReady] = useState2(false);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2();
  const initGoogleSDK = useCallback(() => {
    var _a2, _b2;
    try {
      if (!CAN_RUN || !((_b2 = (_a2 = window.google) == null ? void 0 : _a2.accounts) == null ? void 0 : _b2.oauth2)) {
        throw new Exception({
          error: new Error("Google SDK not available"),
          name: "GoogleSDKNotAvailableException"
        });
      }
      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: GOOGLE_SCOPE,
        ux_mode: "popup",
        // Fires with the authorization code on success.
        callback: (response) => {
          setLoading(false);
          if (response.error || !response.code) {
            const msg = new Error(
              response.error_description || response.error || t("auth_common:sign_in.google_unavailable")
            );
            trackEvent({ event: "login_failed", data: { method: "google", error: msg } });
            setError(msg);
            onError == null ? void 0 : onError(msg);
            return;
          }
          let url = `/${locale}${redirectUri}?code=${encodeURIComponent(response.code)}`;
          if (redirect) url += `&redirect=${encodeURIComponent(redirect)}`;
          push(url);
        },
        error_callback: (err) => {
          setLoading(false);
          if ((err == null ? void 0 : err.type) === "popup_closed") return;
          const msg = new Error((err == null ? void 0 : err.type) || "Google sign-in failed");
          trackEvent({ event: "login_failed", data: { method: "google", error: msg } });
          setError(msg);
          onError == null ? void 0 : onError(msg);
        }
      });
      setSdkReady(true);
    } catch (err) {
      const error2 = err;
      setError(error2);
      onError == null ? void 0 : onError(error2);
    }
  }, [clientId, locale, redirectUri, redirect, push, onError, t, trackEvent]);
  useEffect(() => {
    if (!clientId) return;
    loadScriptAsync({ id: SCRIPT_ID, url: GOOGLE_SCRIPT_SRC, target: "head" }).then(() => initGoogleSDK()).catch((scriptError) => {
      const error2 = new Error("Failed to load Google Sign-In");
      new Exception({
        error: error2,
        name: "GoogleSDKLoadException",
        context: { tags: { journey: "auth" }, extra: { scriptError } }
      });
      setError(error2);
      onError == null ? void 0 : onError(error2);
    });
    return () => {
      var _a2;
      (_a2 = document.getElementById(SCRIPT_ID)) == null ? void 0 : _a2.remove();
    };
  }, [clientId]);
  const handleClick = () => {
    if (!codeClientRef.current || !sdkReady) return;
    onClick == null ? void 0 : onClick();
    setError(void 0);
    setLoading(true);
    trackEvent({
      event: "login_continue_with_google_clicked",
      data: { method: "google", entry_point: pathname }
    });
    codeClientRef.current.requestCode();
  };
  if (!clientId) return null;
  return /* @__PURE__ */ jsxs2("div", { className: "flex w-full flex-col gap-0.5", children: [
    /* @__PURE__ */ jsxs2(
      Button2,
      {
        type: "button",
        theme: "white",
        className: twMerge2("py-3", className),
        onClick: handleClick,
        disabled: !sdkReady || disabled,
        loading,
        children: [
          /* @__PURE__ */ jsx2(GoogleIcon, { className: "h-5 w-5" }),
          /* @__PURE__ */ jsx2(Typography2, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.continue_with_google") })
        ]
      }
    ),
    error instanceof Error && /* @__PURE__ */ jsx2(Typography2, { as: "span", variant: "label-XS", color: "error", children: error.message })
  ] });
};
var GoogleLogin_default = GoogleLogin;

// src/components/form/AppleLogin.tsx
import { useCallback as useCallback2, useEffect as useEffect2, useState as useState3 } from "react";
import { loadScriptAsync as loadScriptAsync2 } from "@wbk/utils";
import { useRouter as useRouter3, useTranslation as useTranslation3 } from "@wbk/config";
import { AppleIcon } from "@wbk/svg";
import { Button as Button3, Typography as Typography3 } from "@wbk/ui";
import { useAnalytics as useAnalytics2 } from "@wbk/analytics";
import { Exception as Exception2 } from "@wbk/logger";
import { twMerge as twMerge3 } from "tailwind-merge";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var APPLE_SCRIPT_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
var SCRIPT_ID2 = "apple-login-sdk";
var CAN_RUN2 = typeof window !== "undefined";
var AppleLogin = ({ className = "", onError, onClick, disabled }) => {
  var _a, _b, _c, _d;
  const { t } = useTranslation3(["auth_common"]);
  const { push, search, pathname } = useRouter3();
  const { trackEvent } = useAnalytics2();
  const { social } = useAuth();
  const clientId = (_b = (_a = social == null ? void 0 : social.apple) == null ? void 0 : _a.clientId) != null ? _b : "";
  const redirectUri = (_d = (_c = social == null ? void 0 : social.apple) == null ? void 0 : _c.redirectUri) != null ? _d : "/auth/apple/callback";
  const redirect = getSocialRedirect(search, pathname);
  const [sdkReady, setSdkReady] = useState3(false);
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3();
  const initAppleSDK = useCallback2(() => {
    try {
      if (!CAN_RUN2 || !window.AppleID) {
        throw new Exception2({
          error: new Error("AppleID SDK not available"),
          name: "AppleSDKNotAvailableException"
        });
      }
      window.AppleID.auth.init({
        clientId,
        scope: "name email",
        redirectURI: `${window.location.origin}${redirectUri}`,
        state: "origin:web",
        usePopup: true
      });
      setSdkReady(true);
    } catch (err) {
      const error2 = err;
      setError(error2);
      onError == null ? void 0 : onError(error2);
    }
  }, [clientId, redirectUri, onError]);
  useEffect2(() => {
    if (!clientId) return;
    loadScriptAsync2({ id: SCRIPT_ID2, url: APPLE_SCRIPT_SRC, target: "head" }).then(() => initAppleSDK()).catch((scriptError) => {
      const error2 = new Error("Failed to load Apple Sign-In");
      new Exception2({
        error: error2,
        name: "AppleSDKLoadException",
        context: { tags: { journey: "auth" }, extra: { scriptError } }
      });
      setError(error2);
      onError == null ? void 0 : onError(error2);
    });
    return () => {
      var _a2;
      (_a2 = document.getElementById(SCRIPT_ID2)) == null ? void 0 : _a2.remove();
    };
  }, [clientId]);
  const handleClick = () => __async(null, null, function* () {
    var _a2, _b2, _c2, _d2;
    if (!CAN_RUN2 || !window.AppleID || !sdkReady) return;
    onClick == null ? void 0 : onClick();
    setLoading(true);
    setError(void 0);
    trackEvent({
      event: "login_continue_with_apple_clicked",
      data: { method: "apple", entry_point: pathname }
    });
    try {
      const res = yield window.AppleID.auth.signIn();
      let url = `${redirectUri}?token=${res.authorization.id_token}`;
      const firstName = (_b2 = (_a2 = res.user) == null ? void 0 : _a2.name) == null ? void 0 : _b2.firstName;
      const lastName = (_d2 = (_c2 = res.user) == null ? void 0 : _c2.name) == null ? void 0 : _d2.lastName;
      if (firstName) url += `&first_name=${encodeURIComponent(firstName)}`;
      if (lastName) url += `&last_name=${encodeURIComponent(lastName)}`;
      if (redirect) url += `&redirect=${encodeURIComponent(redirect)}`;
      push(url);
    } catch (err) {
      const error2 = err;
      if ((err == null ? void 0 : err.error) !== "popup_closed_by_user") {
        trackEvent({ event: "login_failed", data: { method: "apple", error: error2 } });
      }
      setError(error2);
      onError == null ? void 0 : onError(error2);
      setLoading(false);
    }
  });
  if (!clientId) return null;
  return /* @__PURE__ */ jsxs3("div", { className: "flex w-full flex-col gap-0.5", children: [
    /* @__PURE__ */ jsxs3(
      Button3,
      {
        type: "button",
        theme: "white",
        className: twMerge3("py-3", className),
        onClick: handleClick,
        disabled: !sdkReady || disabled,
        loading,
        children: [
          /* @__PURE__ */ jsx3(AppleIcon, { className: "h-5 w-5" }),
          /* @__PURE__ */ jsx3(Typography3, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.continue_with_apple") })
        ]
      }
    ),
    error instanceof Error && /* @__PURE__ */ jsx3(Typography3, { as: "span", variant: "label-XS", color: "error", children: error.message })
  ] });
};
var AppleLogin_default = AppleLogin;

// src/components/form/SocialLogin.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var SocialLogin = ({ disabled }) => {
  const { t } = useTranslation4("auth_common");
  const { countryCode } = useGeolocation();
  const { social } = useAuth();
  const hasSocialConfig = !!((social == null ? void 0 : social.google) || (social == null ? void 0 : social.apple));
  const { data, isLoading, isError } = useSocialEnabled(
    {
      country: countryCode,
      platform: "web"
    },
    { enabled: hasSocialConfig }
  );
  const isApple = data == null ? void 0 : data.apple;
  const isGoogle = data == null ? void 0 : data.google;
  const isEnabled = isApple && isGoogle;
  if (isLoading) {
    return /* @__PURE__ */ jsxs4("div", { className: "animate-pulse space-y-3", "data-testid": "auth_social_skeleton", children: [
      /* @__PURE__ */ jsx4("div", { className: "bg-body-dark/10 h-12 w-full rounded-lg" }),
      /* @__PURE__ */ jsx4("div", { className: "bg-body-dark/10 mx-auto h-4 w-16 rounded pt-4" })
    ] });
  }
  if (isError || !isEnabled) {
    return null;
  }
  return /* @__PURE__ */ jsxs4("div", { className: "space-y-3", children: [
    isApple && /* @__PURE__ */ jsx4(AppleLogin_default, { disabled }),
    isGoogle && /* @__PURE__ */ jsx4(GoogleLogin_default, { disabled }),
    /* @__PURE__ */ jsxs4("div", { className: "relative flex items-center pt-4", children: [
      /* @__PURE__ */ jsx4(Divider, { className: "flex-1" }),
      /* @__PURE__ */ jsx4(Typography4, { variant: "label-M", className: "px-4", children: t("auth_common:sign_in.or") }),
      /* @__PURE__ */ jsx4(Divider, { className: "flex-1" })
    ] })
  ] });
};
var SocialLogin_default = SocialLogin;

// src/components/form/v2/EmailSection.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var EmailSection = ({ isChecking, error }) => {
  const { t } = useTranslation5("auth_common");
  const { locale } = useRouter4();
  const { supportedCountryCode } = useGeolocation2();
  const {
    control,
    formState: { errors },
    watch,
    setValue
  } = useFormContext();
  const email = watch("email");
  const emailSuggestion = useEmailSpelling({ email });
  return /* @__PURE__ */ jsxs5("div", { className: "space-y-5", children: [
    /* @__PURE__ */ jsx5(SocialLogin_default, { disabled: isChecking }),
    /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx5(
        Controller2,
        {
          control,
          name: "email",
          rules: {
            required: t("auth_common:validation.required"),
            pattern: {
              value: EMAIL_PATTERN,
              message: t("auth_common:validation.invalid_email")
            }
          },
          defaultValue: "",
          render: (_a) => {
            var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
            var _a2;
            return /* @__PURE__ */ jsx5(
              Input2,
              __spreadProps(__spreadValues({}, props), {
                "data-testid": "auth_email_input",
                type: "email",
                label: /* @__PURE__ */ jsx5("span", { className: "font-semibold", children: t("auth_common:email") }),
                placeholder: "you@email.com",
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a2 = errors.email) == null ? void 0 : _a2.message) || error,
                disabled: isChecking
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx5(AnimatePresence2, { initial: false, children: emailSuggestion && /* @__PURE__ */ jsx5(
        motion2.div,
        {
          initial: { height: 0, opacity: 0 },
          animate: { height: "auto", opacity: 1 },
          exit: { height: 0, opacity: 0 },
          transition: { duration: 0.15 },
          className: "overflow-hidden",
          children: /* @__PURE__ */ jsxs5(Paper2, { surface: "level-1", className: "flex items-center gap-1 !p-2", children: [
            /* @__PURE__ */ jsx5(Typography5, { variant: "label-S", children: t("auth_common:validation.did_you_mean") }),
            /* @__PURE__ */ jsx5(
              Button4,
              {
                type: "button",
                shape: "text",
                onClick: () => setValue("email", emailSuggestion.full, {
                  shouldValidate: true
                }),
                className: "p-0.5 hover:underline",
                children: /* @__PURE__ */ jsx5(Typography5, { variant: "label-S", children: emailSuggestion.full })
              }
            )
          ] })
        },
        "email-suggestion"
      ) })
    ] }),
    /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-center", children: [
      /* @__PURE__ */ jsx5(Button4, { type: "submit", className: "w-full py-3", loading: isChecking, children: /* @__PURE__ */ jsx5(Typography5, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.continue_with_email") }) }),
      /* @__PURE__ */ jsx5("div", { className: "border-paper mt-4 w-full border-t pt-2 text-center", children: /* @__PURE__ */ jsx5(Typography5, { variant: "body-XS", color: "text-secondary", children: /* @__PURE__ */ jsx5(Trans, { i18nKey: "sign_in.terms_agree", ns: "auth_common", children: /* @__PURE__ */ jsx5(
        Button4,
        {
          as: Link,
          shape: "text",
          theme: "action",
          "data-testid": "auth_terms_page_link",
          className: "text-text-secondary px-0.5 py-0 font-semibold underline",
          href: `/${locale}/terms${supportedCountryCode ? `?country_code=${supportedCountryCode}` : ""}`,
          target: "_blank",
          rel: "noreferrer",
          children: " "
        }
      ) }) }) })
    ] })
  ] });
};
var EmailSection_default = EmailSection;

// src/components/form/v2/AuthWbkHeader.tsx
import { useTranslation as useTranslation6 } from "@wbk/config";
import { WbkLogo } from "@wbk/svg";
import { Typography as Typography6 } from "@wbk/ui";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var AuthWbkHeader = () => {
  const { t } = useTranslation6("auth_common");
  const { poweredBy } = useAuth();
  return poweredBy ? /* @__PURE__ */ jsxs6("div", { className: "text-text text-center", children: [
    /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-center gap-2", children: [
      /* @__PURE__ */ jsx6(Typography6, { variant: "body-XS", children: t("auth_common:powered") }),
      /* @__PURE__ */ jsx6(WbkLogo, { width: 45, height: 14, className: "text-[#F84878]" })
    ] }),
    /* @__PURE__ */ jsx6(Typography6, { variant: "body-S", children: t("auth_common:wbk_login") })
  ] }) : /* @__PURE__ */ jsx6(WbkLogo, { width: 80, height: 25, className: "text-[#F84878]" });
};
var AuthWbkHeader_default = AuthWbkHeader;

// src/components/form/v2/SigninEmail.tsx
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var SigninEmail = ({ title }) => {
  const { t } = useTranslation7("auth_common");
  const { setTokens } = useAuth();
  const { search, locale } = useRouter5();
  const { handleSubmit, setValue, setError } = useFormContext2();
  const { mutateAsync: checkEmail, isPending } = useCheckEmail();
  const { trackEvent } = useAnalytics3();
  const onSubmit = (val) => __async(null, null, function* () {
    var _a;
    const email = ((_a = val == null ? void 0 : val.email) == null ? void 0 : _a.trim()) || "";
    try {
      const result = yield checkEmail({ email, locale });
      trackEvent({
        event: result.exists ? "login_continue_with_email_clicked" : "signup_continue_with_email_clicked",
        // domain only, the full address shouldn't reach a tracker
        data: { email_domain: email.split("@")[1] }
      });
      if (!result.exists) return setValue("mode", "signup");
      if (result.has_password) return setValue("mode", "signin-password");
      setValue("mode", "reset-password-email");
    } catch (err) {
      setError("email", { message: err == null ? void 0 : err.message });
    }
  });
  useEffect3(() => {
    var _a, _b;
    if (typeof window === "undefined") return;
    const token = (_a = search.get("token")) != null ? _a : "";
    if (token) {
      setTokens({ accessToken: token });
    }
    const encodedEmail = (_b = search.get("email")) != null ? _b : "";
    const email = decodeURIComponent(encodedEmail);
    const isValidEmail = EMAIL_PATTERN2.test(email);
    if (email && isValidEmail) {
      setValue("email", email);
    }
  }, []);
  return /* @__PURE__ */ jsxs7("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-5", children: [
    /* @__PURE__ */ jsxs7("div", { className: "flex flex-col items-center justify-center gap-2.5", children: [
      /* @__PURE__ */ jsx7(AuthWbkHeader_default, {}),
      /* @__PURE__ */ jsx7(Typography7, { variant: "heading-M", children: title || t("auth_common:sign_in.sign_in_or_create_account") })
    ] }),
    /* @__PURE__ */ jsx7(EmailSection_default, { isChecking: isPending })
  ] });
};
var SigninEmail_default = SigninEmail;

// src/components/form/v2/SigninPassword.tsx
import { Controller as Controller3, useFormContext as useFormContext4 } from "react-hook-form";
import { Button as Button6, Input as Input3, Typography as Typography8 } from "@wbk/ui";
import { useAppSource, useRouter as useRouter6, useTranslation as useTranslation8 } from "@wbk/config";
import { useAnalytics as useAnalytics4 } from "@wbk/analytics";
import { useForgotPassword, useLogin } from "@wbk/api/hooks";
import { setLoggerUser } from "@wbk/logger";

// src/components/button/AuthBackButton.tsx
import { useFormContext as useFormContext3 } from "react-hook-form";
import { Button as Button5 } from "@wbk/ui";
import { twMerge as twMerge4 } from "tailwind-merge";
import { ChevronIcon } from "@wbk/svg";
import { jsx as jsx8 } from "react/jsx-runtime";
var AuthBackButton = ({ mode, onClick, className = "" }) => {
  const { setValue } = useFormContext3();
  const handleClick = () => {
    setValue("mode", mode);
    onClick == null ? void 0 : onClick();
  };
  return /* @__PURE__ */ jsx8(
    Button5,
    {
      onClick: handleClick,
      type: "button",
      theme: "action",
      shape: "text",
      className: twMerge4(
        "bg-paper-level-1 hover:bg-paper-level-2 active:bg-paper-level-1 flex h-8 w-8 items-center gap-2 rounded-full p-0.5",
        className
      ),
      children: /* @__PURE__ */ jsx8(ChevronIcon, { className: "ltr:rotate-180 rtl:rotate-0" })
    }
  );
};
var AuthBackButton_default = AuthBackButton;

// src/components/form/v2/SigninPassword.tsx
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var SigninPassword = ({ onSuccess }) => {
  const { t } = useTranslation8("auth_common");
  const { loginEvent, trackEvent } = useAnalytics4();
  const { appSource } = useAppSource();
  const { setTokens } = useAuth();
  const { locale, pathname } = useRouter6();
  const { mutateAsync: login, isPending, error } = useLogin({ lang: locale });
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues
  } = useFormContext4();
  const { mutateAsync: mutateOTP, isPending: isSending, error: sendingError } = useForgotPassword();
  const errorMsg = (error == null ? void 0 : error.message) || (sendingError == null ? void 0 : sendingError.message);
  const isLoading = isPending || isSending;
  const email = getValues("email");
  const onSubmit = (data) => __async(null, null, function* () {
    var _a, _b, _c, _d;
    const body = { email, locale, app_source: appSource };
    try {
      const res = yield login(__spreadProps(__spreadValues({}, body), { password: data.password }));
      setTokens({
        accessToken: (_a = res == null ? void 0 : res.user) == null ? void 0 : _a.access_token,
        refreshToken: (_b = res == null ? void 0 : res.user) == null ? void 0 : _b.refresh_token,
        tokenExpiresIn: (_c = res == null ? void 0 : res.user) == null ? void 0 : _c.token_expires_in
      });
      if ((_d = res == null ? void 0 : res.user) == null ? void 0 : _d.force_verify_email) {
        try {
          yield mutateOTP(__spreadProps(__spreadValues({}, body), { checkout_verify: true }));
        } catch (error2) {
          trackEvent({ event: "otp_failed", data: { channel: "email", error: error2 } });
          return;
        }
        trackEvent({ event: "otp_requested", data: { channel: "email" } });
        setValue("mode", "verify-otp");
        return;
      }
      if (onSuccess) {
        onSuccess(res.user);
      }
      loginEvent({
        method: "email",
        user: __spreadProps(__spreadValues({}, res.user), { locale })
      });
      setLoggerUser(res.user);
    } catch (error2) {
      trackEvent({ event: "login_failed", data: { method: "email", error: error2 } });
    }
  });
  return /* @__PURE__ */ jsxs8("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-5", children: [
    /* @__PURE__ */ jsx9(AuthBackButton_default, { mode: "signin-email" }),
    /* @__PURE__ */ jsxs8("div", { className: "flex flex-col items-center justify-center gap-3", children: [
      /* @__PURE__ */ jsx9(AuthWbkHeader_default, {}),
      /* @__PURE__ */ jsxs8("div", { className: "space-y-1 text-center", children: [
        /* @__PURE__ */ jsx9(Typography8, { variant: "heading-M", children: t("auth_common:sign_in.sign_in_with") }),
        /* @__PURE__ */ jsx9(Typography8, { children: email })
      ] })
    ] }),
    /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx9(
        Controller3,
        {
          control,
          name: "password",
          rules: {
            required: t("auth_common:validation.required")
          },
          defaultValue: "",
          render: (_a) => {
            var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
            var _a2;
            return /* @__PURE__ */ jsx9(
              Input3,
              __spreadProps(__spreadValues({}, props), {
                id: "password",
                "data-testid": "auth_login_password_input",
                type: "password",
                label: t("auth_common:password"),
                onChange: (value) => {
                  onChange(value);
                },
                error: (_a2 = errors.password) == null ? void 0 : _a2.message,
                hideErrorSpace: true
              })
            );
          }
        },
        "login-password"
      ),
      /* @__PURE__ */ jsx9(Button6, { type: "submit", className: "w-full py-3", loading: isLoading, children: /* @__PURE__ */ jsx9(Typography8, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.sign_in") }) }),
      /* @__PURE__ */ jsx9("div", { className: "flex flex-col gap-5 pt-2.5", children: /* @__PURE__ */ jsx9(
        Button6,
        {
          disabled: isLoading,
          theme: "action",
          shape: "text",
          type: "button",
          "data-testid": "auth_reset_password",
          onClick: () => {
            setValue("password", "");
            setValue("mode", "reset-password-email");
            trackEvent({
              event: "forgot_password_clicked",
              data: { entry_point: pathname }
            });
          },
          children: /* @__PURE__ */ jsx9(Typography8, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.forgot_password") })
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx9("div", { className: "text-center", id: "v2-captcha-section" }),
    !!errorMsg && /* @__PURE__ */ jsx9(Typography8, { color: "error", className: "text-center", variant: "body-S", children: errorMsg })
  ] });
};
var SigninPassword_default = SigninPassword;

// src/components/form/v2/Signup.tsx
import { Controller as Controller5, useFormContext as useFormContext6 } from "react-hook-form";
import { AnimatePresence as AnimatePresence3, motion as motion3 } from "motion/react";
import { useAnalytics as useAnalytics5 } from "@wbk/analytics";
import { Button as Button7, Checkbox } from "@wbk/ui";
import { Exception as Exception3, setLoggerUser as setLoggerUser2 } from "@wbk/logger";
import { useRegister } from "@wbk/api/hooks";
import { getItem } from "@wbk/utils";
import { Link as Link2, Trans as Trans2, useGeolocation as useGeolocation3, useRouter as useRouter8, useTranslation as useTranslation10 } from "@wbk/config";

// src/components/form/v2/SignupFormData.tsx
import { Controller as Controller4, useFormContext as useFormContext5 } from "react-hook-form";
import { Input as Input4, MobileInput } from "@wbk/ui";
import { SinglePerson } from "@wbk/svg";
import { useRouter as useRouter7, useTranslation as useTranslation9 } from "@wbk/config";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var SignupFormData = ({ onSubmit }) => {
  var _j, _k, _l;
  const { locale } = useRouter7();
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch
  } = useFormContext5();
  const { t } = useTranslation9("auth_common");
  const email = watch("email");
  const password = watch("password");
  const selectedCountry = watch("selected_country");
  const error = watch("error");
  const emailSuggestion = useEmailSpelling({ email });
  return /* @__PURE__ */ jsxs9("form", { id: "signup-form-info", className: "space-y-4", onSubmit: handleSubmit(onSubmit), noValidate: true, children: [
    /* @__PURE__ */ jsxs9("div", { className: "mt-1 flex flex-col-reverse items-center justify-between gap-4 sm:flex-row", children: [
      /* @__PURE__ */ jsxs9("div", { className: "grow space-y-2", children: [
        /* @__PURE__ */ jsx10("h4", { className: "text-2xl", children: t("auth_common:welcome") }),
        /* @__PURE__ */ jsx10("p", { className: "text-text-secondary text-sm", children: t("auth_common:welcome_text") })
      ] }),
      /* @__PURE__ */ jsx10("div", { className: "bg-body-dark/10 text-body-level-2 flex h-16 w-16 max-w-[100px] shrink-0 items-center justify-center rounded-full sm:max-w-none", children: /* @__PURE__ */ jsx10(SinglePerson, { className: "h-10 w-10" }) })
    ] }),
    /* @__PURE__ */ jsxs9("fieldset", { className: "flex flex-col gap-2 py-1 sm:flex-row", children: [
      /* @__PURE__ */ jsx10("div", { className: "grow", children: /* @__PURE__ */ jsx10(
        Controller4,
        {
          control,
          name: "first_name",
          rules: {
            required: t("auth_common:validation.required"),
            maxLength: {
              value: 25,
              message: t("auth_common:validation.max_length", { max: 25 })
            }
          },
          defaultValue: "",
          render: (_a) => {
            var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
            var _a2, _b2, _c2;
            return /* @__PURE__ */ jsx10(
              Input4,
              __spreadProps(__spreadValues({
                id: "first_name",
                "data-testid": "auth_firstname_input",
                autoComplete: "off",
                label: t("auth_common:first_name")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a2 = errors.first_name) == null ? void 0 : _a2.message) || ((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.first_name) == null ? void 0 : _c2[0])
              })
            );
          }
        }
      ) }),
      /* @__PURE__ */ jsx10("div", { className: "grow", children: /* @__PURE__ */ jsx10(
        Controller4,
        {
          control,
          name: "last_name",
          rules: {
            required: t("auth_common:validation.required"),
            maxLength: {
              value: 25,
              message: t("auth_common:validation.max_length", { max: 25 })
            }
          },
          defaultValue: "",
          render: (_d) => {
            var { field: _e } = _d, _f = _e, { onChange } = _f, props = __objRest(_f, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx10(
              Input4,
              __spreadProps(__spreadValues({
                id: "last_name",
                "data-testid": "auth_lastname_input",
                autoComplete: "off",
                label: t("auth_common:last_name")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a = errors.last_name) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.last_name) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx10("fieldset", { className: "my-4", children: /* @__PURE__ */ jsxs9("div", { className: "relative", children: [
      /* @__PURE__ */ jsx10(
        Controller4,
        {
          control,
          name: "email",
          rules: {
            required: t("auth_common:validation.required")
          },
          defaultValue: "",
          render: (_g) => {
            var { field: _h } = _g, _i = _h, { onChange } = _i, props = __objRest(_i, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx10(
              Input4,
              __spreadProps(__spreadValues({
                id: "email",
                "data-testid": "auth_signup_email_input",
                autoComplete: "off",
                readOnly: true,
                label: t("auth_common:email")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a = errors.email) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.email) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ),
      emailSuggestion && /* @__PURE__ */ jsxs9(
        "div",
        {
          className: `text-text-tertiary absolute w-full rounded-lg bg-yellow-100 p-2 text-sm ${((_j = errors.email) == null ? void 0 : _j.message) || ((_l = (_k = error == null ? void 0 : error.errors) == null ? void 0 : _k.email) == null ? void 0 : _l[0]) ? "bottom-0" : "-bottom-4"}`,
          children: [
            t("auth_common:validation.did_you_mean"),
            /* @__PURE__ */ jsx10(
              "button",
              {
                onClick: () => setValue("email", emailSuggestion.full, {
                  shouldValidate: true
                }),
                className: "text-primary mx-1 cursor-pointer font-semibold hover:underline",
                children: emailSuggestion.full
              }
            )
          ]
        }
      )
    ] }) }),
    /* @__PURE__ */ jsxs9("fieldset", { children: [
      /* @__PURE__ */ jsx10(
        Controller4,
        {
          control,
          name: "password",
          rules: {
            required: t("auth_common:validation.required"),
            minLength: {
              value: 8,
              message: t("auth_common:validation.invalid_password")
            }
          },
          defaultValue: "",
          render: (_m) => {
            var { field: _n } = _m, _o = _n, { onChange } = _o, props = __objRest(_o, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx10(
              Input4,
              __spreadProps(__spreadValues({}, props), {
                type: "password",
                "data-testid": "auth_signup_password_input",
                label: t("auth_common:password"),
                autoComplete: "off",
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a = errors.password) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.password) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx10("div", { className: "mt-2", children: /* @__PURE__ */ jsx10(PasswordBullets_default, { password: password || "" }) })
    ] }),
    /* @__PURE__ */ jsx10(
      Controller4,
      {
        name: "phone",
        control,
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => {
            var _a, _b, _c;
            const hasValidation = (_a = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _a.length;
            if (value == null ? void 0 : value.includes(" ")) {
              return t("auth_common:validation.invalid_mobile");
            }
            if (hasValidation) {
              const validStart = (_b = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _b.some(
                (start) => value == null ? void 0 : value.startsWith(start)
              );
              const validLength = ((value == null ? void 0 : value.length) || 0) >= Number((_c = selectedCountry == null ? void 0 : selectedCountry.phone_number_lengths) == null ? void 0 : _c[0]);
              return validStart && validLength || t("auth_common:validation.invalid_mobile");
            }
          }
        },
        render: (_p) => {
          var { field: _q } = _p, _r = _q, { value } = _r, props = __objRest(_r, ["value"]);
          var _a, _b, _c, _d, _e;
          return /* @__PURE__ */ jsx10(
            MobileInput,
            __spreadProps(__spreadValues({
              lang: locale,
              label: t("auth_common:mobile")
            }, props), {
              country: selectedCountry,
              mobile: value || "",
              onChange: (name, value2) => {
                if (name === "country") {
                  setValue("selected_country", value2);
                } else {
                  setValue("phone", value2);
                }
              },
              error: ((_a = errors == null ? void 0 : errors.phone) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.phone) == null ? void 0 : _c[0]),
              placeholder: selectedCountry ? `${((_d = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _d[0]) || ""}${"x".repeat(
                (((_e = selectedCountry.phone_number_lengths) == null ? void 0 : _e[0]) || 7) - 1
              )}` : ""
            })
          );
        }
      },
      selectedCountry == null ? void 0 : selectedCountry.code
    )
  ] });
};
var SignupFormData_default = SignupFormData;

// src/components/form/v2/Signup.tsx
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var Signup = ({ onSuccess }) => {
  var _a, _b;
  const {
    newsLetter: { enable, organizationName },
    setTokens
  } = useAuth();
  const { locale } = useRouter8();
  const { supportedCountryCode } = useGeolocation3();
  const { mutateAsync: register, isPending, error } = useRegister({ lang: locale });
  const {
    setValue,
    control,
    formState: { errors }
  } = useFormContext6();
  const { t } = useTranslation10(["auth_common"]);
  const { signupEvent, trackEvent } = useAnalytics5();
  const serverErr = error;
  const errorMsg = ((_a = serverErr == null ? void 0 : serverErr.errors) == null ? void 0 : _a.user) || ((_b = serverErr == null ? void 0 : serverErr.errors) == null ? void 0 : _b.message) || (serverErr == null ? void 0 : serverErr.message) || t("auth_common:error.unknown_error");
  const onSubmit = (data) => __async(null, null, function* () {
    try {
      const _a2 = data, { selected_country, newsletterAgree } = _a2, restData = __objRest(_a2, ["selected_country", "newsletterAgree"]);
      const referral_key = getItem("referral_key");
      const body = __spreadProps(__spreadValues({}, restData), {
        newsletter: newsletterAgree,
        country_code: selected_country == null ? void 0 : selected_country.code,
        country: selected_country == null ? void 0 : selected_country.code,
        referral_key
      });
      const user = yield register({ user: body, locale });
      const updatedUser = __spreadProps(__spreadValues({}, user || {}), {
        new_user: false
      });
      setTokens({
        accessToken: updatedUser == null ? void 0 : updatedUser.access_token,
        refreshToken: updatedUser == null ? void 0 : updatedUser.refresh_token,
        tokenExpiresIn: updatedUser == null ? void 0 : updatedUser.token_expires_in
      });
      setLoggerUser2(updatedUser);
      if (onSuccess) {
        onSuccess(updatedUser);
      }
      signupEvent({ method: "email", user: updatedUser });
    } catch (error2) {
      if (error2 instanceof Exception3) {
        setValue("error", error2);
      }
      trackEvent({ event: "signup_failed", data: { method: "email", error: error2 } });
    }
  });
  return /* @__PURE__ */ jsxs10("div", { className: "overflow-y-visible", id: "signup-wrapper", children: [
    /* @__PURE__ */ jsx11(AuthBackButton_default, { mode: "signin-email" }),
    /* @__PURE__ */ jsx11(AnimatePresence3, { initial: false, presenceAffectsLayout: true, mode: "wait", children: /* @__PURE__ */ jsx11(
      motion3.div,
      {
        className: "relative z-50 min-h-[250px]",
        initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
        transition: { duration: 0.2 },
        children: /* @__PURE__ */ jsx11(SignupFormData_default, { onSubmit })
      },
      locale
    ) }),
    /* @__PURE__ */ jsxs10("div", { className: "mt-8 space-y-2", children: [
      error && errorMsg && /* @__PURE__ */ jsx11("div", { className: "text-error pb-2 text-center text-sm", children: (errorMsg == null ? void 0 : errorMsg.includes("exists")) ? /* @__PURE__ */ jsxs10(Trans2, { i18nKey: "account_exists", ns: "auth_common", children: [
        /* @__PURE__ */ jsx11(
          "button",
          {
            className: "text-primary hover:underline",
            "aria-label": "Login",
            type: "button",
            onClick: () => setValue("mode", "signin-email")
          }
        ),
        /* @__PURE__ */ jsx11(
          "button",
          {
            className: "text-primary hover:underline",
            "aria-label": "Reset Password",
            type: "button",
            onClick: () => setValue("mode", "reset-password-email")
          }
        )
      ] }) : /* @__PURE__ */ jsx11("p", { children: errorMsg }) }),
      /* @__PURE__ */ jsx11(
        Controller5,
        {
          name: "agreeTerms",
          control,
          rules: {
            required: t("auth_common:validation.required")
          },
          defaultValue: false,
          render: (_c) => {
            var { field: _d } = _c, _e = _d, { onChange, value } = _e, props = __objRest(_e, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx11(
              Checkbox,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx11("span", { className: "text-sm", children: /* @__PURE__ */ jsx11(Trans2, { i18nKey: "agree", ns: "auth_common", children: /* @__PURE__ */ jsx11(
                  Button7,
                  {
                    as: Link2,
                    "data-testid": "auth_terms_link",
                    shape: "text",
                    className: "text-primary px-0.5 py-px font-semibold underline",
                    href: `/${locale}/terms${supportedCountryCode ? `?country_code=${supportedCountryCode}` : ""}`,
                    children: t("auth_common:agree")
                  }
                ) }) }),
                "data-testid": "auth_terms_checkbox",
                checked: value,
                onChange: (v) => {
                  onChange(v);
                  if (v) {
                    trackEvent({ event: "terms_accepted" });
                  }
                },
                error: (_a2 = errors.agreeTerms) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      enable && /* @__PURE__ */ jsx11(
        Controller5,
        {
          name: "newsletterAgree",
          control,
          defaultValue: true,
          render: (_f) => {
            var { field: _g } = _f, _h = _g, { onChange, value } = _h, props = __objRest(_h, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx11(
              Checkbox,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx11("span", { className: "text-sm", children: /* @__PURE__ */ jsx11(
                  Trans2,
                  {
                    i18nKey: "newsletterAgree",
                    ns: "auth_common",
                    values: { name: organizationName },
                    children: /* @__PURE__ */ jsx11("strong", {})
                  }
                ) }),
                checked: value,
                "data-testid": "auth_newsletter_checkbox",
                onChange: (v) => onChange(v),
                error: (_a2 = errors.newsletterAgree) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx11(
        Controller5,
        {
          name: "data_consent",
          control,
          defaultValue: true,
          render: (_i) => {
            var { field: _j } = _i, _k = _j, { onChange, value } = _k, props = __objRest(_k, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx11(
              Checkbox,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx11("span", { className: "text-sm", children: t("auth_common:third_party") }),
                checked: value,
                "data-testid": "auth_third_party_checkbox",
                onChange: (v) => onChange(v),
                error: (_a2 = errors.data_consent) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx11("div", { className: "pt-4", children: /* @__PURE__ */ jsx11(
        Button7,
        {
          type: "submit",
          form: "signup-form-info",
          className: "w-full",
          loading: isPending,
          "data-testid": "auth_signup_submit_button",
          children: /* @__PURE__ */ jsx11("span", { className: "text-lg font-bold", children: t("auth_common:create_account") })
        }
      ) })
    ] })
  ] });
};
var Signup_default = Signup;

// src/components/form/v2/ResetPasswordEmail.tsx
import { useEffect as useEffect4 } from "react";
import { Controller as Controller6, useFormContext as useFormContext7 } from "react-hook-form";
import { Button as Button8, Input as Input5, Typography as Typography9 } from "@wbk/ui";
import { useForgotPassword as useForgotPassword2 } from "@wbk/api/hooks";
import { EMAIL_PATTERN as EMAIL_PATTERN3 } from "@wbk/utils";
import { useAnalytics as useAnalytics6 } from "@wbk/analytics";
import { useAppSource as useAppSource2, useRouter as useRouter9, useTranslation as useTranslation11 } from "@wbk/config";
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
var ResetPasswordEmail = () => {
  const { t } = useTranslation11("auth_common");
  const { appSource } = useAppSource2();
  const { locale } = useRouter9();
  const { trackEvent } = useAnalytics6();
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue
  } = useFormContext7();
  const { mutateAsync: forgotPassword, isPending, error } = useForgotPassword2();
  const onSubmit = (data) => __async(null, null, function* () {
    var _a;
    const email = ((_a = data == null ? void 0 : data.email) == null ? void 0 : _a.trim()) || "";
    const body = { email, locale, app_source: appSource };
    trackEvent({
      event: "password_reset_requested",
      // domain only, the full address shouldn't reach a tracker
      data: { email_domain: email.split("@")[1] }
    });
    try {
      yield forgotPassword(body);
      trackEvent({ event: "otp_requested", data: { channel: "email" } });
      setValue("mode", "reset-password-otp");
    } catch (err) {
      trackEvent({ event: "otp_failed", data: { channel: "email", error: err } });
    }
  });
  useEffect4(() => {
    const email = getValues("email");
    if (email) {
      trackEvent({
        event: "Reset Password - Page View",
        data: { email_domain: email.split("@")[1] }
      });
    }
  }, []);
  return /* @__PURE__ */ jsxs11("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-5", children: [
    /* @__PURE__ */ jsx12(AuthBackButton_default, { mode: "signin-password" }),
    /* @__PURE__ */ jsxs11("div", { className: "flex flex-col items-center justify-center gap-2.5 text-center", children: [
      /* @__PURE__ */ jsx12(AuthWbkHeader_default, {}),
      /* @__PURE__ */ jsx12(Typography9, { variant: "heading-M", children: t("auth_common:reset_password_title") }),
      /* @__PURE__ */ jsx12(Typography9, { variant: "body-S", color: "text-secondary", children: t("auth_common:reset_password_text") })
    ] }),
    /* @__PURE__ */ jsx12(
      Controller6,
      {
        control,
        name: "email",
        rules: {
          required: t("auth_common:validation.required"),
          pattern: {
            value: EMAIL_PATTERN3,
            message: t("auth_common:validation.invalid_email")
          }
        },
        defaultValue: "",
        render: (_a) => {
          var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
          var _a2;
          return /* @__PURE__ */ jsx12(
            Input5,
            __spreadProps(__spreadValues({}, props), {
              type: "email",
              "data-testid": "auth_reset_password_email_input",
              label: t("auth_common:email"),
              onChange: (value) => onChange(value),
              error: (_a2 = errors.email) == null ? void 0 : _a2.message
            })
          );
        }
      }
    ),
    error && /* @__PURE__ */ jsx12(Typography9, { color: "error", variant: "body-S", children: t(error.message || "auth_common:error.unknown_error") }),
    /* @__PURE__ */ jsx12(
      Button8,
      {
        type: "submit",
        loading: isPending,
        className: "w-full py-3",
        "data-testid": "auth_send_reset_password_code_button",
        children: /* @__PURE__ */ jsx12(Typography9, { variant: "body-L", className: "font-semibold", children: t("auth_common:send_otp") })
      }
    )
  ] });
};
var ResetPasswordEmail_default = ResetPasswordEmail;

// src/components/form/v2/ResetPassword.tsx
import { Controller as Controller7, useFormContext as useFormContext8 } from "react-hook-form";
import { AnimatePresence as AnimatePresence4, motion as motion4 } from "motion/react";
import { Button as Button9, Input as Input6, Typography as Typography10 } from "@wbk/ui";
import { useChangePassword } from "@wbk/api/hooks";
import { useRouter as useRouter10, useTranslation as useTranslation12 } from "@wbk/config";
import { useAnalytics as useAnalytics7 } from "@wbk/analytics";
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var ResetPassword = () => {
  var _d, _e, _i, _j;
  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    getFieldState
  } = useFormContext8();
  const password = watch("new_password");
  const passwordState = getFieldState("new_password");
  const { locale } = useRouter10();
  const { t } = useTranslation12(["auth_common"]);
  const { trackEvent } = useAnalytics7();
  const { mutateAsync: changePassword, isPending, error } = useChangePassword();
  const onSubmit = (data) => __async(null, null, function* () {
    var _a;
    try {
      yield changePassword({
        email: data.email,
        new_password: data.new_password || "",
        re_enter_password: data.confirm_password || "",
        verification_code: data.otp || "",
        lang: locale
      });
      setValue("mode", "reset-password-success");
      setValue("new_password", "");
      setValue("confirm_password", "");
      trackEvent({ event: "password_reset_completed" });
    } catch (err) {
      trackEvent({
        event: "Reset Password - Reset Password Failed",
        // domain only, the full address shouldn't reach a tracker
        data: { email_domain: (_a = data.email) == null ? void 0 : _a.split("@")[1], error: err }
      });
    }
  });
  return /* @__PURE__ */ jsxs12("form", { id: "reset-password-form", onSubmit: handleSubmit(onSubmit), className: "space-y-5", children: [
    /* @__PURE__ */ jsx13(
      Controller7,
      {
        control,
        name: "new_password",
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => value ? isPasswordValid(value) : false
        },
        defaultValue: "",
        render: (_a) => {
          var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
          var _a2, _b2, _c2;
          return /* @__PURE__ */ jsx13(
            Input6,
            __spreadProps(__spreadValues({}, props), {
              type: "password",
              "data-testid": "auth_new_password_input",
              label: t("auth_common:new_password"),
              onChange: (value) => onChange(value),
              error: ((_a2 = errors.new_password) == null ? void 0 : _a2.message) || !!((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.new_password) == null ? void 0 : _c2.length)
            })
          );
        }
      }
    ),
    !!((_e = (_d = error == null ? void 0 : error.errors) == null ? void 0 : _d.new_password) == null ? void 0 : _e.length) && /* @__PURE__ */ jsx13("div", { className: "space-y-1", children: error.errors.new_password.map((err) => /* @__PURE__ */ jsx13(Typography10, { color: "error", variant: "body-S", children: err }, err)) }),
    /* @__PURE__ */ jsx13("div", { className: "", children: /* @__PURE__ */ jsx13(PasswordBullets_default, { password: password || "" }) }),
    /* @__PURE__ */ jsx13(AnimatePresence4, { children: passwordState.isDirty && !passwordState.invalid && /* @__PURE__ */ jsxs12(
      motion4.div,
      {
        className: "overflow-hidden",
        initial: { height: 0 },
        animate: { height: "auto" },
        exit: { height: 0 },
        children: [
          /* @__PURE__ */ jsx13(
            Controller7,
            {
              control,
              name: "confirm_password",
              rules: {
                required: t("auth_common:validation.required"),
                validate: (value) => value === password || t("auth_common:validation.passwords_not_match")
              },
              defaultValue: "",
              render: (_f) => {
                var { field: _g } = _f, _h = _g, { onChange } = _h, props = __objRest(_h, ["onChange"]);
                var _a, _b, _c;
                return /* @__PURE__ */ jsx13(
                  Input6,
                  __spreadProps(__spreadValues({}, props), {
                    type: "password",
                    autoComplete: "off",
                    onPaste: (e) => e.preventDefault(),
                    "data-testid": "auth_confirm_new_password_input",
                    label: t("auth_common:confirm_password"),
                    onChange: (value) => onChange(value),
                    error: ((_a = errors.confirm_password) == null ? void 0 : _a.message) || !!((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.re_enter_password) == null ? void 0 : _c.length)
                  })
                );
              }
            }
          ),
          !!((_j = (_i = error == null ? void 0 : error.errors) == null ? void 0 : _i.re_enter_password) == null ? void 0 : _j.length) && /* @__PURE__ */ jsx13("div", { className: "space-y-1", children: error.errors.re_enter_password.map((err) => /* @__PURE__ */ jsx13(Typography10, { color: "error", variant: "body-S", children: err }, err)) })
        ]
      }
    ) }),
    typeof (error == null ? void 0 : error.message) === "string" && error.message !== "[object Object]" && /* @__PURE__ */ jsx13(Typography10, { color: "error", variant: "body-S", className: "text-center", children: error.message }),
    /* @__PURE__ */ jsx13(
      Button9,
      {
        type: "submit",
        form: "reset-password-form",
        className: "w-full",
        loading: isPending,
        disabled: !isValid,
        id: "reset-password-form-button",
        "data-testid": "auth_update_password_button",
        children: /* @__PURE__ */ jsx13(Typography10, { variant: "label-M", children: t("auth_common:update_password") })
      }
    )
  ] });
};
var ResetPassword_default = ResetPassword;

// src/components/form/v2/AuthOtp.tsx
import { useMemo } from "react";
import { useFormContext as useFormContext10 } from "react-hook-form";
import { Button as Button10, OtpInputs as OtpInputs2, Typography as Typography11 } from "@wbk/ui";
import { useTranslation as useTranslation13 } from "@wbk/config";

// src/components/form/v2/useAuthOtp.ts
import { useAppSource as useAppSource3, useRouter as useRouter11 } from "@wbk/config";
import { useAnalytics as useAnalytics8 } from "@wbk/analytics";
import { useForgotPassword as useForgotPassword3, useResendOtp, useVerifyOtp } from "@wbk/api/hooks";
import { useFormContext as useFormContext9 } from "react-hook-form";
var useAuthOtp = ({ onSuccess }) => {
  const { appSource } = useAppSource3();
  const { locale } = useRouter11();
  const { trackEvent } = useAnalytics8();
  const { setTokens } = useAuth();
  const { getValues, setValue, watch } = useFormContext9();
  const email = getValues("email");
  const mode = watch("mode");
  const otp = watch("otp");
  const {
    mutateAsync: verifyOtp,
    isPending: isVerifying,
    error: verifyError
  } = useVerifyOtp({ lang: locale });
  const { mutateAsync: resendOtp, isPending: isResending, error: resendError } = useResendOtp();
  const {
    mutateAsync: forgotPassword,
    isPending: isForgotting,
    error: forgotError
  } = useForgotPassword3();
  const handleSubmitOtp = (code) => __async(null, null, function* () {
    try {
      const payload = { email, otp: code, locale };
      if (mode === "reset-password-otp") {
        yield verifyOtp(__spreadProps(__spreadValues({}, payload), { verify_otp: true }));
        setValue("mode", "reset-password");
      } else if (mode === "verify-otp") {
        const data = yield verifyOtp(payload);
        if (data == null ? void 0 : data.user) {
          setTokens({
            accessToken: data.user.access_token,
            refreshToken: data.user.refresh_token,
            tokenExpiresIn: data.user.token_expires_in
          });
          onSuccess == null ? void 0 : onSuccess(data.user);
        }
        setValue("mode", "signin-password");
      }
      trackEvent({ event: "otp_verified", data: { mode } });
    } catch (err) {
      trackEvent({ event: "otp_failed", data: { mode, error: err } });
    }
  });
  const handleResendOtp = () => __async(null, null, function* () {
    const payload = { email, locale, app_source: appSource };
    if (mode === "reset-password-otp") {
      yield forgotPassword(payload);
    }
    if (mode === "verify-otp") {
      yield resendOtp(payload);
    }
    trackEvent({ event: "otp_requested", data: { channel: "email", mode } });
  });
  return {
    email,
    otp,
    mode,
    error: (verifyError == null ? void 0 : verifyError.message) || (forgotError == null ? void 0 : forgotError.message) || (resendError == null ? void 0 : resendError.message),
    isLoading: isVerifying || isResending || isForgotting,
    handleSubmitOtp,
    handleResendOtp
  };
};

// src/components/form/v2/AuthOtp.tsx
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var AuthOtp = ({ onSuccess }) => {
  const { t } = useTranslation13("auth_common");
  const { setValue, watch } = useFormContext10();
  const { email, isLoading, error, handleSubmitOtp, handleResendOtp } = useAuthOtp({
    onSuccess
  });
  const mode = watch("mode");
  const has_password = watch("has_password");
  const backMode = useMemo(() => {
    switch (mode) {
      case "reset-password-otp":
        return "reset-password-email";
      default:
        return "signin-password";
    }
  }, [mode]);
  return /* @__PURE__ */ jsxs13("div", { className: "space-y-5", children: [
    /* @__PURE__ */ jsx14(AuthBackButton_default, { mode: backMode }),
    /* @__PURE__ */ jsxs13("div", { className: "flex flex-col items-center justify-center gap-3 text-center", children: [
      /* @__PURE__ */ jsx14(AuthWbkHeader_default, {}),
      /* @__PURE__ */ jsxs13("div", { className: "space-y-1 text-center", children: [
        /* @__PURE__ */ jsx14(Typography11, { variant: "heading-M", children: t("auth_common:sign_in.verify_email") }),
        /* @__PURE__ */ jsx14(Typography11, { children: email })
      ] }),
      /* @__PURE__ */ jsx14(Typography11, { variant: "body-S", color: "text-secondary", className: "pt-5", children: t("auth_common:sign_in.enter_otp") })
    ] }),
    /* @__PURE__ */ jsx14(
      OtpInputs2,
      {
        formId: "otp-form",
        onChange: (v) => setValue("otp", v),
        onSubmit: handleSubmitOtp,
        onlyNumbers: true,
        disabled: isLoading,
        error
      }
    ),
    /* @__PURE__ */ jsxs13("div", { className: "flex flex-col gap-5 pt-2.5", children: [
      /* @__PURE__ */ jsx14(
        WithCountDown_default,
        {
          seconds: 60,
          render: ({ count, reset }) => /* @__PURE__ */ jsx14("div", { className: "flex flex-wrap items-center justify-center gap-2 text-center", children: /* @__PURE__ */ jsx14(
            Button10,
            {
              theme: "action",
              shape: "text",
              type: "button",
              disabled: count > 0,
              loading: isLoading,
              onClick: () => __async(null, null, function* () {
                yield handleResendOtp();
                reset();
              }),
              children: /* @__PURE__ */ jsx14(Typography11, { variant: "body-L", className: "font-semibold", children: `${t("auth_common:sign_in.send_new_code")} ${count > 0 ? `(${count})` : ""}` })
            }
          ) })
        }
      ),
      mode !== "reset-password-otp" && has_password && /* @__PURE__ */ jsx14(
        Button10,
        {
          disabled: isLoading,
          theme: "action",
          shape: "text",
          type: "button",
          onClick: () => setValue("mode", "signin-password"),
          children: /* @__PURE__ */ jsx14(Typography11, { variant: "body-L", className: "font-semibold", children: t("auth_common:sign_in.use_password_instead") })
        }
      )
    ] })
  ] });
};
var AuthOtp_default = AuthOtp;

// src/components/form/v2/ResetPasswordSuccess.tsx
import { useFormContext as useFormContext11 } from "react-hook-form";
import { Button as Button11, Typography as Typography12 } from "@wbk/ui";
import { AnimatedCheck } from "@wbk/svg";
import { useTranslation as useTranslation14 } from "@wbk/config";
import { jsx as jsx15, jsxs as jsxs14 } from "react/jsx-runtime";
var ResetPasswordSuccess = () => {
  const { t } = useTranslation14(["auth_common"]);
  const { setValue } = useFormContext11();
  return /* @__PURE__ */ jsxs14("div", { className: "flex flex-col items-center gap-6 py-4 text-center", children: [
    /* @__PURE__ */ jsx15(AnimatedCheck, { className: "text-success h-24 w-24" }),
    /* @__PURE__ */ jsxs14("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx15(Typography12, { as: "h2", variant: "heading-M", children: t("auth_common:password_updated") }),
      /* @__PURE__ */ jsx15(Typography12, { variant: "body-M", color: "text-secondary", children: t("auth_common:password_updated_description") })
    ] }),
    /* @__PURE__ */ jsx15(
      Button11,
      {
        "data-testid": "auth_to_login_after_reset_button",
        onClick: () => setValue("mode", "signin-password"),
        className: "w-full",
        children: /* @__PURE__ */ jsx15(Typography12, { variant: "label-M", children: t("auth_common:login") })
      }
    )
  ] });
};
var ResetPasswordSuccess_default = ResetPasswordSuccess;

// src/components/form/engines/AuthV2Engine.tsx
import { Fragment, jsx as jsx16 } from "react/jsx-runtime";
var AuthV2Engine = ({
  methods,
  title,
  onSuccess
}) => {
  const mode = methods.watch("mode");
  const { trackEvent } = useAnalytics9();
  const { pathname } = useRouter12();
  useEffect5(() => {
    if (mode !== "signin-email" && mode !== "signup") return;
    trackEvent({
      event: mode === "signup" ? "signup_screen_viewed" : "login_screen_viewed",
      data: { entry_point: pathname }
    });
  }, [mode]);
  const Screen = useMemo2(() => {
    switch (mode) {
      case "signin-email":
        return /* @__PURE__ */ jsx16(SigninEmail_default, { title });
      case "signin-password":
        return /* @__PURE__ */ jsx16(SigninPassword_default, { onSuccess });
      case "signup":
        return /* @__PURE__ */ jsx16(Signup_default, { onSuccess });
      case "verify-otp":
      case "reset-password-otp":
        return /* @__PURE__ */ jsx16(AuthOtp_default, { onSuccess });
      case "reset-password-email":
        return /* @__PURE__ */ jsx16(ResetPasswordEmail_default, {});
      case "reset-password":
        return /* @__PURE__ */ jsx16(ResetPassword_default, {});
      case "reset-password-success":
        return /* @__PURE__ */ jsx16(ResetPasswordSuccess_default, {});
      default:
        return /* @__PURE__ */ jsx16(SigninEmail_default, { title });
    }
  }, [mode, onSuccess, title]);
  return /* @__PURE__ */ jsx16(Fragment, { children: Screen });
};

// src/components/form/engines/AuthV1Engine.tsx
import { useMemo as useMemo4 } from "react";
import { useAnalytics as useAnalytics14 } from "@wbk/analytics";
import { useTranslation as useTranslation23 } from "@wbk/config";

// src/components/button/Back.tsx
import { twMerge as twMerge5 } from "tailwind-merge";
import { Button as Button12 } from "@wbk/ui";
import { useTranslation as useTranslation15 } from "@wbk/config";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var BackButton = (_a) => {
  var _b = _a, { text, className = "" } = _b, props = __objRest(_b, ["text", "className"]);
  const { t } = useTranslation15(["auth_common"]);
  return /* @__PURE__ */ jsxs15(
    Button12,
    __spreadProps(__spreadValues({
      theme: "action",
      shape: "text",
      className: twMerge5(
        "-mx-1 mb-2 flex min-h-[30px] min-w-[36px] shrink-0 items-center gap-2 !px-1 py-1 text-sm",
        className
      )
    }, props), {
      children: [
        /* @__PURE__ */ jsx17(
          "svg",
          {
            className: "h-6 w-6 shrink-0 ltr:rotate-180",
            viewBox: "0 0 24 24",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            children: /* @__PURE__ */ jsx17(
              "path",
              {
                d: "M8.58984 16.59L13.1698 12L8.58984 7.41L9.99984 6L15.9998 12L9.99984 18L8.58984 16.59Z",
                fill: "currentColor"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx17("span", { className: "ltr:pr-2 rtl:pl-2", children: text || t("auth_common:back") })
      ]
    })
  );
};
var Back_default = BackButton;

// src/components/form/v1/Login.tsx
import { useEffect as useEffect6 } from "react";
import { Controller as Controller8, useFormContext as useFormContext12 } from "react-hook-form";
import { AnimatePresence as AnimatePresence5, motion as motion5 } from "motion/react";
import { useAnalytics as useAnalytics10 } from "@wbk/analytics";
import { Button as Button14, Divider as Divider3, Input as Input7 } from "@wbk/ui";
import { useForgotPassword as useForgotPassword4, useLogin as useLogin2 } from "@wbk/api/hooks";
import { EMAIL_PATTERN as EMAIL_PATTERN4 } from "@wbk/utils";
import { setLoggerUser as setLoggerUser3 } from "@wbk/logger";
import { WbkLogo as WbkLogo2 } from "@wbk/svg";
import { useAppSource as useAppSource4, useRouter as useRouter14, useTranslation as useTranslation17 } from "@wbk/config";

// src/components/form/SSOLogin.tsx
import { useRouter as useRouter13, useTranslation as useTranslation16 } from "@wbk/config";
import { Button as Button13, Divider as Divider2, ImageWithFallback, Typography as Typography13 } from "@wbk/ui";
import { useGetOrganizationBySlug } from "@wbk/api/hooks";
import { twMerge as twMerge6 } from "tailwind-merge";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var SsoLogin = ({ className }) => {
  const { t } = useTranslation16(["auth_common"]);
  const { locale } = useRouter13();
  const { sso } = useAuth();
  const { data: organization } = useGetOrganizationBySlug(
    { slug: sso == null ? void 0 : sso.organizationSlug, lang: locale },
    { enabled: !!(sso == null ? void 0 : sso.organizationSlug) }
  );
  const configuration = organization == null ? void 0 : organization.sso_configuration;
  if (!(organization == null ? void 0 : organization.sso_enabled) || !configuration || !(configuration == null ? void 0 : configuration.client_id) || !(configuration == null ? void 0 : configuration.authorization_endpoint))
    return null;
  const handleLogin = () => {
    switch (configuration.type) {
      case "oidc": {
        let authorizationUrl;
        try {
          authorizationUrl = new URL(configuration.authorization_endpoint);
        } catch (e) {
          return;
        }
        const callbackUri = `${window.location.origin}/auth/${configuration.type}/callback`;
        const returnTo = new URLSearchParams(window.location.search).get("redirect") || "";
        authorizationUrl.searchParams.set("client_id", configuration.client_id);
        authorizationUrl.searchParams.set("redirect_uri", callbackUri);
        authorizationUrl.searchParams.set("response_type", "code");
        authorizationUrl.searchParams.set("scope", (configuration.scopes || ["openid"]).join(" "));
        authorizationUrl.searchParams.set("state", returnTo || crypto.randomUUID());
        window.location.assign(authorizationUrl.toString());
        break;
      }
      default:
        break;
    }
  };
  return /* @__PURE__ */ jsxs16("div", { className: twMerge6("w-full", className), children: [
    /* @__PURE__ */ jsxs16(
      Button13,
      {
        type: "button",
        theme: "secondary",
        shape: "outlined",
        className: "w-full",
        onClick: handleLogin,
        "data-testid": "auth_sso_login_button",
        children: [
          configuration.icon && /* @__PURE__ */ jsx18(
            ImageWithFallback,
            {
              src: configuration.icon,
              alt: "",
              className: "h-5 w-5 object-contain",
              style: configuration.icon_color ? { color: configuration.icon_color } : void 0
            }
          ),
          /* @__PURE__ */ jsx18(
            Typography13,
            {
              as: "span",
              strong: true,
              style: configuration.title_color ? { color: configuration.title_color } : void 0,
              children: configuration.title || t("auth_common:sso.continue")
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs16("div", { className: "my-4 flex items-center gap-3", children: [
      /* @__PURE__ */ jsx18(Divider2, { className: "flex-1" }),
      /* @__PURE__ */ jsx18(Typography13, { as: "span", variant: "body-S", color: "text-secondary", children: t("auth_common:sso.separator") }),
      /* @__PURE__ */ jsx18(Divider2, { className: "flex-1" })
    ] })
  ] });
};
var SSOLogin_default = SsoLogin;

// src/components/form/v1/Login.tsx
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var EmailLogin = ({ onSuccess }) => {
  const {
    watch,
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue
  } = useFormContext12();
  const { setTokens, poweredBy } = useAuth();
  const { appSource } = useAppSource4();
  const { locale, search } = useRouter14();
  const { t } = useTranslation17(["auth_common"]);
  const { mutateAsync: login, isPending, error: loginError } = useLogin2({ lang: locale });
  const {
    mutateAsync: forgotPassword,
    isPending: passLoading,
    error: passError
  } = useForgotPassword4();
  const { trackEvent, loginEvent } = useAnalytics10();
  const mode = watch("mode");
  const isLoading = isPending || passLoading;
  const error = (loginError == null ? void 0 : loginError.message) || (passError == null ? void 0 : passError.message);
  const onSubmit = (data) => __async(null, null, function* () {
    var _a, _b, _c, _d, _e;
    try {
      const email = ((_a = data == null ? void 0 : data.email) == null ? void 0 : _a.trim()) || "";
      const body = { email, locale, app_source: appSource };
      if (mode === "reset-email") {
        yield forgotPassword(body);
        setValue("mode", "reset-otp");
        trackEvent({
          event: "Reset Password - Send OTP Success",
          data: { email: getValues("email") }
        });
      } else {
        const res = yield login(__spreadProps(__spreadValues({}, body), { password: data.password }));
        setTokens({
          accessToken: (_b = res == null ? void 0 : res.user) == null ? void 0 : _b.access_token,
          refreshToken: (_c = res == null ? void 0 : res.user) == null ? void 0 : _c.refresh_token,
          tokenExpiresIn: (_d = res == null ? void 0 : res.user) == null ? void 0 : _d.token_expires_in
        });
        if ((_e = res == null ? void 0 : res.user) == null ? void 0 : _e.force_verify_email) {
          yield forgotPassword(__spreadProps(__spreadValues({}, body), { checkout_verify: true }));
          setValue("mode", "otp");
          return;
        }
        loginEvent({
          method: "form_submit",
          user: __spreadProps(__spreadValues({}, res.user), { locale })
        });
        if (onSuccess) {
          onSuccess(res.user);
        }
        setLoggerUser3(res.user);
      }
    } catch (error2) {
      if (mode === "email") {
        trackEvent({ event: "Login - Login Failed", data: { email: data.email, error: error2 } });
      }
    }
  });
  useEffect6(() => {
    var _a, _b;
    if (typeof window === "undefined") return;
    const token = (_a = search.get("token")) != null ? _a : "";
    if (token) {
      setTokens({ accessToken: token });
    }
    const encodedEmail = (_b = search.get("email")) != null ? _b : "";
    const email = decodeURIComponent(encodedEmail);
    const isValidEmail = EMAIL_PATTERN4.test(email);
    if (email && isValidEmail) {
      setValue("email", email);
    }
  }, []);
  return /* @__PURE__ */ jsxs17("form", { onSubmit: handleSubmit(onSubmit), id: "email-login", name: "email-login-form", children: [
    poweredBy && /* @__PURE__ */ jsxs17("div", { className: "text-secondary-contrast pb-4 text-center", children: [
      /* @__PURE__ */ jsxs17("div", { className: "flex items-center justify-center gap-3", children: [
        /* @__PURE__ */ jsx19("p", { className: "text-xs", children: t("auth_common:powered") }),
        /* @__PURE__ */ jsx19(WbkLogo2, { width: 45, height: 14, className: "text-[#F84878]" })
      ] }),
      /* @__PURE__ */ jsx19("p", { className: "text-sm", children: t("auth_common:wbk_login") })
    ] }),
    mode === "email" && /* @__PURE__ */ jsx19(SSOLogin_default, {}),
    /* @__PURE__ */ jsx19(
      Controller8,
      {
        control,
        name: "email",
        rules: {
          required: t("auth_common:validation.required"),
          pattern: {
            value: EMAIL_PATTERN4,
            message: t("auth_common:validation.invalid_email")
          }
        },
        defaultValue: "",
        render: (_a) => {
          var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
          var _a2;
          return /* @__PURE__ */ jsx19(
            Input7,
            __spreadProps(__spreadValues({}, props), {
              "data-testid": mode === "reset-email" ? "auth_reset_password_email_input" : "auth_login_email_input",
              type: "email",
              label: /* @__PURE__ */ jsx19("span", { className: "font-semibold", children: t("auth_common:email") }),
              placeholder: "you@email.com",
              onChange: (value) => {
                onChange(value);
              },
              error: (_a2 = errors.email) == null ? void 0 : _a2.message
            })
          );
        }
      }
    ),
    /* @__PURE__ */ jsx19("div", { className: "h-4" }),
    /* @__PURE__ */ jsx19(AnimatePresence5, { initial: false, children: mode !== "reset-email" && /* @__PURE__ */ jsxs17(
      motion5.div,
      {
        className: "overflow-hidden",
        initial: { height: 0 },
        animate: { height: "auto" },
        exit: { height: 0 },
        children: [
          /* @__PURE__ */ jsx19(
            Controller8,
            {
              control,
              name: "password",
              rules: {
                required: t("auth_common:validation.required")
              },
              defaultValue: "",
              render: (_d) => {
                var { field: _e } = _d, _f = _e, { onChange } = _f, props = __objRest(_f, ["onChange"]);
                var _a;
                return /* @__PURE__ */ jsx19(
                  Input7,
                  __spreadProps(__spreadValues({}, props), {
                    id: "password",
                    "data-testid": "auth_login_password_input",
                    type: "password",
                    label: t("auth_common:password"),
                    onChange: (value) => {
                      onChange(value);
                    },
                    error: (_a = errors.password) == null ? void 0 : _a.message,
                    hideErrorSpace: true
                  })
                );
              }
            },
            "login-password"
          ),
          /* @__PURE__ */ jsx19("div", { className: "flex justify-end text-sm", children: /* @__PURE__ */ jsx19(
            Button14,
            {
              className: "hover:!text-primary my-4 flex justify-end !p-0.5 !text-blue-500 underline-offset-2 !ring-0 hover:underline",
              theme: "transparent",
              shape: "text",
              type: "button",
              "data-testid": "auth_reset_password",
              onClick: () => {
                setValue("password", "");
                setValue("mode", "reset-email");
                trackEvent({ event: "Reset Password - Page View" });
              },
              children: t("auth_common:reset_password_question")
            }
          ) })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx19("div", { className: "text-center", id: "v2-captcha-section" }),
    !!error && /* @__PURE__ */ jsx19("p", { className: "text-error pb-2 text-center text-sm", children: error }),
    /* @__PURE__ */ jsx19(
      Button14,
      {
        type: "submit",
        form: "email-login",
        className: "mt-4 w-full",
        onClick: () => {
          if (mode === "reset-email") {
            return trackEvent({
              event: "Reset Password - Send OTP Btn",
              data: { email: getValues("email") }
            });
          }
          trackEvent({ event: "Login - Login Btn", data: { email: getValues("email") } });
        },
        loading: isLoading,
        id: "email-login-button",
        "data-testid": mode === "reset-email" ? "auth_send_reset_password_code_button" : "auth_login_submit_button",
        shape: "solid",
        children: /* @__PURE__ */ jsx19("span", { className: "text-xl font-bold", children: mode === "reset-email" ? t("auth_common:send_otp") : t("auth_common:login") })
      }
    ),
    /* @__PURE__ */ jsx19(AnimatePresence5, { initial: false, children: mode !== "reset-email" && /* @__PURE__ */ jsxs17(
      motion5.div,
      {
        className: "overflow-hidden text-center",
        initial: { height: 0 },
        animate: { height: "auto" },
        exit: { height: 0 },
        children: [
          /* @__PURE__ */ jsx19(Divider3, { className: "mb-4 mt-6" }),
          /* @__PURE__ */ jsx19("p", { className: "text-sm", children: t("auth_common:dont_have_account") }),
          /* @__PURE__ */ jsx19(
            Button14,
            {
              className: "mt-3 w-full max-w-[200px] !py-1",
              theme: "action",
              shape: "outlined",
              "data-testid": "auth_signup_button",
              type: "button",
              onClick: () => {
                setValue("mode", "signup");
                trackEvent({ event: "Signup - Page View" });
              },
              children: t("auth_common:signup")
            }
          )
        ]
      }
    ) })
  ] });
};
var Login_default = EmailLogin;

// src/components/form/v1/OtpSignup.tsx
import { useFormContext as useFormContext13 } from "react-hook-form";
import { Button as Button15, OtpInputs as OtpInputs3 } from "@wbk/ui";
import { useForgotPassword as useForgotPassword5, useVerifyOtp as useVerifyOtp2 } from "@wbk/api/hooks";
import { useAppSource as useAppSource5, useRouter as useRouter15, useTranslation as useTranslation18 } from "@wbk/config";
import { useAnalytics as useAnalytics11 } from "@wbk/analytics";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
var OtpSignup = () => {
  const { getValues, setValue, watch } = useFormContext13();
  const { setTokens } = useAuth();
  const { appSource } = useAppSource5();
  const { locale } = useRouter15();
  const { trackEvent } = useAnalytics11();
  const { mutateAsync: verifyOtp, isPending, error: otpError } = useVerifyOtp2({ lang: locale });
  const {
    mutateAsync: forgotPassword,
    isPending: passLoading,
    error: passError
  } = useForgotPassword5();
  const isLoading = isPending || passLoading;
  const error = (otpError == null ? void 0 : otpError.message) || (passError == null ? void 0 : passError.message);
  const { t } = useTranslation18(["auth_common"]);
  const otp = watch("otp");
  const mode = watch("mode");
  const handleOtp = (otp2) => __async(null, null, function* () {
    try {
      const body = {
        email: getValues("email"),
        otp: otp2,
        locale
      };
      if (mode === "reset-otp") {
        yield verifyOtp(__spreadProps(__spreadValues({}, body), { verify_otp: true }));
        setValue("mode", "reset-password");
      } else {
        const data = yield verifyOtp(__spreadProps(__spreadValues({}, body), { otp: otp2 }));
        if (data == null ? void 0 : data.user) {
          const user = __spreadProps(__spreadValues({}, data.user), {
            new_user: data.new_user || false
          });
          setTokens({
            accessToken: user.access_token,
            refreshToken: user.refresh_token,
            tokenExpiresIn: user.token_expires_in
          });
        }
        trackEvent({
          event: "Reset Password - Verify OTP Success",
          data: { email: getValues("email") }
        });
      }
    } catch (error2) {
      if (mode === "reset-otp") {
        trackEvent({
          event: "Reset Password - Verify OTP Failed",
          data: { email: getValues("email"), error: error2 }
        });
      }
    }
  });
  const resendCode = () => __async(null, null, function* () {
    try {
      const body = {
        email: getValues("email"),
        resend: true,
        locale,
        app_source: appSource
      };
      yield forgotPassword(body);
      if (mode === "otp") {
        trackEvent({
          event: "Reset Password - Resend OTP",
          data: { email: body.email }
        });
      }
    } catch (err) {
    }
  });
  return /* @__PURE__ */ jsxs18("div", { className: "", children: [
    /* @__PURE__ */ jsx20("div", { className: "text-center text-2xl font-bold", children: t("auth_common:verify_your_email_address") }),
    /* @__PURE__ */ jsx20(
      OtpInputs3,
      {
        formId: "otp-verify",
        legend: /* @__PURE__ */ jsx20("p", { className: "px-2 py-4 text-center text-sm text-zinc-500", children: t("auth_common:enter_the_otp", { email: getValues("email") }) }),
        onChange: (v) => {
          setValue("otp", v);
        },
        onSubmit: handleOtp,
        onlyNumbers: true,
        disabled: isLoading,
        error,
        renderButton: ({ resetState }) => /* @__PURE__ */ jsxs18("div", { className: "mt-6 text-center text-sm", children: [
          /* @__PURE__ */ jsx20(
            WithCountDown_default,
            {
              seconds: 60,
              render: ({ count, reset }) => /* @__PURE__ */ jsxs18("div", { className: "flex items-center justify-center gap-2", children: [
                /* @__PURE__ */ jsx20("p", { className: "mx-1 font-medium text-zinc-500", children: t("auth_common:didnt_receive_it") }),
                /* @__PURE__ */ jsx20(
                  Button15,
                  {
                    type: "button",
                    "data-testid": "auth_otp_resend_code_button",
                    shape: "text",
                    className: "px-2 py-1 text-sm",
                    disabled: count > 0,
                    loading: isLoading,
                    onClick: () => __async(null, null, function* () {
                      try {
                        yield resendCode();
                        reset();
                        resetState();
                      } catch (error2) {
                      }
                    }),
                    id: "resend-otp",
                    children: `${t("auth_common:resend_otp")} ${count > 0 ? `(${count})` : ""}`
                  }
                )
              ] })
            }
          ),
          /* @__PURE__ */ jsx20("div", { className: "p-4", children: /* @__PURE__ */ jsx20(
            Button15,
            {
              type: "submit",
              form: "otp-verify",
              "data-testid": "auth_reset_password_otp_submit",
              disabled: (otp == null ? void 0 : otp.length) !== 4,
              loading: isLoading,
              className: "mt-6 w-full",
              id: "verify-otp",
              children: /* @__PURE__ */ jsx20("span", { className: "text-xl font-bold", children: mode === "reset-otp" ? t("auth_common:reset_password") : t("auth_common:verify") })
            }
          ) })
        ] })
      }
    )
  ] });
};
var OtpSignup_default = OtpSignup;

// src/components/form/v1/NewPasswordForm.tsx
import { Controller as Controller9, useFormContext as useFormContext14 } from "react-hook-form";
import { AnimatePresence as AnimatePresence6, motion as motion6 } from "motion/react";
import { Button as Button16, Input as Input8 } from "@wbk/ui";
import { useChangePassword as useChangePassword2 } from "@wbk/api/hooks";
import { useRouter as useRouter16, useTranslation as useTranslation19 } from "@wbk/config";
import { useAnalytics as useAnalytics12 } from "@wbk/analytics";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
var NewPasswordForm = () => {
  var _d, _e, _i, _j, _k;
  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    getFieldState
  } = useFormContext14();
  const { mutateAsync: changePassword, isPending, error } = useChangePassword2();
  const { locale } = useRouter16();
  const { trackEvent } = useAnalytics12();
  const { t } = useTranslation19(["auth_common"]);
  const password = watch("new_password");
  const passwordState = getFieldState("new_password");
  const onSubmit = (props) => __async(null, null, function* () {
    try {
      yield changePassword({
        email: props.email,
        new_password: props.new_password || "",
        re_enter_password: props.confirm_password || "",
        verification_code: props.otp || "",
        lang: locale
      });
      setValue("mode", "reset-password-success");
      trackEvent({
        event: "Reset Password - Reset Password Success",
        data: { email: props.email }
      });
    } catch (error2) {
      trackEvent({
        event: "Reset Password - Reset Password Failed",
        data: { email: props.email, error: error2 }
      });
    }
  });
  return /* @__PURE__ */ jsxs19("form", { id: "reset-password-form", onSubmit: handleSubmit(onSubmit), children: [
    /* @__PURE__ */ jsx21(
      Controller9,
      {
        control,
        name: "new_password",
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => {
            const val = value || "";
            const lengthValid = LENGTH_8_PATTERN.test(val);
            const oneLetterValid = ONE_LETTER_PATTERN.test(val);
            const oneNumberValid = ONE_NUMBER_PATTERN.test(val);
            const isValid2 = lengthValid && oneLetterValid && oneNumberValid;
            return isValid2;
          }
        },
        defaultValue: "",
        render: (_a) => {
          var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
          var _a2, _b2, _c2;
          return /* @__PURE__ */ jsx21(
            Input8,
            __spreadProps(__spreadValues({}, props), {
              type: "password",
              "data-testid": "auth_new_password_input",
              label: t("auth_common:new_password"),
              onChange: (value) => {
                onChange(value);
              },
              error: ((_a2 = errors.new_password) == null ? void 0 : _a2.message) || !!((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.new_password) == null ? void 0 : _c2.length)
            })
          );
        }
      }
    ),
    !!((_e = (_d = error == null ? void 0 : error.errors) == null ? void 0 : _d.new_password) == null ? void 0 : _e.length) && /* @__PURE__ */ jsx21("div", { className: "space-y-1 pt-1", children: error.errors.new_password.map((err) => /* @__PURE__ */ jsx21("p", { className: "text-error text-sm", children: err }, err)) }),
    /* @__PURE__ */ jsx21("div", { className: "mt-2", children: /* @__PURE__ */ jsx21(PasswordBullets_default, { password: password || "" }) }),
    /* @__PURE__ */ jsx21(AnimatePresence6, { children: passwordState.isDirty && !passwordState.invalid && /* @__PURE__ */ jsxs19(
      motion6.div,
      {
        className: "overflow-hidden",
        initial: { height: 0 },
        animate: { height: "auto" },
        exit: { height: 0 },
        children: [
          /* @__PURE__ */ jsx21(
            Controller9,
            {
              control,
              name: "confirm_password",
              rules: {
                required: t("auth_common:validation.required"),
                validate: (value) => value === password || t("auth_common:validation.passwords_not_match")
              },
              defaultValue: "",
              render: (_f) => {
                var { field: _g } = _f, _h = _g, { onChange } = _h, props = __objRest(_h, ["onChange"]);
                var _a, _b, _c;
                return /* @__PURE__ */ jsx21(
                  Input8,
                  __spreadProps(__spreadValues({}, props), {
                    autoComplete: "off",
                    type: "password",
                    "data-testid": "auth_confirm_new_password_input",
                    label: t("auth_common:confirm_password"),
                    onChange: (value) => {
                      onChange(value);
                    },
                    onPaste: (e) => {
                      e.preventDefault();
                    },
                    error: ((_a = errors.confirm_password) == null ? void 0 : _a.message) || !!((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.re_enter_password) == null ? void 0 : _c.length)
                  })
                );
              }
            }
          ),
          !!((_j = (_i = error == null ? void 0 : error.errors) == null ? void 0 : _i.re_enter_password) == null ? void 0 : _j.length) && /* @__PURE__ */ jsx21("div", { className: "space-y-1 pt-1", children: (_k = error == null ? void 0 : error.errors) == null ? void 0 : _k.re_enter_password.map((err) => /* @__PURE__ */ jsx21("p", { className: "text-error text-sm", children: err }, err)) })
        ]
      }
    ) }),
    typeof (error == null ? void 0 : error.message) === "string" && error.message !== "[object Object]" && /* @__PURE__ */ jsx21("p", { className: "text-error pt-1 text-center text-sm", children: error.message }),
    /* @__PURE__ */ jsx21(
      Button16,
      {
        type: "submit",
        form: "reset-password-form",
        className: "mt-4 w-full",
        loading: isPending,
        disabled: !isValid,
        id: "reset-password-form-button",
        "data-testid": "auth_update_password_button",
        children: /* @__PURE__ */ jsx21("span", { className: "text-xl font-bold", children: t("auth_common:update_password") })
      }
    )
  ] });
};
var NewPasswordForm_default = NewPasswordForm;

// src/components/form/v1/ResetPasswordSuccess.tsx
import { useFormContext as useFormContext15 } from "react-hook-form";
import { Button as Button17, Typography as Typography14 } from "@wbk/ui";
import { AnimatedCheck as AnimatedCheck2 } from "@wbk/svg";
import { useTranslation as useTranslation20 } from "@wbk/config";
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var ResetPasswordSuccess2 = () => {
  const { t } = useTranslation20(["auth_common"]);
  const { setValue } = useFormContext15();
  return /* @__PURE__ */ jsxs20("div", { className: "flex flex-col items-center gap-6 py-4 text-center", children: [
    /* @__PURE__ */ jsx22(AnimatedCheck2, { className: "text-success h-24 w-24" }),
    /* @__PURE__ */ jsxs20("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx22(Typography14, { as: "h2", variant: "heading-M", children: t("auth_common:password_updated") }),
      /* @__PURE__ */ jsx22(Typography14, { variant: "body-M", color: "text-secondary", children: t("auth_common:password_updated_description") })
    ] }),
    /* @__PURE__ */ jsx22(
      Button17,
      {
        "data-testid": "auth_to_login_after_reset_button",
        className: "w-full",
        onClick: () => setValue("mode", "email"),
        children: /* @__PURE__ */ jsx22(Typography14, { variant: "label-L", children: t("auth_common:login") })
      }
    )
  ] });
};
var ResetPasswordSuccess_default2 = ResetPasswordSuccess2;

// src/components/form/v1/Signup.tsx
import { useMemo as useMemo3 } from "react";
import { Controller as Controller11, useFormContext as useFormContext17 } from "react-hook-form";
import { AnimatePresence as AnimatePresence7, motion as motion7 } from "motion/react";
import { useAnalytics as useAnalytics13 } from "@wbk/analytics";
import { Button as Button18, Checkbox as Checkbox2 } from "@wbk/ui";
import { Exception as Exception4, setLoggerUser as setLoggerUser4 } from "@wbk/logger";
import { useRegister as useRegister2 } from "@wbk/api/hooks";
import { getItem as getItem2 } from "@wbk/utils";
import { Link as Link3, Trans as Trans3, useGeolocation as useGeolocation4, useRouter as useRouter18, useTranslation as useTranslation22 } from "@wbk/config";

// src/components/form/v1/SignupFormData.tsx
import { Controller as Controller10, useFormContext as useFormContext16 } from "react-hook-form";
import { Input as Input9, MobileInput as MobileInput2 } from "@wbk/ui";
import { SinglePerson as SinglePerson2 } from "@wbk/svg";
import { useRouter as useRouter17, useTranslation as useTranslation21 } from "@wbk/config";
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var SignupFormData2 = ({ onSubmit }) => {
  var _j, _k, _l;
  const { locale } = useRouter17();
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    watch
  } = useFormContext16();
  const { t } = useTranslation21("auth_common");
  const email = watch("email");
  const password = watch("password");
  const selectedCountry = watch("selected_country");
  const error = watch("error");
  const emailSuggestion = useEmailSpelling({ email });
  return /* @__PURE__ */ jsxs21("form", { id: "signup-form-info", className: "space-y-4", onSubmit: handleSubmit(onSubmit), noValidate: true, children: [
    /* @__PURE__ */ jsxs21("div", { className: "mt-1 flex flex-col-reverse items-center justify-between gap-4 sm:flex-row", children: [
      /* @__PURE__ */ jsxs21("div", { className: "grow space-y-2", children: [
        /* @__PURE__ */ jsx23("h4", { className: "text-2xl", children: t("auth_common:welcome") }),
        /* @__PURE__ */ jsx23("p", { className: "text-text-secondary text-sm", children: t("auth_common:welcome_text") })
      ] }),
      /* @__PURE__ */ jsx23("div", { className: "bg-body-dark/10 text-body-level-2 flex h-16 w-16 max-w-[100px] shrink-0 items-center justify-center rounded-full sm:max-w-none", children: /* @__PURE__ */ jsx23(SinglePerson2, { className: "h-10 w-10" }) })
    ] }),
    /* @__PURE__ */ jsxs21("fieldset", { className: "flex flex-col gap-2 py-1 sm:flex-row", children: [
      /* @__PURE__ */ jsx23("div", { className: "grow", children: /* @__PURE__ */ jsx23(
        Controller10,
        {
          control,
          name: "first_name",
          rules: {
            required: t("auth_common:validation.required"),
            maxLength: {
              value: 25,
              message: t("auth_common:validation.max_length", { max: 25 })
            }
          },
          defaultValue: "",
          render: (_a) => {
            var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
            var _a2, _b2, _c2;
            return /* @__PURE__ */ jsx23(
              Input9,
              __spreadProps(__spreadValues({
                id: "first_name",
                "data-testid": "auth_firstname_input",
                autoComplete: "off",
                label: t("auth_common:first_name")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a2 = errors.first_name) == null ? void 0 : _a2.message) || ((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.first_name) == null ? void 0 : _c2[0])
              })
            );
          }
        }
      ) }),
      /* @__PURE__ */ jsx23("div", { className: "grow", children: /* @__PURE__ */ jsx23(
        Controller10,
        {
          control,
          name: "last_name",
          rules: {
            required: t("auth_common:validation.required"),
            maxLength: {
              value: 25,
              message: t("auth_common:validation.max_length", { max: 25 })
            }
          },
          defaultValue: "",
          render: (_d) => {
            var { field: _e } = _d, _f = _e, { onChange } = _f, props = __objRest(_f, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx23(
              Input9,
              __spreadProps(__spreadValues({
                id: "last_name",
                "data-testid": "auth_lastname_input",
                autoComplete: "off",
                label: t("auth_common:last_name")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a = errors.last_name) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.last_name) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs21("fieldset", { className: "my-4", children: [
      /* @__PURE__ */ jsxs21("div", { className: "relative", children: [
        /* @__PURE__ */ jsx23(
          Controller10,
          {
            control,
            name: "email",
            rules: {
              required: t("auth_common:validation.required")
            },
            defaultValue: "",
            render: (_g) => {
              var { field: _h } = _g, _i = _h, { onChange } = _i, props = __objRest(_i, ["onChange"]);
              var _a, _b, _c;
              return /* @__PURE__ */ jsx23(
                Input9,
                __spreadProps(__spreadValues({
                  id: "email",
                  "data-testid": "auth_signup_email_input",
                  autoComplete: "off",
                  label: t("auth_common:email")
                }, props), {
                  onChange: (value) => {
                    onChange(value);
                  },
                  error: ((_a = errors.email) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.email) == null ? void 0 : _c[0])
                })
              );
            }
          }
        ),
        /* @__PURE__ */ jsx23("p", { className: "text-text-secondary pt-0.5 text-sm", children: t("auth_common:validation.email_warning") }),
        emailSuggestion && /* @__PURE__ */ jsxs21(
          "div",
          {
            className: `text-text-tertiary absolute w-full rounded-lg bg-yellow-100 p-2 text-sm ${((_j = errors.email) == null ? void 0 : _j.message) || ((_l = (_k = error == null ? void 0 : error.errors) == null ? void 0 : _k.email) == null ? void 0 : _l[0]) ? "bottom-0" : "-bottom-4"}`,
            children: [
              t("auth_common:validation.did_you_mean"),
              /* @__PURE__ */ jsx23(
                "button",
                {
                  onClick: () => setValue("email", emailSuggestion.full, {
                    shouldValidate: true
                  }),
                  className: "text-primary mx-1 cursor-pointer font-semibold hover:underline",
                  children: emailSuggestion.full
                }
              )
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx23("div", { className: "mt-4", children: /* @__PURE__ */ jsx23(
        Controller10,
        {
          control,
          name: "confirm_email",
          rules: {
            required: t("auth_common:validation.required"),
            validate: (value) => value === email || t("auth_common:validation.emails_not_match")
          },
          defaultValue: "",
          render: (_m) => {
            var { field: _n } = _m, _o = _n, { onChange } = _o, props = __objRest(_o, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx23(
              Input9,
              __spreadProps(__spreadValues({
                id: "confirm_email",
                "data-testid": "auth_confirm_email_input",
                autoComplete: "off",
                label: t("auth_common:confirm_email")
              }, props), {
                onChange: (value) => {
                  onChange(value);
                },
                onPaste: (e) => {
                  e.preventDefault();
                },
                error: ((_a = errors.confirm_email) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.confirm_email) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs21("fieldset", { children: [
      /* @__PURE__ */ jsx23(
        Controller10,
        {
          control,
          name: "password",
          rules: {
            required: t("auth_common:validation.required"),
            minLength: {
              value: 8,
              message: t("auth_common:validation.invalid_password")
            }
          },
          defaultValue: "",
          render: (_p) => {
            var { field: _q } = _p, _r = _q, { onChange } = _r, props = __objRest(_r, ["onChange"]);
            var _a, _b, _c;
            return /* @__PURE__ */ jsx23(
              Input9,
              __spreadProps(__spreadValues({}, props), {
                type: "password",
                "data-testid": "auth_signup_password_input",
                label: t("auth_common:password"),
                autoComplete: "off",
                onChange: (value) => {
                  onChange(value);
                },
                error: ((_a = errors.password) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.password) == null ? void 0 : _c[0])
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx23("div", { className: "mt-2", children: /* @__PURE__ */ jsx23(PasswordBullets_default, { password: password || "" }) })
    ] }),
    /* @__PURE__ */ jsx23(
      Controller10,
      {
        name: "phone",
        control,
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => {
            var _a, _b, _c;
            const hasValidation = (_a = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _a.length;
            if (value.includes(" ")) {
              return t("auth_common:validation.invalid_mobile");
            }
            if (hasValidation) {
              const validStart = (_b = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _b.some(
                (start) => value.startsWith(start)
              );
              const validLength = value.length >= Number((_c = selectedCountry == null ? void 0 : selectedCountry.phone_number_lengths) == null ? void 0 : _c[0]);
              return validStart && validLength || t("auth_common:validation.invalid_mobile");
            }
          }
        },
        render: (_s) => {
          var { field: _t } = _s, _u = _t, { value } = _u, props = __objRest(_u, ["value"]);
          var _a, _b, _c, _d, _e;
          return /* @__PURE__ */ jsx23(
            MobileInput2,
            __spreadProps(__spreadValues({
              lang: locale,
              label: t("auth_common:mobile")
            }, props), {
              country: selectedCountry,
              mobile: value || "",
              onChange: (name, value2) => {
                if (name === "country") {
                  setValue("selected_country", value2);
                } else {
                  setValue("phone", value2);
                }
              },
              error: ((_a = errors == null ? void 0 : errors.phone) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.phone) == null ? void 0 : _c[0]),
              placeholder: selectedCountry ? `${((_d = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _d[0]) || ""}${"x".repeat(
                (((_e = selectedCountry.phone_number_lengths) == null ? void 0 : _e[0]) || 7) - 1
              )}` : ""
            })
          );
        }
      },
      selectedCountry == null ? void 0 : selectedCountry.code
    )
  ] });
};
var SignupFormData_default2 = SignupFormData2;

// src/components/form/v1/Signup.tsx
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var SignupForm = ({ onSuccess }) => {
  var _a, _b;
  const {
    newsLetter: { enable, organizationName },
    setTokens
  } = useAuth();
  const { locale } = useRouter18();
  const { supportedCountryCode } = useGeolocation4();
  const { mutateAsync: register, isPending, error } = useRegister2({ lang: locale });
  const {
    getValues,
    setValue,
    watch,
    reset,
    control,
    formState: { errors }
  } = useFormContext17();
  const { t } = useTranslation22(["auth_common"]);
  const { signupEvent, trackEvent } = useAnalytics13();
  const serverErr = error;
  const errorMsg = ((_a = serverErr == null ? void 0 : serverErr.errors) == null ? void 0 : _a.user) || ((_b = serverErr == null ? void 0 : serverErr.errors) == null ? void 0 : _b.message) || (serverErr == null ? void 0 : serverErr.message) || t("auth_common:error.unknown_error");
  const step = watch("step") || "info";
  const steps = useMemo3(() => {
    let data = {
      step: 1,
      text: "back_to_login",
      prevStep: "info"
    };
    switch (step) {
      case "password":
        data = {
          step: 2,
          text: "back",
          prevStep: "info"
        };
        break;
      default:
        break;
    }
    return data;
  }, [step]);
  const onSubmit = (data) => __async(null, null, function* () {
    try {
      const _a2 = data, { selected_country, newsletterAgree } = _a2, restData = __objRest(_a2, ["selected_country", "newsletterAgree"]);
      const referral_key = getItem2("referral_key");
      const body = __spreadProps(__spreadValues({}, restData), {
        newsletter: newsletterAgree,
        country_code: selected_country == null ? void 0 : selected_country.code,
        country: selected_country == null ? void 0 : selected_country.code,
        referral_key
      });
      const user = yield register({ user: body, locale });
      const updatedUser = __spreadProps(__spreadValues({}, user || {}), {
        new_user: false
      });
      setTokens({
        accessToken: updatedUser == null ? void 0 : updatedUser.access_token,
        refreshToken: updatedUser == null ? void 0 : updatedUser.refresh_token,
        tokenExpiresIn: updatedUser == null ? void 0 : updatedUser.token_expires_in
      });
      setLoggerUser4(updatedUser);
      if (onSuccess) {
        onSuccess(updatedUser);
      }
      signupEvent({ method: "email", user: updatedUser });
    } catch (error2) {
      if (error2 instanceof Exception4) {
        setValue("error", error2);
      }
      trackEvent({ event: "Signup - Signup Failed", data: { email: data.email, error: error2 } });
    }
  });
  return /* @__PURE__ */ jsxs22("div", { className: "overflow-y-visible", id: "signup-wrapper", children: [
    /* @__PURE__ */ jsx24("div", { className: "relative", children: /* @__PURE__ */ jsx24("div", { className: "flex items-center justify-between", children: /* @__PURE__ */ jsx24(
      Back_default,
      {
        className: "mb-2 text-base font-medium uppercase",
        "data-testid": "auth_signup_back_to_login_button",
        onClick: () => {
          if (step === "info") {
            trackEvent({ event: "Login - Page View" });
            return setValue("mode", "email");
          }
          setValue("step", steps.prevStep);
          reset();
        },
        text: t(`auth_common:${steps.text}`)
      }
    ) }) }),
    /* @__PURE__ */ jsx24(AnimatePresence7, { initial: false, presenceAffectsLayout: true, mode: "wait", children: /* @__PURE__ */ jsx24(
      motion7.div,
      {
        className: "relative z-50 min-h-[250px]",
        initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
        transition: { duration: 0.2 },
        children: /* @__PURE__ */ jsx24(SignupFormData_default2, { onSubmit })
      },
      `${step}-${locale}`
    ) }),
    /* @__PURE__ */ jsxs22("div", { className: "mt-8 space-y-2", children: [
      error && errorMsg && /* @__PURE__ */ jsx24("div", { className: "text-error pb-2 text-center text-sm", children: (errorMsg == null ? void 0 : errorMsg.includes("exists")) ? /* @__PURE__ */ jsxs22(Trans3, { i18nKey: "account_exists", ns: "auth_common", children: [
        /* @__PURE__ */ jsx24(
          "button",
          {
            className: "text-primary hover:underline",
            "aria-label": "Login",
            type: "button",
            onClick: () => setValue("mode", "email")
          }
        ),
        /* @__PURE__ */ jsx24(
          "button",
          {
            className: "text-primary hover:underline",
            "aria-label": "Reset Password",
            type: "button",
            onClick: () => setValue("mode", "reset-email")
          }
        )
      ] }) : /* @__PURE__ */ jsx24("p", { children: errorMsg }) }),
      /* @__PURE__ */ jsx24(
        Controller11,
        {
          name: "agreeTerms",
          control,
          rules: {
            required: t("auth_common:validation.required")
          },
          defaultValue: false,
          render: (_c) => {
            var { field: _d } = _c, _e = _d, { onChange, value } = _e, props = __objRest(_e, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx24(
              Checkbox2,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx24("span", { className: "text-sm", children: /* @__PURE__ */ jsx24(Trans3, { i18nKey: "agree", ns: "auth_common", children: /* @__PURE__ */ jsx24(
                  Button18,
                  {
                    as: Link3,
                    "data-testid": "auth_terms_link",
                    shape: "text",
                    className: "text-primary px-0.5 py-px font-semibold underline",
                    href: `/${locale}/terms${supportedCountryCode ? `?country_code=${supportedCountryCode}` : ""}`,
                    children: t("auth_common:agree")
                  }
                ) }) }),
                "data-testid": "auth_terms_checkbox",
                checked: value,
                onChange: (v) => onChange(v),
                error: (_a2 = errors.agreeTerms) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      enable && /* @__PURE__ */ jsx24(
        Controller11,
        {
          name: "newsletterAgree",
          control,
          defaultValue: true,
          render: (_f) => {
            var { field: _g } = _f, _h = _g, { onChange, value } = _h, props = __objRest(_h, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx24(
              Checkbox2,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx24("span", { className: "text-sm", children: /* @__PURE__ */ jsx24(
                  Trans3,
                  {
                    i18nKey: "newsletterAgree",
                    ns: "auth_common",
                    values: { name: organizationName },
                    children: /* @__PURE__ */ jsx24("strong", {})
                  }
                ) }),
                checked: value,
                "data-testid": "auth_newsletter_checkbox",
                onChange: (v) => onChange(v),
                error: (_a2 = errors.newsletterAgree) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx24(
        Controller11,
        {
          name: "data_consent",
          control,
          defaultValue: true,
          render: (_i) => {
            var { field: _j } = _i, _k = _j, { onChange, value } = _k, props = __objRest(_k, ["onChange", "value"]);
            var _a2;
            return /* @__PURE__ */ jsx24(
              Checkbox2,
              __spreadProps(__spreadValues({}, props), {
                label: /* @__PURE__ */ jsx24("span", { className: "text-sm", children: t("auth_common:third_party") }),
                checked: value,
                "data-testid": "auth_third_party_checkbox",
                onChange: (v) => onChange(v),
                error: (_a2 = errors.data_consent) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx24("div", { className: "pt-4", children: /* @__PURE__ */ jsx24(
        Button18,
        {
          type: "submit",
          form: `signup-form-${step}`,
          className: "w-full",
          onClick: () => {
            trackEvent({ event: "Signup - Signup Btn", data: { email: getValues("email") } });
          },
          loading: isPending,
          "data-testid": "auth_signup_submit_button",
          children: /* @__PURE__ */ jsx24("span", { className: "text-lg font-bold", children: t("auth_common:create_account") })
        }
      ) })
    ] })
  ] });
};
var Signup_default2 = SignupForm;

// src/components/form/engines/AuthV1Engine.tsx
import { Fragment as Fragment2, jsx as jsx25, jsxs as jsxs23 } from "react/jsx-runtime";
var AuthV1Engine = ({
  methods,
  title,
  onSuccess
}) => {
  const { t } = useTranslation23(["auth_common"]);
  const { trackEvent } = useAnalytics14();
  const mode = methods.watch("mode");
  const showHeader = useMemo4(
    () => ["email", "otp", "reset-otp", "reset-email", "reset-password"].includes(mode),
    [mode]
  );
  const header = useMemo4(() => {
    if (!showHeader) return null;
    switch (mode) {
      case "otp":
        return /* @__PURE__ */ jsx25("div", { className: "px-2", children: /* @__PURE__ */ jsx25(
          Back_default,
          {
            className: "text-base font-medium uppercase",
            onClick: () => methods.setValue("mode", "email"),
            text: t("auth_common:change_email_address"),
            id: "change-email_address"
          }
        ) });
      case "reset-email":
        return /* @__PURE__ */ jsxs23("div", { children: [
          /* @__PURE__ */ jsx25(
            Back_default,
            {
              onClick: () => {
                methods.setValue("mode", "email");
                trackEvent({ event: "Login - Page View" });
              },
              text: t("auth_common:back_to_login"),
              id: "back-login",
              "data-testid": "auth_email_form_back_to_login_button"
            }
          ),
          /* @__PURE__ */ jsx25("p", { className: "text-2xl font-bold", children: t("auth_common:reset_password_title") }),
          /* @__PURE__ */ jsx25("p", { className: "text-text-secondary text-sm", children: t("auth_common:reset_password_text") })
        ] });
      case "reset-otp":
        return /* @__PURE__ */ jsx25("div", { children: /* @__PURE__ */ jsx25(
          Back_default,
          {
            onClick: () => methods.setValue("mode", "email"),
            text: t("auth_common:back_to_login"),
            id: "back-login",
            "data-testid": "auth_otp_form_back_to_login_button"
          }
        ) });
      case "reset-password":
        return /* @__PURE__ */ jsxs23("div", { children: [
          /* @__PURE__ */ jsx25(
            Back_default,
            {
              onClick: () => {
                methods.setValue("mode", "email");
                trackEvent({ event: "Login - Page View" });
              },
              text: t("auth_common:back_to_login"),
              id: "back-login",
              "data-testid": "auth_new_password_form_back_to_login_button"
            }
          ),
          /* @__PURE__ */ jsx25("p", { className: "text-2xl font-bold", children: t("auth_common:reset_password_title") }),
          /* @__PURE__ */ jsx25("p", { className: "text-text-secondary text-sm", children: t("auth_common:password_description") })
        ] });
      default:
        return /* @__PURE__ */ jsx25("div", { className: "pb-2 text-2xl font-bold", children: title ? t(title) : t("auth_common:signup_title") });
    }
  }, [mode, showHeader, t, title]);
  const Screen = useMemo4(() => {
    switch (mode) {
      case "otp":
      case "reset-otp":
        return /* @__PURE__ */ jsx25(OtpSignup_default, {});
      case "reset-password":
        return /* @__PURE__ */ jsx25(NewPasswordForm_default, {});
      case "reset-password-success":
        return /* @__PURE__ */ jsx25(ResetPasswordSuccess_default2, {});
      case "signup":
        return /* @__PURE__ */ jsx25(Signup_default2, { onSuccess });
      default:
        return /* @__PURE__ */ jsx25(Login_default, { onSuccess });
    }
  }, [mode, onSuccess]);
  return /* @__PURE__ */ jsxs23(Fragment2, { children: [
    header,
    Screen
  ] });
};

// src/components/form/AuthForm.tsx
import { jsx as jsx26, jsxs as jsxs24 } from "react/jsx-runtime";
function Shell({
  className,
  defaultValues,
  header,
  children
}) {
  const { locale } = useRouter19();
  const methods = useForm2({
    defaultValues
  });
  const mode = methods.watch("mode");
  return /* @__PURE__ */ jsx26(WithGrecaptcha2, { children: /* @__PURE__ */ jsx26(FormProvider, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsxs24(Paper3, { className: twMerge7("bg-paper m-4 overflow-hidden", className), children: [
    header ? /* @__PURE__ */ jsx26("div", { className: "mb-4 flex items-start justify-between gap-2", children: header }) : null,
    /* @__PURE__ */ jsx26(AnimatePresence8, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx26(
      motion8.div,
      {
        initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
        transition: { duration: 0.2 },
        children: children(methods)
      },
      `${mode}-${locale}-${mode}`
    ) })
  ] }) })) });
}
var AuthForm = ({
  initialMode,
  className,
  title,
  onSuccess,
  version: versionOverride
}) => {
  const { useV2 } = useAuthConfig();
  const { user } = useAuth();
  const { country } = useGeolocation5();
  const version = versionOverride != null ? versionOverride : useV2 ? "v2" : "v1";
  if (version === "v2") {
    const defaultValues = {
      email: "",
      mode: initialMode != null ? initialMode : "signin-email",
      selected_country: country != null ? country : void 0
    };
    return /* @__PURE__ */ jsx26(
      Shell,
      {
        className,
        defaultValues,
        children: (methods) => /* @__PURE__ */ jsx26(AuthV2Engine, { methods, title, onSuccess })
      },
      country == null ? void 0 : country.code
    );
  }
  const defaultV1Mode = initialMode != null ? initialMode : (user == null ? void 0 : user.force_verify_email) ? "otp" : "email";
  const defaultValuesV1 = {
    email: "",
    mode: defaultV1Mode,
    selected_country: country != null ? country : void 0
  };
  return /* @__PURE__ */ jsx26(
    Shell,
    {
      className,
      defaultValues: defaultValuesV1,
      children: (methods) => /* @__PURE__ */ jsx26(AuthV1Engine, { methods, title, onSuccess })
    },
    country == null ? void 0 : country.code
  );
};

// src/components/login/AuthModal.tsx
import { useDeviceDetect } from "@wbk/hooks";
import { BottomSheet, Modal } from "@wbk/ui";
import { useTranslation as useTranslation24 } from "@wbk/config";
import { jsx as jsx27 } from "react/jsx-runtime";
var AuthModal = ({ show, render, onClose, onSuccess, ignoreHistory }) => {
  const { t } = useTranslation24("auth_common");
  const { isMobile } = useDeviceDetect();
  return isMobile ? /* @__PURE__ */ jsx27(
    BottomSheet,
    {
      closeButton: true,
      sectionClassName: "pb-20",
      show,
      onClose,
      render,
      ignoreHistory,
      body: ({ close }) => /* @__PURE__ */ jsx27(
        AuthForm,
        {
          title: t("auth_common:login"),
          className: "m-0 border-none",
          onSuccess: (user) => {
            close();
            onSuccess == null ? void 0 : onSuccess(user);
          }
        }
      )
    }
  ) : /* @__PURE__ */ jsx27(
    Modal,
    {
      closeButton: true,
      show,
      onClose,
      className: "max-h-svh pb-2",
      render,
      ignoreHistory,
      body: ({ close }) => /* @__PURE__ */ jsx27(
        AuthForm,
        {
          title: t("auth_common:login"),
          className: "m-0 border-none",
          onSuccess: (user) => {
            close();
            onSuccess == null ? void 0 : onSuccess(user);
          }
        }
      )
    }
  );
};
var AuthModal_default = AuthModal;

// src/components/logout/LogoutModal.tsx
import { Button as Button19, ResponsiveModal } from "@wbk/ui";
import { useLogout } from "@wbk/api/hooks";
import { useRouter as useRouter20, useTranslation as useTranslation25 } from "@wbk/config";
import { useAnalytics as useAnalytics15 } from "@wbk/analytics";
import { jsx as jsx28, jsxs as jsxs25 } from "react/jsx-runtime";
var LogoutModal = ({ render, onClosed }) => {
  const { locale } = useRouter20();
  const { clearTokens } = useAuth();
  const { t } = useTranslation25(["auth_common"]);
  const { mutateAsync, isPending } = useLogout({ lang: locale });
  const { logoutEvent } = useAnalytics15();
  const onConfirm = (close) => __async(null, null, function* () {
    try {
      yield mutateAsync();
      clearTokens();
      logoutEvent();
      close();
      if (onClosed) {
        onClosed();
      }
    } catch (error) {
    }
  });
  return /* @__PURE__ */ jsx28(
    ResponsiveModal,
    {
      render,
      action: false,
      cancel: false,
      body: ({ close }) => /* @__PURE__ */ jsxs25("div", { className: "pb-4", children: [
        /* @__PURE__ */ jsx28("div", { className: "bg-error/30 mx-auto flex h-14 w-14 items-center justify-center rounded-full text-white opacity-50", children: /* @__PURE__ */ jsx28(
          "svg",
          {
            className: "h-10 w-10",
            xmlns: "http://www.w3.org/2000/svg",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor",
            "aria-hidden": "true",
            children: /* @__PURE__ */ jsx28(
              "path",
              {
                strokeLinecap: "round",
                strokeLinejoin: "round",
                strokeWidth: "1.2",
                d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsx28("p", { className: "my-6 text-center text-xl", children: t("auth_common:confirm_logout") }),
        /* @__PURE__ */ jsxs25("div", { className: "flex items-baseline justify-evenly gap-4", children: [
          /* @__PURE__ */ jsx28(
            Button19,
            {
              "data-testid": "auth_cancel_logout",
              onClick: close,
              shape: "outlined",
              theme: "action",
              className: "w-full",
              disabled: isPending,
              children: t("auth_common:cancel")
            }
          ),
          /* @__PURE__ */ jsx28(
            Button19,
            {
              "data-testid": "auth_logout",
              theme: "error",
              className: "w-full uppercase",
              onClick: () => {
                onConfirm(close);
              },
              loading: isPending,
              children: t("auth_common:logout")
            }
          )
        ] })
      ] })
    }
  );
};
var LogoutModal_default = LogoutModal;

// src/components/profile/FormItems.tsx
import { Controller as Controller12, useFormContext as useFormContext18 } from "react-hook-form";
import { Input as Input10, MobileInput as MobileInput3, Select, ToggleGroup } from "@wbk/ui";
import { useGetCities } from "@wbk/api/hooks";
import { COUNTRIES, dateAdd } from "@wbk/utils";
import { useRouter as useRouter21, useTranslation as useTranslation26 } from "@wbk/config";
import { Fragment as Fragment3, jsx as jsx29, jsxs as jsxs26 } from "react/jsx-runtime";
var ProfileFormItems = ({ error }) => {
  var _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
  const { locale } = useRouter21();
  const { t } = useTranslation26(["auth_common"]);
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext18();
  const nationality = watch("nationality");
  const countryOfResidence = watch("country");
  const selectedCountry = watch("dialCountry");
  const { data: cities, error: cityError } = useGetCities(
    { country: countryOfResidence || "", lang: locale },
    { enabled: !!countryOfResidence }
  );
  return /* @__PURE__ */ jsxs26(Fragment3, { children: [
    /* @__PURE__ */ jsx29(
      Controller12,
      {
        name: "phone",
        control,
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => {
            var _a, _b;
            const hasValidation = (_a = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _a.length;
            if (hasValidation) {
              const validStart = (_b = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _b.some(
                (start) => value == null ? void 0 : value.startsWith(start)
              );
              return validStart || t("auth_common:validation.invalid_mobile");
            }
            return true;
          }
        },
        render: (_a) => {
          var { field: _b } = _a, _c = _b, { value } = _c, props = __objRest(_c, ["value"]);
          var _a2, _b2, _c2, _d2, _e2;
          return /* @__PURE__ */ jsx29(
            MobileInput3,
            __spreadProps(__spreadValues({}, props), {
              label: t("auth_common:mobile"),
              portal: false,
              country: selectedCountry,
              mobile: value || "",
              onChange: (name, value2) => {
                if (name === "country") {
                  setValue("dialCountry", value2);
                } else {
                  setValue("phone", value2);
                }
              },
              error: ((_a2 = errors == null ? void 0 : errors.phone) == null ? void 0 : _a2.message) || ((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.phone) == null ? void 0 : _c2[0]),
              placeholder: selectedCountry ? `${((_d2 = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _d2[0]) || ""}${"x".repeat(
                (((_e2 = selectedCountry == null ? void 0 : selectedCountry.phone_number_lengths) == null ? void 0 : _e2[0]) || 7) - 1
              )}` : ""
            })
          );
        }
      },
      `phone-${selectedCountry == null ? void 0 : selectedCountry.code}`
    ),
    /* @__PURE__ */ jsx29(
      Input10,
      __spreadProps(__spreadValues({
        label: t("auth_common:first_name"),
        id: "first_name",
        error: ((_d = errors.first_name) == null ? void 0 : _d.message) || ((_f = (_e = error == null ? void 0 : error.errors) == null ? void 0 : _e.first_name) == null ? void 0 : _f[0])
      }, register("first_name", {
        required: t("auth_common:validation.required"),
        maxLength: {
          value: 25,
          message: t("auth_common:validation.max_length", { length: 25 })
        }
      })), {
        onChange: (value) => {
          setValue("first_name", value);
        }
      })
    ),
    /* @__PURE__ */ jsx29(
      Input10,
      __spreadProps(__spreadValues({
        label: t("auth_common:last_name"),
        id: "last_name",
        error: ((_g = errors.last_name) == null ? void 0 : _g.message) || ((_i = (_h = error == null ? void 0 : error.errors) == null ? void 0 : _h.last_name) == null ? void 0 : _i[0])
      }, register("last_name", {
        required: t("auth_common:validation.required"),
        maxLength: {
          value: 25,
          message: t("auth_common:validation.max_length", { length: 25 })
        }
      })), {
        onChange: (value) => {
          setValue("last_name", value);
        }
      })
    ),
    /* @__PURE__ */ jsx29(
      Controller12,
      {
        name: "gender",
        control,
        rules: { required: t("auth_common:validation.required") },
        render: ({ field }) => {
          var _a, _b, _c;
          return /* @__PURE__ */ jsx29(
            ToggleGroup,
            __spreadProps(__spreadValues({
              label: t("auth_common:gender"),
              options: [
                { text: t("auth_common:male"), value: "male" },
                { text: t("auth_common:female"), value: "female" }
              ]
            }, field), {
              value: field.value || "",
              type: "single",
              error: ((_a = errors.gender) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.gender) == null ? void 0 : _c[0])
            })
          );
        }
      }
    ),
    /* @__PURE__ */ jsx29(
      Input10,
      __spreadProps(__spreadValues({
        type: "date",
        className: "uppercase",
        pattern: "\\d{4}-\\d{2}-\\d{2}",
        max: (_l = (_k = (_j = dateAdd({
          date: /* @__PURE__ */ new Date(),
          value: -MIN_AGE_ALLOWED,
          interval: "year"
        })) == null ? void 0 : _j.toISOString()) == null ? void 0 : _k.split("T")) == null ? void 0 : _l[0],
        label: t("auth_common:birthdate")
      }, register("dob", { required: t("auth_common:validation.required") })), {
        onChange: (value) => {
          setValue("dob", value);
        },
        error: ((_m = errors.dob) == null ? void 0 : _m.message) || ((_o = (_n = error == null ? void 0 : error.errors) == null ? void 0 : _n.dob) == null ? void 0 : _o[0])
      })
    ),
    /* @__PURE__ */ jsx29(
      Controller12,
      {
        name: "nationality",
        control,
        rules: { required: t("auth_common:validation.required") },
        defaultValue: "",
        render: (_p) => {
          var { field: _q } = _p, _r = _q, { onChange } = _r, props = __objRest(_r, ["onChange"]);
          var _a, _b, _c;
          return /* @__PURE__ */ jsx29(
            Select,
            __spreadProps(__spreadValues({}, props), {
              id: "nationality",
              autoComplete: "off",
              portal: false,
              onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
              label: t("auth_common:nationality"),
              options: COUNTRIES.map((c) => ({
                id: c.code,
                value: c.code,
                text: c.name[locale] || c.name.en,
                icon: /* @__PURE__ */ jsx29(
                  "img",
                  {
                    className: "shrink-0",
                    src: `https://wbk-assets-backup.s3.eu-west-1.amazonaws.com/flags/w80/${c.code.toLowerCase()}.png`,
                    width: 20,
                    height: 14,
                    alt: c.name.en
                  }
                )
              })),
              error: ((_a = errors.nationality) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.nationality) == null ? void 0 : _c[0])
            })
          );
        }
      },
      `nationality-${nationality}`
    ),
    /* @__PURE__ */ jsx29(
      Controller12,
      {
        name: "country",
        control,
        rules: { required: t("auth_common:validation.required") },
        defaultValue: "",
        render: (_s) => {
          var { field: _t } = _s, _u = _t, { onChange } = _u, props = __objRest(_u, ["onChange"]);
          var _a, _b, _c;
          return /* @__PURE__ */ jsx29(
            Select,
            __spreadProps(__spreadValues({}, props), {
              id: "country",
              autoComplete: "off",
              portal: false,
              onChange: (selected) => {
                onChange((selected == null ? void 0 : selected.value) || "");
                setValue("city", "", { shouldValidate: true });
              },
              label: t("auth_common:country_of_residence"),
              options: COUNTRIES.map((c) => ({
                id: c.code,
                value: c.code,
                text: c.name[locale] || c.name.en,
                icon: /* @__PURE__ */ jsx29(
                  "img",
                  {
                    className: "shrink-0",
                    src: `https://wbk-assets-backup.s3.eu-west-1.amazonaws.com/flags/w80/${c.code.toLowerCase()}.png`,
                    width: 20,
                    height: 14,
                    alt: c.name.en
                  }
                )
              })),
              error: ((_a = errors.country) == null ? void 0 : _a.message) || ((_c = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.country) == null ? void 0 : _c[0])
            })
          );
        }
      },
      `country-${countryOfResidence}`
    ),
    /* @__PURE__ */ jsx29(
      Controller12,
      {
        name: "city",
        control,
        rules: { required: t("auth_common:validation.required") },
        defaultValue: "",
        render: (_v) => {
          var { field: _w } = _v, _x = _w, { onChange } = _x, props = __objRest(_x, ["onChange"]);
          var _a, _b, _c, _d2;
          return /* @__PURE__ */ jsx29(
            Select,
            __spreadProps(__spreadValues({}, props), {
              id: "city",
              autoComplete: "off",
              portal: false,
              onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
              label: t("auth_common:city"),
              options: (_a = cities || []) == null ? void 0 : _a.map((city) => ({
                id: city._id,
                value: city.name,
                text: city.name
              })),
              error: ((_b = errors.city) == null ? void 0 : _b.message) || ((_d2 = (_c = error == null ? void 0 : error.errors) == null ? void 0 : _c.city) == null ? void 0 : _d2[0]) || (cityError == null ? void 0 : cityError.message)
            })
          );
        }
      },
      `city-${countryOfResidence}-${cities == null ? void 0 : cities.length}`
    )
  ] });
};
var FormItems_default = ProfileFormItems;

// src/components/profile/Menu.tsx
import { useMemo as useMemo5 } from "react";
import { Button as Button20 } from "@wbk/ui";
import {
  AccessibilityIcon,
  BidIcon,
  FlatTicketIcon,
  GiftIcon,
  HeartOutlined,
  MultiplePersonIcon,
  PersonRoundedIcon,
  WalletIcon
} from "@wbk/svg";
import { Link as Link4, useRouter as useRouter22, useTranslation as useTranslation27 } from "@wbk/config";
import { useGetUserWallets } from "@wbk/api/hooks";
import { jsx as jsx30, jsxs as jsxs27 } from "react/jsx-runtime";
var MENU_ITEMS = [
  "my_bookings",
  "my_bids",
  "my_wallet",
  "my_info",
  "accessibility",
  "favorites_and_interests",
  "vouchers",
  "my_travelers"
];
var DEFAULT_MENU = ["my_bookings", "my_info"];
var PROFILE_LINKS = [
  {
    label: MENU_ITEMS[0],
    href: "/profile/bookings",
    isActive: (pathname) => pathname.startsWith("/profile/bookings"),
    Icon: FlatTicketIcon
  },
  {
    label: MENU_ITEMS[1],
    href: "/profile/bids",
    isActive: (pathname) => pathname.startsWith("/profile/bids"),
    Icon: BidIcon
  },
  {
    label: MENU_ITEMS[2],
    href: "/profile/wallet",
    isActive: (pathname) => pathname.startsWith("/profile/wallet"),
    Icon: WalletIcon
  },
  {
    label: MENU_ITEMS[3],
    href: "/profile",
    isActive: (pathname) => pathname === "/profile",
    Icon: PersonRoundedIcon
  },
  {
    label: MENU_ITEMS[4],
    href: "/profile/accessibility",
    isActive: (pathname) => pathname.startsWith("/profile/accessibility"),
    Icon: AccessibilityIcon
  },
  {
    label: MENU_ITEMS[5],
    href: "/profile/favorites",
    isActive: (pathname) => pathname.startsWith("/profile/favorites"),
    Icon: HeartOutlined
  },
  {
    label: MENU_ITEMS[6],
    href: "/profile/vouchers",
    isActive: (pathname) => pathname.startsWith("/profile/vouchers"),
    Icon: GiftIcon
  },
  {
    label: MENU_ITEMS[7],
    href: "/profile/travelers",
    isActive: (pathname) => pathname.startsWith("/profile/travelers"),
    Icon: MultiplePersonIcon
  }
];
var ProfileMenu = ({ menu = DEFAULT_MENU }) => {
  const { user } = useAuth();
  const { locale } = useRouter22();
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const { t } = useTranslation27(["auth_common"]);
  const {
    data: wallets,
    isError,
    isSuccess
  } = useGetUserWallets({ lang: locale }, { enabled: !!user });
  const hasWallet = !isError && (!isSuccess || wallets.length > 0);
  const links = useMemo5(() => {
    const walletEnabled = (user == null ? void 0 : user.wallet_enabled) === "on" && hasWallet;
    if (!walletEnabled) {
      return PROFILE_LINKS.filter((m) => m.label !== "my_wallet").filter(
        (m) => menu.includes(m.label)
      );
    }
    return PROFILE_LINKS.filter((m) => m.label && menu.includes(m.label));
  }, [menu, user == null ? void 0 : user.wallet_enabled, hasWallet]);
  return /* @__PURE__ */ jsxs27("ul", { className: "my-4 flex gap-4 overflow-x-auto p-0.5 lg:mt-4 lg:flex-col lg:gap-2", children: [
    links == null ? void 0 : links.map(({ href, label, Icon, isActive }) => {
      const active = isActive(pathname.replace(`/${locale}`, ""));
      return /* @__PURE__ */ jsx30("li", { className: "shrink-0", children: /* @__PURE__ */ jsxs27(
        Button20,
        {
          as: Link4,
          href: `/${locale}${href}`,
          shape: "text",
          theme: "action",
          "data-active": active,
          className: "hover:bg-action/10 data-[active=true]:bg-action/10 flex items-center justify-start gap-2 rounded-lg p-3",
          id: `profile-menu-${label}`,
          "data-menuitem": label,
          "data-location": "profile-menu",
          children: [
            /* @__PURE__ */ jsx30(Icon, { className: "h-6 w-6 shrink-0" }),
            t(`auth_common:${label}`)
          ]
        }
      ) }, href);
    }),
    /* @__PURE__ */ jsxs27("li", { className: "flex grow items-end justify-end lg:flex-col", children: [
      /* @__PURE__ */ jsx30("hr", { className: "border-paper hidden w-full lg:block" }),
      /* @__PURE__ */ jsx30(
        LogoutModal_default,
        {
          render: ({ open }) => /* @__PURE__ */ jsx30(
            "button",
            {
              onClick: open,
              className: "hover:bg-error text-error hover:text-error-contrast cursor-pointer rounded-lg p-3 lg:mt-6 lg:inline-flex lg:w-full",
              id: "profile-menu-logout",
              "data-menuitem": "logout",
              "data-location": "profile-menu",
              "aria-label": t("auth_common:logout"),
              children: t("auth_common:logout")
            }
          )
        }
      )
    ] })
  ] });
};

// src/components/profile/EmailEditor.tsx
import { useRef as useRef2, useState as useState4 } from "react";
import { WithGrecaptcha as WithGrecaptcha3 } from "@wbk/api/grecaptcha";
import { Button as Button21, Paper as Paper4, Toast, Typography as Typography15 } from "@wbk/ui";
import { useTranslation as useTranslation28 } from "@wbk/config";
import { jsx as jsx31, jsxs as jsxs28 } from "react/jsx-runtime";
var EmailEditor = () => {
  const { user } = useAuth();
  const { t } = useTranslation28(["auth_common"]);
  const [mode, setMode] = useState4();
  const toast = useRef2(void 0);
  return /* @__PURE__ */ jsxs28(WithGrecaptcha3, { children: [
    /* @__PURE__ */ jsxs28(Paper4, { className: "space-y-6", children: [
      /* @__PURE__ */ jsx31("h2", { className: "text-2xl", children: t("auth_common:account_information") }),
      /* @__PURE__ */ jsxs28("div", { className: "flex items-center justify-between gap-4", children: [
        /* @__PURE__ */ jsxs28("div", { className: "line-clamp-2", children: [
          /* @__PURE__ */ jsxs28(Typography15, { variant: "label-L", children: [
            t("auth_common:email"),
            !(user == null ? void 0 : user.email_verified) && /* @__PURE__ */ jsx31("span", { children: ` ( ${t("auth_common:not_verified")} )` })
          ] }),
          /* @__PURE__ */ jsx31(Typography15, { variant: "label-M", color: "text-tertiary", children: (user == null ? void 0 : user.email) || " - " })
        ] }),
        !(user == null ? void 0 : user.email_verified) && /* @__PURE__ */ jsxs28("div", { className: "flex shrink-0 gap-2", children: [
          /* @__PURE__ */ jsx31(
            Button21,
            {
              className: "p-1 text-sm underline",
              type: "button",
              theme: "action",
              shape: "text",
              onClick: () => setMode("otp"),
              children: t("auth_common:verify")
            }
          ),
          /* @__PURE__ */ jsx31(
            Button21,
            {
              className: "p-1 text-sm underline",
              type: "button",
              theme: "action",
              shape: "text",
              onClick: () => setMode("email-update"),
              children: t("auth_common:edit")
            }
          )
        ] })
      ] }),
      (user == null ? void 0 : user.fan_id) && /* @__PURE__ */ jsxs28("div", { className: "line-clamp-2", children: [
        /* @__PURE__ */ jsx31(Typography15, { variant: "label-L", children: t("auth_common:fan_id.fan_id") }),
        /* @__PURE__ */ jsx31(Typography15, { variant: "label-M", color: "text-tertiary", children: user.fan_id })
      ] })
    ] }),
    /* @__PURE__ */ jsx31(
      OtpCheckPoint,
      {
        initialMode: mode,
        show: !!mode,
        onClose: () => {
          setMode(void 0);
        },
        onSuccess: () => {
          var _a;
          setMode(void 0);
          (_a = toast.current) == null ? void 0 : _a.publish({
            title: t("auth_common:email_verified_successfully"),
            icon: true
          });
        },
        profile: true
      },
      mode
    ),
    /* @__PURE__ */ jsx31(Toast, { ref: toast })
  ] });
};
var EmailEditor_default = EmailEditor;

// src/components/profile/PersonalEditor.tsx
import { useMemo as useMemo6 } from "react";
import { COUNTRIES as COUNTRIES3, formatDate } from "@wbk/utils";
import { useRouter as useRouter24, useTranslation as useTranslation30 } from "@wbk/config";
import { Paper as Paper5 } from "@wbk/ui";

// src/components/profile/Form.tsx
import { useEffect as useEffect7 } from "react";
import { FormProvider as FormProvider2, useForm as useForm3, useFormContext as useFormContext19 } from "react-hook-form";
import { Button as Button22, ResponsiveModal as ResponsiveModal2 } from "@wbk/ui";
import { useUpdateProfile as useUpdateProfile2 } from "@wbk/api/hooks";
import { useGeolocation as useGeolocation6, useRouter as useRouter23, useTranslation as useTranslation29 } from "@wbk/config";
import { COUNTRIES as COUNTRIES2 } from "@wbk/utils";
import { jsx as jsx32, jsxs as jsxs29 } from "react/jsx-runtime";
var ProfileForm = () => {
  const { t } = useTranslation29("auth_common");
  const methods = useForm3();
  return /* @__PURE__ */ jsx32(FormProvider2, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx32(
    ResponsiveModal2,
    {
      title: t("auth_common:personal_information"),
      action: {
        label: t("auth_common:save_changes"),
        form: "personal-information-form",
        loading: methods.watch("loading")
      },
      render: ({ open }) => /* @__PURE__ */ jsx32(
        Button22,
        {
          className: "p-1 text-sm underline",
          type: "button",
          theme: "action",
          shape: "text",
          onClick: open,
          children: t("auth_common:edit")
        }
      ),
      body: ({ close }) => /* @__PURE__ */ jsx32(FormBody, { onClose: close })
    }
  ) }));
};
var FormBody = ({ onClose }) => {
  const { locale } = useRouter23();
  const { mutateAsync: updateProfile, error } = useUpdateProfile2({ lang: locale });
  const { setValue, handleSubmit, reset } = useFormContext19();
  const { user } = useAuth();
  const { country: detectedCountry } = useGeolocation6();
  useEffect7(() => {
    var _a;
    const dialCountry = COUNTRIES2.find((c) => c.code === (user == null ? void 0 : user.cell_country_code)) || detectedCountry;
    const phone = (_a = user == null ? void 0 : user.cell_number) == null ? void 0 : _a.replace(`+${dialCountry == null ? void 0 : dialCountry.dial_code}`, "");
    reset(__spreadProps(__spreadValues({}, getDefaultUser(user)), { dialCountry, phone }));
  }, [detectedCountry == null ? void 0 : detectedCountry.code, user == null ? void 0 : user.cell_country_code, user == null ? void 0 : user.cell_number]);
  const onSubmit = (_a) => __async(null, null, function* () {
    var _b = _a, { dialCountry, phone } = _b, data = __objRest(_b, ["dialCountry", "phone"]);
    var _a2;
    try {
      setValue("loading", true);
      const body = __spreadProps(__spreadValues({}, data), {
        cell_country_code: dialCountry == null ? void 0 : dialCountry.code,
        cell_number: phone,
        dob: (_a2 = data.dob) == null ? void 0 : _a2.split("-").reverse().join("/")
      });
      yield updateProfile({ user: body, locale });
      onClose();
    } finally {
      setValue("loading", false);
    }
  });
  return /* @__PURE__ */ jsxs29(
    "form",
    {
      className: "space-y-4",
      onSubmit: handleSubmit(onSubmit),
      noValidate: true,
      id: "personal-information-form",
      children: [
        /* @__PURE__ */ jsx32(FormItems_default, { error }),
        (error == null ? void 0 : error.message) && /* @__PURE__ */ jsx32("p", { className: "text-error", children: error == null ? void 0 : error.message })
      ]
    }
  );
};
var Form_default = ProfileForm;

// src/components/profile/PersonalEditor.tsx
import { Fragment as Fragment4, jsx as jsx33, jsxs as jsxs30 } from "react/jsx-runtime";
var PersonalEditor = () => {
  const { user } = useAuth();
  const { locale } = useRouter24();
  const { t } = useTranslation30(["auth_common"]);
  const country = useMemo6(() => {
    return COUNTRIES3.find((c) => c.code === (user == null ? void 0 : user.country));
  }, [user == null ? void 0 : user.country]);
  const countryName = (country == null ? void 0 : country.name[locale]) || (country == null ? void 0 : country.name.en);
  const residence = (user == null ? void 0 : user.city) ? `${user.city}, ${countryName}` : countryName;
  return /* @__PURE__ */ jsxs30(Paper5, { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs30("div", { className: "flex justify-between gap-4", children: [
      /* @__PURE__ */ jsx33("h2", { className: "text-2xl", children: t("auth_common:personal_information") }),
      /* @__PURE__ */ jsx33(Form_default, {})
    ] }),
    /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
      /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:name") }),
      /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: (user == null ? void 0 : user.name) || " - " })
    ] }),
    (user == null ? void 0 : user.dob) && /* @__PURE__ */ jsxs30(Fragment4, { children: [
      /* @__PURE__ */ jsx33("hr", { className: "border-paper" }),
      /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
        /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:birthdate") }),
        /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: formatDate(user.dob, locale, { dateStyle: "medium" }) || " - " })
      ] })
    ] }),
    /* @__PURE__ */ jsx33("hr", { className: "border-paper" }),
    /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
      /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:mobile") }),
      /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: (user == null ? void 0 : user.cell_number) ? `0${user == null ? void 0 : user.cell_number}` : " - " })
    ] }),
    /* @__PURE__ */ jsx33("hr", { className: "border-paper" }),
    /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
      /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:gender") }),
      /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: t(`auth_common:${(user == null ? void 0 : user.gender) || "male"}`) })
    ] }),
    /* @__PURE__ */ jsx33("hr", { className: "border-paper" }),
    /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
      /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:nationality") }),
      /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: (user == null ? void 0 : user.nationality) || " - " })
    ] }),
    /* @__PURE__ */ jsx33("hr", { className: "border-paper" }),
    /* @__PURE__ */ jsxs30("div", { className: "line-clamp-2", children: [
      /* @__PURE__ */ jsx33("p", { className: "text-lg font-medium", children: t("auth_common:residence") }),
      /* @__PURE__ */ jsx33("p", { className: "text-text-tertiary", children: residence || " - " })
    ] })
  ] });
};
var PersonalEditor_default = PersonalEditor;

// src/components/profile/UpdateAppleEmail.tsx
import { useRef as useRef3, useState as useState5 } from "react";
import { useGetUser } from "@wbk/api/hooks";
import { WithGrecaptcha as WithGrecaptcha4 } from "@wbk/api/grecaptcha";
import { Button as Button23, Paper as Paper6, Toast as Toast2, Typography as Typography16 } from "@wbk/ui";
import { WarningIcon } from "@wbk/svg";
import { useRouter as useRouter25, useTranslation as useTranslation31 } from "@wbk/config";
import { jsx as jsx34, jsxs as jsxs31 } from "react/jsx-runtime";
var UpdateAppleEmail = () => {
  const { t } = useTranslation31(["auth_common"]);
  const { locale } = useRouter25();
  const { refetch: refetchUser } = useGetUser({ lang: locale }, { enabled: false });
  const [mode, setMode] = useState5();
  const toast = useRef3(void 0);
  return /* @__PURE__ */ jsxs31(WithGrecaptcha4, { children: [
    /* @__PURE__ */ jsxs31(Paper6, { border: false, className: "flex gap-2.5 bg-[#6E3C00]", children: [
      /* @__PURE__ */ jsx34(WarningIcon, { className: "mt-1 h-8 w-8 shrink-0" }),
      /* @__PURE__ */ jsxs31("div", { className: "space-y-5", children: [
        /* @__PURE__ */ jsx34(Typography16, { variant: "label-L", children: t("auth_common:profile.apple_relay_email_warning") }),
        /* @__PURE__ */ jsx34(
          Button23,
          {
            type: "button",
            theme: "action",
            shape: "outlined",
            className: "border-action/20",
            onClick: () => setMode("email-update"),
            children: /* @__PURE__ */ jsx34(Typography16, { variant: "label-M", strong: true, children: t("auth_common:profile.link_email") })
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx34(
      OtpCheckPoint,
      {
        initialMode: mode,
        show: !!mode,
        onClose: () => setMode(void 0),
        onSuccess: () => {
          setMode(void 0);
          refetchUser();
        },
        profile: true
      },
      mode
    ),
    /* @__PURE__ */ jsx34(Toast2, { ref: toast })
  ] });
};
var UpdateAppleEmail_default = UpdateAppleEmail;

// src/components/profile/Profile.tsx
import { jsx as jsx35, jsxs as jsxs32 } from "react/jsx-runtime";
var UserProfile = () => {
  const { user } = useAuth();
  return /* @__PURE__ */ jsxs32("div", { className: "mb-4 space-y-6", children: [
    (user == null ? void 0 : user.is_relay_email) && !(user == null ? void 0 : user.relay_email_replaced) ? /* @__PURE__ */ jsx35(UpdateAppleEmail_default, {}) : /* @__PURE__ */ jsx35(EmailEditor_default, {}),
    /* @__PURE__ */ jsx35(PersonalEditor_default, {})
  ] });
};

// src/components/profile/ProfileTop.tsx
import { Button as Button24, Paper as Paper7, Typography as Typography17 } from "@wbk/ui";
import { formatDate as formatDate2 } from "@wbk/utils";
import { Link as Link5, Trans as Trans4, useRouter as useRouter26, useTranslation as useTranslation32 } from "@wbk/config";
import { ChevronIcon as ChevronIcon2, SubscriptionIcon } from "@wbk/svg";
import { useAnalytics as useAnalytics16 } from "@wbk/analytics";
import { jsx as jsx36, jsxs as jsxs33 } from "react/jsx-runtime";
var ProfileTop = ({ subscription }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { user } = useAuth();
  const { locale } = useRouter26();
  const { t } = useTranslation32("auth_common");
  const { trackEvent } = useAnalytics16();
  if (!user) return null;
  const plan = subscription == null ? void 0 : subscription.plan;
  const PlanComp = (subscription == null ? void 0 : subscription.is_cancelled) ? Link5 : "div";
  return /* @__PURE__ */ jsxs33("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs33(Paper7, { className: "w-full overflow-hidden !p-0", children: [
      /* @__PURE__ */ jsxs33("div", { className: "flex items-center gap-5 p-5", children: [
        /* @__PURE__ */ jsxs33("div", { className: "bg-primary text-primary-contrast relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl font-bold uppercase sm:mb-0", children: [
          (_d = (_c = (_b = (_a = user.name) == null ? void 0 : _a.split) == null ? void 0 : _b.call(_a, " ")) == null ? void 0 : _c[0]) == null ? void 0 : _d[0],
          (_h = (_g = (_f = (_e = user.name) == null ? void 0 : _e.split) == null ? void 0 : _f.call(_e, " ")) == null ? void 0 : _g[1]) == null ? void 0 : _h[0]
        ] }),
        /* @__PURE__ */ jsx36(Typography17, { variant: "heading-S", children: user.name })
      ] }),
      !!plan && /* @__PURE__ */ jsx36("div", { className: "bg-paper-level-1 border-paper-level-1 border-t px-5 py-2", children: /* @__PURE__ */ jsxs33("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs33(
          PlanComp,
          {
            to: `/${locale}/subscriptions/manage/${plan.plan_group._id}`,
            className: "flex items-center gap-2",
            children: [
              plan.icon && /* @__PURE__ */ jsx36(
                "img",
                {
                  src: plan.icon,
                  className: "h-5 w-5 shrink-0 object-contain",
                  alt: plan.plan_group.name
                }
              ),
              /* @__PURE__ */ jsx36(Typography17, { strong: true, children: plan.plan_group.name })
            ]
          }
        ),
        plan.is_basic_plan || subscription.is_cancelled ? /* @__PURE__ */ jsxs33(Button24, { as: Link5, href: `/${locale}/subscriptions`, className: "shrink-0 p-2", children: [
          /* @__PURE__ */ jsx36(SubscriptionIcon, { className: "h-4 w-4 shrink-0" }),
          /* @__PURE__ */ jsx36(
            Typography17,
            {
              variant: "body-S",
              strong: true,
              onClick: () => {
                trackEvent({ event: "Header - Subscription Upgrade" });
              },
              children: t("auth_common:upgrade")
            }
          )
        ] }) : /* @__PURE__ */ jsxs33(
          Button24,
          {
            shape: "text",
            theme: "action",
            as: Link5,
            href: `/${locale}/subscriptions/manage/${plan.plan_group._id}`,
            className: "shrink-0 gap-2 p-2",
            onClick: () => {
              trackEvent({ event: "user_subscription_plan" });
              trackEvent({ event: "Header - Subscription View" });
            },
            children: [
              /* @__PURE__ */ jsx36(Typography17, { variant: "body-S", strong: true, children: t("auth_common:view_benefits") }),
              /* @__PURE__ */ jsx36(ChevronIcon2, { className: "shrink-0 rtl:rotate-180" })
            ]
          }
        )
      ] }) })
    ] }),
    !!(subscription == null ? void 0 : subscription.plan) && subscription.is_cancelled && /* @__PURE__ */ jsx36("p", { className: "text-text-secondary text-sm", children: /* @__PURE__ */ jsx36(
      Trans4,
      {
        i18nKey: "subscription_cancelled_benefits_expired",
        ns: "auth_common",
        values: {
          date: formatDate2(subscription.end_date, locale, {
            day: "numeric",
            month: "short",
            year: "numeric"
          })
        },
        children: /* @__PURE__ */ jsx36("span", { className: "text-error" })
      }
    ) })
  ] });
};

// src/components/wrappers/WithAuth.tsx
import { AnimatedWbkLogo } from "@wbk/svg";
import { useDeviceDetect as useDeviceDetect2 } from "@wbk/hooks";
import { useAppLoading } from "@wbk/config";
import { Fragment as Fragment5, jsx as jsx37, jsxs as jsxs34 } from "react/jsx-runtime";
var WithAuth = (Component, config) => {
  const WithAuthComponent = (props) => {
    const { user, isNewUser, loading } = useAuth({ autoFetch: true });
    const { isWebView } = useDeviceDetect2();
    const { appLoadingComponent } = useAppLoading();
    if (loading) {
      return /* @__PURE__ */ jsxs34(Fragment5, { children: [
        /* @__PURE__ */ jsx37("style", { children: "body{overflow:hidden}" }),
        /* @__PURE__ */ jsx37("div", { className: "relative flex h-[calc(100svh-50px)] items-center justify-center overflow-hidden rounded-md py-20", children: appLoadingComponent || /* @__PURE__ */ jsx37(AnimatedWbkLogo, { className: "h-24 w-24" }) })
      ] });
    }
    if (!user || isNewUser) {
      return /* @__PURE__ */ jsx37(
        "section",
        {
          "data-webview": !!isWebView,
          className: "after:bg-body/80 relative flex min-h-[95vh] items-center after:absolute after:inset-0 data-[webview=false]:before:absolute data-[webview=false]:before:inset-0 data-[webview=false]:before:z-[-1] data-[webview=false]:before:bg-[url(/images/default-cover.jpg)] data-[webview=false]:before:bg-cover data-[webview=false]:before:bg-no-repeat",
          children: /* @__PURE__ */ jsxs34("div", { className: "container relative z-10 mx-auto grid grid-cols-1 py-10 lg:grid-cols-2", children: [
            !isWebView && /* @__PURE__ */ jsx37("div", { className: "flex items-center pb-8", children: /* @__PURE__ */ jsx37("figure", { className: "mx-auto w-full", children: /* @__PURE__ */ jsx37(
              "img",
              {
                className: "mx-auto h-full max-h-[100px] w-full max-w-[160px] lg:max-w-xs",
                src: "/icons/logo.svg",
                alt: ""
              }
            ) }) }),
            /* @__PURE__ */ jsx37("div", { className: "mx-auto w-full max-w-lg", children: /* @__PURE__ */ jsx37(AuthForm, { title: config == null ? void 0 : config.title, className: "m-0" }) })
          ] })
        }
      );
    }
    return /* @__PURE__ */ jsx37(Component, __spreadProps(__spreadValues({}, props), { user }));
  };
  return WithAuthComponent;
};

// src/context/provider.tsx
import { jsx as jsx38, jsxs as jsxs35 } from "react/jsx-runtime";
var tokenRefreshTimeoutId;
var AuthProvider = (_a) => {
  var _b = _a, {
    children,
    refreshToken = true,
    autoInit = true
  } = _b, props = __objRest(_b, [
    "children",
    "refreshToken",
    "autoInit"
  ]);
  var _a2, _b2, _c;
  const [state, setState] = useState6(__spreadProps(__spreadValues({}, props), {
    refreshToken,
    canFetchUser: !refreshToken,
    autoInit
  }));
  const { loginEvent } = useAnalytics17();
  const { locale, search } = useRouter27();
  const { isActive } = useQueue();
  const { active } = useSubscriptionConfig();
  const {
    data: user,
    isPending,
    isFetching,
    error,
    errorUpdateCount
  } = useGetUser2({ lang: locale }, { enabled: state.autoInit && state.canFetchUser });
  const { mutateAsync: refreshTokens, isPending: refreshPending } = useRefreshTokens();
  const { data: subscriptions, isFetched: isFetchedSubscriptions } = useGetUserSubscriptions(
    { lang: locale },
    { enabled: !!user && !isActive && !!active }
  );
  const fetchUser = () => {
    if (state.autoInit) return;
    setState((prev) => __spreadProps(__spreadValues({}, prev), { autoInit: true }));
  };
  const setTokens = ({
    accessToken,
    refreshToken: refreshToken2,
    tokenExpiresIn
  }) => {
    var _a3, _b3, _c2, _d, _e, _f, _g, _h, _i, _j, _k;
    const tokenExpiryTime = tokenExpiresIn ? Date.now() + tokenExpiresIn * 1e3 : void 0;
    const expiresIn = tokenExpiresIn ? tokenExpiresIn / (60 * 60 * 24) : (_b3 = (_a3 = ApiConfig.config) == null ? void 0 : _a3.cookie) == null ? void 0 : _b3.tokenExpiration;
    if (accessToken) {
      createCookie({
        name: COOKIE_TOKEN,
        value: accessToken,
        expiration: expiresIn,
        domain: (_e = (_d = (_c2 = ApiConfig) == null ? void 0 : _c2.config) == null ? void 0 : _d.cookie) == null ? void 0 : _e.domain
      });
    }
    const after6Months = 30 * 6;
    if (refreshToken2) {
      createCookie({
        name: COOKIE_REFRESH_TOKEN,
        value: refreshToken2,
        expiration: after6Months,
        domain: (_h = (_g = (_f = ApiConfig) == null ? void 0 : _f.config) == null ? void 0 : _g.cookie) == null ? void 0 : _h.domain
      });
    }
    if (tokenExpiryTime) {
      createCookie({
        name: COOKIE_TOKEN_EXPIRES_IN,
        value: tokenExpiryTime.toString(),
        expiration: after6Months,
        domain: (_k = (_j = (_i = ApiConfig) == null ? void 0 : _i.config) == null ? void 0 : _j.cookie) == null ? void 0 : _k.domain
      });
    }
    if (refreshToken2 && tokenExpiresIn) {
      startRefreshTokenTimer(refreshToken2, tokenExpiresIn);
    }
    setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
  };
  const updateTokens = (refreshToken2) => __async(null, null, function* () {
    try {
      const { data } = yield refreshTokens(refreshToken2);
      setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenExpiresIn: data.token_expires_in
      });
    } catch (e) {
      clearTokens();
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
    }
  });
  const startRefreshTokenTimer = (refreshToken2, tokenExpiresIn) => {
    if (!state.refreshToken) return;
    if (tokenRefreshTimeoutId) {
      clearTimeout(tokenRefreshTimeoutId);
    }
    const timeRemaining = tokenExpiresIn * 1e3;
    tokenRefreshTimeoutId = setTimeout(() => {
      updateTokens(refreshToken2);
    }, timeRemaining);
  };
  const clearTokens = () => {
    var _a3, _b3, _c2, _d, _e, _f, _g, _h, _i;
    if (tokenRefreshTimeoutId) {
      clearTimeout(tokenRefreshTimeoutId);
    }
    eraseCookie(COOKIE_TOKEN, (_c2 = (_b3 = (_a3 = ApiConfig) == null ? void 0 : _a3.config) == null ? void 0 : _b3.cookie) == null ? void 0 : _c2.domain);
    eraseCookie(COOKIE_REFRESH_TOKEN, (_f = (_e = (_d = ApiConfig) == null ? void 0 : _d.config) == null ? void 0 : _e.cookie) == null ? void 0 : _f.domain);
    eraseCookie(COOKIE_TOKEN_EXPIRES_IN, (_i = (_h = (_g = ApiConfig) == null ? void 0 : _g.config) == null ? void 0 : _h.cookie) == null ? void 0 : _i.domain);
  };
  useEffect8(() => {
    const storageRefreshToken = readCookie(COOKIE_REFRESH_TOKEN);
    const storageTokenExpiresIn = readCookie(COOKIE_TOKEN_EXPIRES_IN);
    if (!storageRefreshToken) {
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
      return;
    }
    const expiryTime = parseInt(storageTokenExpiresIn || "0", 10);
    const timeRemaining = expiryTime - Date.now();
    const hasToken = !!readCookie(COOKIE_TOKEN);
    if (hasToken && timeRemaining > 0) {
      startRefreshTokenTimer(storageRefreshToken, timeRemaining / 1e3);
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: true }));
    } else {
      updateTokens(storageRefreshToken);
    }
  }, []);
  useEffect8(() => {
    const code = error == null ? void 0 : error.statusCode;
    const isUnauth = code === 401;
    if (isUnauth && errorUpdateCount === 1) {
      setState((prev) => __spreadProps(__spreadValues({}, prev), { canFetchUser: false }));
      const storageRefreshToken = readCookie(COOKIE_REFRESH_TOKEN);
      if (storageRefreshToken) {
        updateTokens(storageRefreshToken);
      }
    }
  }, [error, errorUpdateCount]);
  const isNewUser = useMemo7(() => {
    return !!(user == null ? void 0 : user._id) && !!(user == null ? void 0 : user.force_verify_email);
  }, [user == null ? void 0 : user._id, user == null ? void 0 : user.force_verify_email]);
  const isTawakkalnaUser = useMemo7(() => {
    return readCookie("twk") === "true" || search.get("app_source") === "twk";
  }, [search]);
  useEffect8(() => {
    if (user && isFetchedSubscriptions) {
      loginEvent({
        method: "profile",
        user: __spreadProps(__spreadValues({}, user), { locale }),
        subscriptions
      });
    }
  }, [user, isFetchedSubscriptions]);
  return /* @__PURE__ */ jsxs35(
    context_default.Provider,
    {
      value: __spreadProps(__spreadValues({}, state), {
        sso: props.sso,
        newsLetter: {
          enable: (_b2 = (_a2 = props.newsLetter) == null ? void 0 : _a2.enable) != null ? _b2 : true,
          organizationName: ((_c = props.newsLetter) == null ? void 0 : _c.organizationName) || "webook.com"
        },
        user,
        isNewUser,
        isTawakkalnaUser,
        loading: isPending || refreshPending,
        isFetching,
        fetchUser,
        setTokens,
        clearTokens
      }),
      children: [
        /* @__PURE__ */ jsx38(ForceUpdateProfileCheckPoint, {}),
        children
      ]
    }
  );
};

// src/components/checkpoint/global/Entry.tsx
import { useTranslation as useTranslation33 } from "@wbk/config";
import { StrokeCheckIcon } from "@wbk/svg";
import { Typography as Typography18 } from "@wbk/ui";
import { jsx as jsx39, jsxs as jsxs36 } from "react/jsx-runtime";
var Entry = () => {
  const { t } = useTranslation33("auth_common");
  return /* @__PURE__ */ jsxs36("div", { children: [
    /* @__PURE__ */ jsx39("img", { src: "/@wbk/auth/account-details.png", alt: "account-details", width: 480, height: 215 }),
    /* @__PURE__ */ jsx39(Typography18, { variant: "heading-L", className: "mb-4", children: t("auth_common:force_complete_profile.title") }),
    /* @__PURE__ */ jsxs36("ul", { className: "mb-4 space-y-6", children: [
      /* @__PURE__ */ jsxs36("li", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx39(StrokeCheckIcon, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsxs36("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx39(Typography18, { variant: "label-M", children: t("auth_common:force_complete_profile.tailored_deals") }),
          /* @__PURE__ */ jsx39(Typography18, { variant: "body-S", color: "text-secondary", children: t("auth_common:force_complete_profile.tailored_deals_desc") })
        ] })
      ] }),
      /* @__PURE__ */ jsxs36("li", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx39(StrokeCheckIcon, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsxs36("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx39(Typography18, { variant: "label-M", children: t("auth_common:force_complete_profile.enhance_security") }),
          /* @__PURE__ */ jsx39(Typography18, { variant: "body-S", color: "text-secondary", children: t("auth_common:force_complete_profile.enhance_security_desc") })
        ] })
      ] }),
      /* @__PURE__ */ jsxs36("li", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx39(StrokeCheckIcon, { className: "h-6 w-6 shrink-0" }),
        /* @__PURE__ */ jsxs36("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx39(Typography18, { variant: "label-M", children: t("auth_common:force_complete_profile.keep_access_to_webook") }),
          /* @__PURE__ */ jsx39(Typography18, { variant: "body-S", color: "text-secondary", children: t("auth_common:force_complete_profile.keep_access_to_webook_desc") })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx39(Typography18, { children: t("auth_common:force_complete_profile.only_takes_a_minute") })
  ] });
};
var Entry_default = Entry;

// src/components/checkpoint/global/Form.tsx
import { useEffect as useEffect9 } from "react";
import { useFormContext as useFormContext20 } from "react-hook-form";
import { useUpdateProfile as useUpdateProfile3 } from "@wbk/api/hooks";
import { useGeolocation as useGeolocation7, useRouter as useRouter28, useTranslation as useTranslation34 } from "@wbk/config";
import { Typography as Typography19 } from "@wbk/ui";
import { WbkLogo as WbkLogo3 } from "@wbk/svg";
import { COUNTRIES as COUNTRIES4 } from "@wbk/utils";
import { Fragment as Fragment6, jsx as jsx40, jsxs as jsxs37 } from "react/jsx-runtime";
var UpdateProfileForm = ({ onClose }) => {
  const { t } = useTranslation34("auth_common");
  const { handleSubmit, setValue, reset } = useFormContext20();
  const { locale } = useRouter28();
  const { mutateAsync: updateProfile, error } = useUpdateProfile3({ lang: locale });
  const { country: detectedCountry } = useGeolocation7();
  const { user } = useAuth();
  useEffect9(() => {
    const dialCountry = COUNTRIES4.find((c) => c.code === (user == null ? void 0 : user.cell_country_code)) || detectedCountry;
    reset(__spreadProps(__spreadValues({}, getDefaultUser(user)), {
      dialCountry,
      // Global check point should force user to re-enter the below
      nationality: "",
      country: "",
      city: "",
      phone: ""
    }));
  }, [detectedCountry == null ? void 0 : detectedCountry.code, user == null ? void 0 : user.cell_country_code, user == null ? void 0 : user.cell_number]);
  const onSubmit = (_a) => __async(null, null, function* () {
    var _b = _a, { dialCountry, phone, loading: _loading } = _b, data = __objRest(_b, ["dialCountry", "phone", "loading"]);
    var _a2;
    try {
      setValue("loading", true);
      const body = __spreadProps(__spreadValues({}, data), {
        cell_country_code: dialCountry == null ? void 0 : dialCountry.code,
        cell_number: phone,
        dob: (_a2 = data.dob) == null ? void 0 : _a2.split("-").reverse().join("/"),
        passport_data: void 0
      });
      yield updateProfile({ user: body, locale });
      onClose == null ? void 0 : onClose();
    } finally {
      setValue("loading", false);
    }
  });
  return /* @__PURE__ */ jsxs37(Fragment6, { children: [
    /* @__PURE__ */ jsx40(WbkLogo3, { className: "text-primary w-16" }),
    /* @__PURE__ */ jsx40(Typography19, { as: "h2", variant: "heading-XXL", className: "mb-6", children: t("auth_common:force_complete_profile.update_information") }),
    /* @__PURE__ */ jsx40("form", { id: "profile-form", onSubmit: handleSubmit(onSubmit), className: "space-y-4", children: /* @__PURE__ */ jsx40(FormItems_default, { error }) })
  ] });
};

// src/components/checkpoint/global/index.tsx
import { jsx as jsx41, jsxs as jsxs38 } from "react/jsx-runtime";
var ForceUpdateProfileCheckPoint = () => {
  const [showBar, setShowBar] = useState7(false);
  const modal = useRef4(void 0);
  const { t } = useTranslation35("auth_common");
  const { locale } = useRouter29();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics18();
  const methods = useForm4({
    defaultValues: { mode: "entry" }
  });
  const mode = methods.watch("mode");
  useEffect10(() => {
    var _a;
    if (user == null ? void 0 : user.force_update) {
      (_a = modal.current) == null ? void 0 : _a.open();
    }
  }, [user == null ? void 0 : user.force_update]);
  if (!user) {
    return null;
  }
  return /* @__PURE__ */ jsx41(FormProvider3, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx41(
    ResponsiveModal3,
    {
      ref: modal,
      size: "lg",
      onClose: () => {
        setShowBar(true);
        methods.setValue("mode", "entry");
        trackEvent({
          event: "force_update_screen_exit",
          data: { user_id: user == null ? void 0 : user._id }
        });
      },
      cancel: {
        label: mode === "entry" ? t("auth_common:force_complete_profile.skip_for_now") : t("auth_common:force_complete_profile.cancel"),
        onClick: ({ close }) => close()
      },
      action: {
        label: mode === "entry" ? t("auth_common:force_complete_profile.update_information") : t("auth_common:force_complete_profile.save"),
        loading: methods.watch("loading"),
        form: "profile-form",
        onClick: () => {
          if (mode === "entry") {
            methods.setValue("mode", "form");
          }
          trackEvent({
            event: "force_update_screen_save_info",
            data: { user_id: user == null ? void 0 : user._id }
          });
        }
      },
      render: ({ open }) => /* @__PURE__ */ jsx41(
        "div",
        {
          className: "h-0 overflow-hidden bg-[#7B49FF] transition-all data-[open=true]:h-16",
          "data-open": showBar && !!(user == null ? void 0 : user.force_update),
          children: /* @__PURE__ */ jsxs38("div", { className: "container flex h-16 items-center justify-between gap-3 py-4 text-white", children: [
            /* @__PURE__ */ jsxs38("div", { className: "flex items-center gap-4", children: [
              /* @__PURE__ */ jsx41(ExclamationTriangleIcon, { className: "h-6 w-6" }),
              /* @__PURE__ */ jsx41(Typography20, { className: "text-sm font-medium", variant: "body-M", children: t("auth_common:force_complete_profile.update_profile_to_keep_using_webook") })
            ] }),
            /* @__PURE__ */ jsxs38(
              Button25,
              {
                theme: "action",
                onClick: () => {
                  open();
                  setShowBar(false);
                  trackEvent({
                    event: "force_update_banner_spotlight_clicked",
                    data: {
                      user_id: user == null ? void 0 : user._id
                    }
                  });
                },
                className: "h-9 shrink-0",
                children: [
                  /* @__PURE__ */ jsx41("span", { className: "hidden md:block", children: t("auth_common:force_complete_profile.update_information") }),
                  /* @__PURE__ */ jsx41("span", { className: "block md:hidden", children: t("auth_common:force_complete_profile.update") })
                ]
              }
            )
          ] })
        }
      ),
      body: ({ close }) => /* @__PURE__ */ jsx41(AnimatePresence9, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx41(
        motion9.div,
        {
          initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
          transition: { duration: 0.2 },
          children: mode === "entry" ? /* @__PURE__ */ jsx41(Entry_default, {}) : /* @__PURE__ */ jsx41(
            UpdateProfileForm,
            {
              onClose: () => {
                close();
                setShowBar(false);
              }
            }
          )
        },
        `${mode}-${locale}`
      ) })
    }
  ) }));
};

// src/components/checkpoint/nafath/index.tsx
import { useEffect as useEffect11, useRef as useRef5, useState as useState8 } from "react";
import { Controller as Controller13, useForm as useForm5 } from "react-hook-form";
import { twMerge as twMerge8 } from "tailwind-merge";
import { Button as Button26, Input as Input11 } from "@wbk/ui";
import { useCheckNafath, useRequestNafath } from "@wbk/api/hooks";
import { useRouter as useRouter30, useTranslation as useTranslation36 } from "@wbk/config";
import { jsx as jsx42, jsxs as jsxs39 } from "react/jsx-runtime";
var NafathCheckPoint = ({ className }) => {
  var _d;
  const { locale } = useRouter30();
  const { t } = useTranslation36(["auth_common"]);
  const [mode, setMode] = useState8("iqama");
  const retryRef = useRef5(void 0);
  const { data: nafathData, mutateAsync, isPending, error: reqError } = useRequestNafath();
  const { mutateAsync: checkNafath, error: checkError, reset } = useCheckNafath({ lang: locale });
  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm5({
    defaultValues: {
      user_id: ""
    }
  });
  const onSubmit = (data) => __async(null, null, function* () {
    try {
      reset();
      if (retryRef.current) {
        clearInterval(retryRef.current);
      }
      const res = yield mutateAsync({ id: data.user_id, locale });
      setMode("code");
      retryRef.current = setInterval(() => __async(null, null, function* () {
        try {
          yield checkNafath({
            id: data.user_id,
            transId: res.data.transId,
            code: res.data.code,
            locale
          });
        } catch (error) {
          clearInterval(retryRef.current);
        }
      }), 5e3);
    } catch (err) {
    }
  });
  useEffect11(() => {
    return () => {
      if (retryRef.current) {
        clearInterval(retryRef.current);
      }
    };
  }, []);
  return /* @__PURE__ */ jsx42("div", { className: twMerge8("p-4 py-12", className), children: /* @__PURE__ */ jsx42("div", { className: "bg-body-level-1 mx-auto max-w-lg rounded-md", children: mode === "iqama" ? /* @__PURE__ */ jsxs39(
    "form",
    {
      id: "checkout-complete-nafath",
      onSubmit: handleSubmit(onSubmit),
      className: "space-y-4 p-4",
      children: [
        /* @__PURE__ */ jsxs39("div", { children: [
          /* @__PURE__ */ jsx42("h2", { className: "pb-8 text-2xl font-semibold md:text-3xl", children: t("auth_common:nafath_title") }),
          /* @__PURE__ */ jsx42(
            "img",
            {
              width: 37,
              height: 16,
              className: "object-contain",
              src: "/@wbk/auth/nafath-title.png",
              alt: "nafath title"
            }
          ),
          /* @__PURE__ */ jsxs39("div", { className: "mt-4 flex items-start gap-5", children: [
            /* @__PURE__ */ jsx42("p", { className: "text-text-secondary flex-1 text-base md:text-lg", children: t("auth_common:nafath_decs") }),
            /* @__PURE__ */ jsx42(
              "img",
              {
                className: "object-contain",
                width: 94,
                height: 102,
                src: "/@wbk/auth/nafath-logo.png",
                alt: "nafath logo"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx42("fieldset", { children: /* @__PURE__ */ jsx42(
          Controller13,
          {
            control,
            name: "user_id",
            rules: {
              required: t("auth_common:validation.required"),
              minLength: {
                value: 10,
                message: t("auth_common:validation.min_length", { min: 10 })
              },
              maxLength: {
                value: 10,
                message: t("auth_common:validation.max_length", { max: 10 })
              }
            },
            render: (_a) => {
              var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
              var _a2;
              return /* @__PURE__ */ jsx42(
                Input11,
                __spreadValues({
                  id: "user_id",
                  inputMode: "numeric",
                  label: t("auth_common:nafath_id"),
                  onChange: (value) => {
                    const valid = /^[0-9]*$/.test(value);
                    if (valid && value.length <= 10) {
                      onChange(value);
                    }
                  },
                  error: (_a2 = errors.user_id) == null ? void 0 : _a2.message
                }, props)
              );
            }
          }
        ) }),
        (reqError == null ? void 0 : reqError.message) && /* @__PURE__ */ jsx42("p", { className: "text-error", children: reqError.message }),
        /* @__PURE__ */ jsx42(
          Button26,
          {
            className: "w-full text-[#0D9488]",
            theme: "white",
            type: "submit",
            form: "checkout-complete-nafath",
            loading: isPending,
            children: /* @__PURE__ */ jsx42("span", { className: "text-lg font-bold", children: t("auth_common:connect_nafath") })
          }
        )
      ]
    }
  ) : /* @__PURE__ */ jsxs39("div", { className: "space-y-4 p-4", children: [
    /* @__PURE__ */ jsx42(
      "img",
      {
        width: 37,
        height: 16,
        className: "object-contain",
        src: "/@wbk/auth/nafath-title.png",
        alt: "nafath title"
      }
    ),
    /* @__PURE__ */ jsx42("p", { className: "text-base md:text-lg", children: t("auth_common:nafath_code") }),
    /* @__PURE__ */ jsx42("h2", { className: "bg-body-level-2 mx-auto flex h-16 w-16 items-center justify-center rounded-full p-2", children: ((_d = nafathData == null ? void 0 : nafathData.data) == null ? void 0 : _d.code) || "00" }),
    (checkError == null ? void 0 : checkError.message) && /* @__PURE__ */ jsxs39("div", { children: [
      /* @__PURE__ */ jsx42("p", { className: "text-error", children: checkError == null ? void 0 : checkError.message }),
      /* @__PURE__ */ jsx42(
        Button26,
        {
          theme: "white",
          shape: "outlined",
          className: "mt-4 w-full",
          onClick: () => setMode("iqama"),
          children: /* @__PURE__ */ jsx42("span", { className: "text-lg font-bold", children: t("auth_common:change_id") })
        }
      )
    ] })
  ] }) }) });
};

// src/components/checkpoint/otp/index.tsx
import { useRef as useRef6 } from "react";
import { FormProvider as FormProvider4, useForm as useForm6 } from "react-hook-form";
import { twMerge as twMerge9 } from "tailwind-merge";
import { BottomSheet as BottomSheet2, Modal as Modal2, Toast as Toast3 } from "@wbk/ui";
import { useDeviceDetect as useDeviceDetect3 } from "@wbk/hooks";
import { AnimatePresence as AnimatePresence10, motion as motion10 } from "motion/react";
import { useRouter as useRouter34, useTranslation as useTranslation41 } from "@wbk/config";
import { WithGrecaptcha as WithGrecaptcha5 } from "@wbk/api/grecaptcha";

// src/components/checkpoint/otp/VerifyEmail.tsx
import { useCallback as useCallback3, useEffect as useEffect12 } from "react";
import { Button as Button27, OtpInputs as OtpInputs4 } from "@wbk/ui";
import { useFormContext as useFormContext21 } from "react-hook-form";
import { useSendOtpToEmail, useVerifyEmail } from "@wbk/api/hooks";
import { ChevronIcon as ChevronIcon3 } from "@wbk/svg";
import { useAppSource as useAppSource6, useRouter as useRouter31, useTranslation as useTranslation37 } from "@wbk/config";
import { Fragment as Fragment7, jsx as jsx43, jsxs as jsxs40 } from "react/jsx-runtime";
var VerifyEmail = ({ show, profile, onSuccess }) => {
  const { user } = useAuth();
  const { appSource } = useAppSource6();
  const { locale } = useRouter31();
  const { t } = useTranslation37(["auth_common"]);
  const { setValue } = useFormContext21();
  const { mutateAsync: sendOtp, isPending: isSending, error: sendingError } = useSendOtpToEmail();
  const { mutateAsync: verifyOtp, isPending, error } = useVerifyEmail({ lang: locale });
  const handleSendOtp = useCallback3(() => __async(null, null, function* () {
    try {
      if (show) {
        yield sendOtp({ locale, app_source: appSource });
      }
    } catch (err) {
    }
  }), [appSource, locale, show]);
  const handleVerify = (otp) => __async(null, null, function* () {
    try {
      yield verifyOtp({ code: otp, locale });
      const verifiedUser = __spreadProps(__spreadValues({}, user), {
        email_verified: true,
        force_verify_email: false
      });
      if (onSuccess) {
        onSuccess(verifiedUser, otp);
      }
    } catch (err) {
    }
  });
  useEffect12(() => {
    handleSendOtp();
  }, [handleSendOtp]);
  return /* @__PURE__ */ jsxs40("div", { children: [
    /* @__PURE__ */ jsx43("h2", { className: "pb-1 text-2xl font-semibold", children: t("auth_common:verify_your_email_address") }),
    /* @__PURE__ */ jsx43("p", { className: "text-text text-sm", children: t("auth_common:verify_your_email_address_desc", { email: (user == null ? void 0 : user.email) || "" }) }),
    /* @__PURE__ */ jsx43("div", { className: "pt-10", children: /* @__PURE__ */ jsx43(
      OtpInputs4,
      {
        formId: "checkout-vefity-otp",
        onChange: (otp) => {
          setValue("otp", otp);
        },
        onSubmit: (otp) => __async(null, null, function* () {
          setValue("otp", otp);
          handleVerify(otp);
        }),
        onlyNumbers: true,
        disabled: isPending,
        error: (error == null ? void 0 : error.message) || (sendingError == null ? void 0 : sendingError.message),
        renderButton: ({ resetState }) => /* @__PURE__ */ jsx43("div", { className: "mt-4 text-center text-sm", children: /* @__PURE__ */ jsx43(
          WithCountDown_default,
          {
            seconds: 60,
            render: ({ count, reset }) => /* @__PURE__ */ jsx43(
              Button27,
              {
                type: "button",
                shape: "text",
                theme: "action",
                className: "shrink-0 !bg-transparent px-2 py-1 text-sm underline",
                disabled: isSending || count > 0,
                loading: isPending,
                onClick: () => __async(null, null, function* () {
                  try {
                    yield handleSendOtp();
                    reset();
                    resetState();
                  } catch (error2) {
                  }
                }),
                id: "resend-otp",
                children: `${t("auth_common:resend_otp")} ${count > 0 ? `(${count})` : ""}`
              }
            )
          }
        ) })
      }
    ) }),
    /* @__PURE__ */ jsx43("hr", { className: "border-paper -mx-4 my-4" }),
    /* @__PURE__ */ jsxs40("div", { className: "flex items-center justify-between gap-4", children: [
      /* @__PURE__ */ jsx43("div", { className: "text-text text-sm", children: !(user == null ? void 0 : user.email_verified) && /* @__PURE__ */ jsx43(Fragment7, { children: profile ? /* @__PURE__ */ jsxs40(
        Button27,
        {
          className: "gap-2 px-2",
          theme: "action",
          shape: "text",
          type: "button",
          "data-testid": "auth_otp_change_email_button",
          onClick: () => {
            setValue("mode", "email-update");
          },
          children: [
            /* @__PURE__ */ jsx43(ChevronIcon3, { className: "ltr:rotate-180" }),
            t("auth_common:change_email")
          ]
        }
      ) : /* @__PURE__ */ jsxs40(Fragment7, { children: [
        /* @__PURE__ */ jsx43("p", { children: t("auth_common:wrong_email") }),
        /* @__PURE__ */ jsx43(
          Button27,
          {
            className: "underline-offset-3 px-0 py-px underline !ring-0 hover:underline focus:underline",
            theme: "action",
            shape: "text",
            type: "button",
            "data-testid": "auth_otp_change_email_button",
            onClick: () => {
              setValue("mode", "email-update");
            },
            children: t("auth_common:change_email_address")
          }
        )
      ] }) }) }),
      /* @__PURE__ */ jsx43(
        Button27,
        {
          type: "submit",
          form: "checkout-vefity-otp",
          theme: "action",
          loading: isPending,
          className: "text-lg font-bold",
          children: t("auth_common:verify")
        }
      )
    ] })
  ] });
};
var VerifyEmail_default = VerifyEmail;

// src/components/checkpoint/otp/ChangeEmail.tsx
import { Controller as Controller14, useFormContext as useFormContext22 } from "react-hook-form";
import { Button as Button28, Input as Input12 } from "@wbk/ui";
import { useUpdateEmail } from "@wbk/api/hooks";
import { EMAIL_PATTERN as EMAIL_PATTERN5 } from "@wbk/utils";
import { useAppSource as useAppSource7, useRouter as useRouter32, useTranslation as useTranslation38 } from "@wbk/config";
import { jsx as jsx44, jsxs as jsxs41 } from "react/jsx-runtime";
var ChangeEmail = ({ onClose }) => {
  var _d, _e, _f;
  const { appSource } = useAppSource7();
  const { newsLetter } = useAuth();
  const { locale } = useRouter32();
  const { t } = useTranslation38(["auth_common"]);
  const {
    setValue,
    watch,
    control,
    formState: { errors },
    handleSubmit
  } = useFormContext22();
  const { mutateAsync, isPending, error } = useUpdateEmail({ lang: locale });
  const email = watch("email");
  const emailSuggestion = useEmailSpelling({ email });
  const onSubmit = (data) => __async(null, null, function* () {
    try {
      yield mutateAsync({ email: data.email, locale, app_source: appSource });
      setValue("mode", "otp");
    } catch (error2) {
    }
  });
  return /* @__PURE__ */ jsxs41("form", { onSubmit: handleSubmit(onSubmit), children: [
    /* @__PURE__ */ jsx44("h4", { className: "text-2xl font-semibold", children: t("auth_common:update_email") }),
    /* @__PURE__ */ jsx44("p", { className: "text-text-secondary pt-0.5 text-sm", children: t("auth_common:update_email_desc", { website: newsLetter.organizationName }) }),
    /* @__PURE__ */ jsxs41("fieldset", { className: "my-4 space-y-4 pb-4", children: [
      /* @__PURE__ */ jsxs41("div", { className: "relative", children: [
        /* @__PURE__ */ jsx44(
          Controller14,
          {
            control,
            name: "email",
            defaultValue: "",
            rules: {
              required: t("auth_common:validation.required"),
              pattern: {
                value: EMAIL_PATTERN5,
                message: t("auth_common:validation.invalid_email")
              }
            },
            render: (_a) => {
              var { field: _b } = _a, _c = _b, { onChange } = _c, props = __objRest(_c, ["onChange"]);
              var _a2, _b2, _c2;
              return /* @__PURE__ */ jsx44(
                Input12,
                __spreadProps(__spreadValues({
                  id: "new_email",
                  "data-testid": "auth_renew_email_input",
                  autoComplete: "off",
                  label: t("auth_common:new_email")
                }, props), {
                  onChange: (value) => {
                    onChange(value);
                  },
                  error: ((_a2 = errors.email) == null ? void 0 : _a2.message) || ((_c2 = (_b2 = error == null ? void 0 : error.errors) == null ? void 0 : _b2.email) == null ? void 0 : _c2[0]) || (error == null ? void 0 : error.message)
                })
              );
            }
          }
        ),
        emailSuggestion && /* @__PURE__ */ jsxs41(
          "div",
          {
            className: `text-text-tertiary absolute w-full rounded-lg bg-yellow-100 p-2 text-sm ${((_d = errors.email) == null ? void 0 : _d.message) || ((_f = (_e = error == null ? void 0 : error.errors) == null ? void 0 : _e.email) == null ? void 0 : _f[0]) ? "-bottom-4" : "-bottom-9"}`,
            children: [
              t("auth_common:validation.did_you_mean"),
              /* @__PURE__ */ jsx44(
                "button",
                {
                  onClick: () => setValue("email", emailSuggestion.full, {
                    shouldValidate: true
                  }),
                  className: "text-primary mx-1 cursor-pointer font-semibold hover:underline",
                  children: emailSuggestion.full
                }
              )
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx44(
        Controller14,
        {
          control,
          name: "confirm_email",
          rules: {
            required: t("auth_common:validation.required"),
            validate: (value) => value === email || t("auth_common:validation.emails_not_match")
          },
          defaultValue: "",
          render: (_g) => {
            var { field: _h } = _g, _i = _h, { onChange } = _i, props = __objRest(_i, ["onChange"]);
            var _a;
            return /* @__PURE__ */ jsx44(
              Input12,
              __spreadProps(__spreadValues({}, props), {
                id: "confirm_email",
                "data-testid": "auth_confirm_email_input",
                autoComplete: "off",
                label: t("auth_common:confirm_email"),
                onChange: (value) => {
                  onChange(value);
                },
                onPaste: (e) => {
                  e.preventDefault();
                },
                error: (_a = errors.confirm_email) == null ? void 0 : _a.message
              })
            );
          }
        }
      )
    ] }),
    /* @__PURE__ */ jsx44("hr", { className: "-mx-4 my-4 border-white/20" }),
    /* @__PURE__ */ jsxs41("div", { className: "flex items-center justify-end gap-4", children: [
      /* @__PURE__ */ jsx44(
        Button28,
        {
          theme: "action",
          shape: "text",
          type: "button",
          "data-testid": "auth_update_email_cancel_button",
          onClick: onClose,
          children: t("auth_common:cancel")
        }
      ),
      /* @__PURE__ */ jsx44(
        Button28,
        {
          type: "submit",
          theme: "action",
          loading: isPending,
          className: "text-lg font-bold",
          "data-testid": "auth_update_email_confirm_button",
          children: t("auth_common:update_email")
        }
      )
    ] })
  ] });
};
var ChangeEmail_default = ChangeEmail;

// src/components/checkpoint/otp/Entry.tsx
import { Button as Button29, Typography as Typography21 } from "@wbk/ui";
import { useFormContext as useFormContext23 } from "react-hook-form";
import { useTranslation as useTranslation39, useWalletConfig } from "@wbk/config";
import { jsx as jsx45, jsxs as jsxs42 } from "react/jsx-runtime";
var Entry2 = ({ title, description, type = "verify_email", onClose }) => {
  const { user } = useAuth({ autoFetch: true });
  const { organizationName } = useWalletConfig();
  const { setValue } = useFormContext23();
  const { t } = useTranslation39(["auth_common", "payment_common"]);
  const defaultTitle = type === "verify_wallet" ? t("payment_common:verify_wallet_credit", {
    organizationName: organizationName || "webook.com"
  }) : t("auth_common:verify_your_email_address");
  const defaultDescription = type === "verify_wallet" ? t("payment_common:verify_wallet_credit_desc", {
    organizationName: organizationName || "webook.com",
    email: (user == null ? void 0 : user.email) || ""
  }) : t("auth_common:email_verification_description");
  return /* @__PURE__ */ jsxs42("div", { children: [
    /* @__PURE__ */ jsxs42("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsx45(Typography21, { variant: "heading-XL", children: title || defaultTitle }),
      /* @__PURE__ */ jsx45(Typography21, { className: "text-text-secondary", children: description || defaultDescription }),
      (user == null ? void 0 : user.email) && /* @__PURE__ */ jsx45(Typography21, { variant: "body-L", children: user == null ? void 0 : user.email }),
      /* @__PURE__ */ jsx45(
        Button29,
        {
          shape: "text",
          theme: "action",
          className: "p-0 underline",
          onClick: () => setValue("mode", "email-update"),
          children: t("auth_common:change_email")
        }
      )
    ] }),
    /* @__PURE__ */ jsx45("hr", { className: "-mx-4 my-4 border-white/20" }),
    /* @__PURE__ */ jsxs42("div", { className: "flex items-center justify-end gap-4", children: [
      onClose && /* @__PURE__ */ jsx45(
        Button29,
        {
          theme: "action",
          shape: "text",
          type: "button",
          "data-testid": "auth_update_email_cancel_button",
          onClick: onClose,
          children: t("auth_common:cancel")
        }
      ),
      /* @__PURE__ */ jsx45(
        Button29,
        {
          theme: "action",
          className: "text-lg font-bold",
          "data-testid": "auth_update_email_confirm_button",
          onClick: () => setValue("mode", "otp"),
          children: t("auth_common:send_otp")
        }
      )
    ] })
  ] });
};
var Entry_default2 = Entry2;

// src/components/checkpoint/otp/EnterOtp.tsx
import { useCallback as useCallback4, useEffect as useEffect13 } from "react";
import { Button as Button30, OtpInputs as OtpInputs5 } from "@wbk/ui";
import { useFormContext as useFormContext24 } from "react-hook-form";
import { useRouter as useRouter33, useTranslation as useTranslation40 } from "@wbk/config";
import { useSendOtp } from "@wbk/api/hooks";
import { jsx as jsx46, jsxs as jsxs43 } from "react/jsx-runtime";
var VerifyEmail2 = ({ show, onSuccess }) => {
  const { user } = useAuth();
  const { locale } = useRouter33();
  const { t } = useTranslation40(["auth_common"]);
  const { setValue } = useFormContext24();
  const { mutateAsync: sendOtp, isPending: isSending, error: sendingError } = useSendOtp();
  const handleSendOtp = useCallback4(() => __async(null, null, function* () {
    try {
      if (show) {
        yield sendOtp({ lang: locale, source: "wallet-debit" });
      }
    } catch (err) {
    }
  }), [locale, show]);
  const handleSubmit = (otp) => __async(null, null, function* () {
    onSuccess == null ? void 0 : onSuccess(user, otp);
  });
  useEffect13(() => {
    handleSendOtp();
  }, [handleSendOtp]);
  return /* @__PURE__ */ jsxs43("div", { children: [
    /* @__PURE__ */ jsx46("h2", { className: "pb-1 text-2xl font-semibold", children: t("auth_common:verify_your_email_address") }),
    /* @__PURE__ */ jsx46("p", { className: "text-text text-sm", children: t("auth_common:verify_your_email_address_desc", { email: (user == null ? void 0 : user.email) || "" }) }),
    /* @__PURE__ */ jsx46("div", { className: "pt-10", children: /* @__PURE__ */ jsx46(
      OtpInputs5,
      {
        formId: "checkout-vefity-otp",
        onChange: (otp) => {
          setValue("otp", otp);
        },
        onSubmit: (otp) => __async(null, null, function* () {
          setValue("otp", otp);
          handleSubmit(otp);
        }),
        onlyNumbers: true,
        error: sendingError == null ? void 0 : sendingError.message,
        renderButton: ({ resetState }) => /* @__PURE__ */ jsx46("div", { className: "mt-4 text-center text-sm", children: /* @__PURE__ */ jsx46(
          WithCountDown_default,
          {
            seconds: 60,
            render: ({ count, reset }) => /* @__PURE__ */ jsx46(
              Button30,
              {
                type: "button",
                shape: "text",
                theme: "action",
                className: "shrink-0 !bg-transparent px-2 py-1 text-sm underline",
                disabled: isSending || count > 0,
                onClick: () => __async(null, null, function* () {
                  try {
                    yield handleSendOtp();
                    reset();
                    resetState();
                  } catch (error) {
                  }
                }),
                id: "resend-otp",
                children: `${t("auth_common:resend_otp")} ${count > 0 ? `(${count})` : ""}`
              }
            )
          }
        ) })
      }
    ) }),
    /* @__PURE__ */ jsx46("hr", { className: "-mx-4 my-4 border-white/20" }),
    /* @__PURE__ */ jsx46("div", { className: "flex items-center justify-end gap-4", children: /* @__PURE__ */ jsx46(
      Button30,
      {
        type: "submit",
        form: "checkout-vefity-otp",
        theme: "action",
        className: "text-lg font-bold",
        children: t("auth_common:verify")
      }
    ) })
  ] });
};
var EnterOtp_default = VerifyEmail2;

// src/components/checkpoint/otp/index.tsx
import { jsx as jsx47, jsxs as jsxs44 } from "react/jsx-runtime";
var OtpCheckPoint = ({
  show,
  render,
  onSuccess,
  onClose,
  className,
  initialMode = "entry",
  profile,
  title,
  description,
  type = "verify_email"
}) => {
  const { isMobile } = useDeviceDetect3();
  const { locale } = useRouter34();
  const { t } = useTranslation41(["auth_common"]);
  const methods = useForm6({
    defaultValues: { mode: initialMode }
  });
  const mode = methods.watch("mode");
  const toast = useRef6(void 0);
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setTimeout(methods.reset, 200);
  };
  const handleSuccess = (result, otp) => {
    var _a;
    if (onSuccess) {
      onSuccess(result, otp);
    }
    if (type === "verify_email") {
      (_a = toast.current) == null ? void 0 : _a.publish({
        title: t("auth_common:email_verified_successfully"),
        icon: true
      });
    }
  };
  const Body = /* @__PURE__ */ jsx47(FormProvider4, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx47("div", { className: "overflow-hidden", children: /* @__PURE__ */ jsx47(AnimatePresence10, { initial: false, mode: "wait", children: /* @__PURE__ */ jsx47(
    motion10.div,
    {
      className: twMerge9("p-4", className),
      initial: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "100%" : "-100%", opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: (locale == null ? void 0 : locale.startsWith("ar")) ? "-100%" : "100%", opacity: 0 },
      transition: { duration: 0.2 },
      children: mode === "entry" ? /* @__PURE__ */ jsx47(Entry_default2, { type, title, description, onClose: handleClose }) : mode === "email-update" ? /* @__PURE__ */ jsx47(ChangeEmail_default, { onClose: handleClose }) : type === "verify_wallet" ? /* @__PURE__ */ jsx47(EnterOtp_default, { show, onSuccess: handleSuccess }) : /* @__PURE__ */ jsx47(VerifyEmail_default, { show, onSuccess: handleSuccess, profile })
    },
    `${mode}-${locale}`
  ) }) }) }));
  return /* @__PURE__ */ jsxs44(WithGrecaptcha5, { children: [
    isMobile ? /* @__PURE__ */ jsx47(
      BottomSheet2,
      {
        ignoreHistory: true,
        show,
        onClose: handleClose,
        className: "z-[999]",
        sectionClassName: "pb-12",
        render,
        body: () => Body
      }
    ) : /* @__PURE__ */ jsx47(
      Modal2,
      {
        closeButton: true,
        ignoreHistory: true,
        show,
        onClose: handleClose,
        render,
        body: () => Body,
        zIndex: 999
      }
    ),
    /* @__PURE__ */ jsx47(Toast3, { ref: toast })
  ] });
};

// src/components/checkpoint/profile/index.tsx
import { useEffect as useEffect14 } from "react";
import { Controller as Controller15, FormProvider as FormProvider5, useForm as useForm7 } from "react-hook-form";
import { twMerge as twMerge10 } from "tailwind-merge";
import { BottomSheet as BottomSheet3, Button as Button31, Input as Input13, Modal as Modal3, ToggleGroup as ToggleGroup2 } from "@wbk/ui";
import { useDeviceDetect as useDeviceDetect4 } from "@wbk/hooks";
import { useUpdateProfile as useUpdateProfile4 } from "@wbk/api/hooks";
import { AnimatePresence as AnimatePresence11, motion as motion11 } from "motion/react";
import { useGeolocation as useGeolocation8, useRouter as useRouter35, useTranslation as useTranslation42 } from "@wbk/config";
import { COUNTRIES as COUNTRIES5 } from "@wbk/utils";
import { Fragment as Fragment8, jsx as jsx48, jsxs as jsxs45 } from "react/jsx-runtime";
var ProfileCheckPoint = ({
  show,
  onSuccess,
  onClose,
  flatLayout,
  className,
  showVisaCheckbox
}) => {
  var _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  const { isMobile } = useDeviceDetect4();
  const { user } = useAuth();
  const { locale } = useRouter35();
  const { t } = useTranslation42(["auth_common"]);
  const { mutateAsync: updateProfile, isPending, error } = useUpdateProfile4({ lang: locale });
  const methods = useForm7();
  const { country: detectedCountry } = useGeolocation8();
  useEffect14(() => {
    var _a;
    const dialCountry = COUNTRIES5.find((c) => c.code === (user == null ? void 0 : user.cell_country_code)) || detectedCountry;
    const phone = (_a = user == null ? void 0 : user.cell_number) == null ? void 0 : _a.replace(`+${dialCountry == null ? void 0 : dialCountry.dial_code}`, "");
    methods.reset(__spreadProps(__spreadValues({}, getDefaultUser(user)), { dialCountry, phone }));
  }, [detectedCountry == null ? void 0 : detectedCountry.code, user == null ? void 0 : user.cell_country_code, user == null ? void 0 : user.cell_number]);
  const needVisa = methods.watch("require_visa");
  const onSubmit = (_a) => __async(null, null, function* () {
    var _b = _a, { dialCountry, phone } = _b, data = __objRest(_b, ["dialCountry", "phone"]);
    var _a2;
    try {
      const body = __spreadProps(__spreadValues({}, data), {
        cell_country_code: dialCountry == null ? void 0 : dialCountry.code,
        cell_number: phone,
        dob: (_a2 = data.dob) == null ? void 0 : _a2.split("-").reverse().join("/"),
        passport_data: data.require_visa === "yes" && data.passport_data ? __spreadProps(__spreadValues({}, data.passport_data), {
          nationality: data.nationality || ""
        }) : void 0
      });
      const newUser = yield updateProfile({ user: body, locale });
      if (onSuccess) {
        onSuccess(newUser);
      }
    } catch (err) {
    }
  });
  const Body = /* @__PURE__ */ jsxs45(Fragment8, { children: [
    /* @__PURE__ */ jsxs45("div", { children: [
      /* @__PURE__ */ jsx48("h2", { className: "pb-1 text-2xl font-semibold", children: t("auth_common:complete_your_account") }),
      /* @__PURE__ */ jsx48("p", { className: "text-sm", children: t("auth_common:complete_your_account_desc") })
    ] }),
    /* @__PURE__ */ jsx48(FormItems_default, { error }),
    showVisaCheckbox && /* @__PURE__ */ jsxs45(Fragment8, { children: [
      /* @__PURE__ */ jsx48(
        Controller15,
        {
          name: "require_visa",
          control: methods.control,
          rules: { required: t("auth_common:validation.required") },
          render: ({ field }) => {
            var _a;
            return /* @__PURE__ */ jsx48(
              ToggleGroup2,
              __spreadProps(__spreadValues({
                label: t("auth_common:require_visa"),
                type: "single",
                options: [
                  { text: t("auth_common:yes"), value: "yes" },
                  { text: t("auth_common:no"), value: "no" }
                ]
              }, field), {
                value: field.value || "",
                error: (_a = methods.formState.errors.require_visa) == null ? void 0 : _a.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx48(AnimatePresence11, { initial: false, children: needVisa === "yes" && /* @__PURE__ */ jsxs45(
        motion11.div,
        {
          className: "space-y-4 overflow-hidden",
          initial: { height: 0 },
          animate: { height: "auto" },
          exit: { height: 0 },
          children: [
            /* @__PURE__ */ jsx48(
              Input13,
              __spreadProps(__spreadValues({
                id: "passport",
                label: t("auth_common:passport"),
                placeholder: t("auth_common:passport")
              }, methods.register("passport_data.passport", {
                required: t("auth_common:validation.required"),
                minLength: {
                  value: 7,
                  message: t("auth_common:validation.min_length", { min: 7 })
                },
                maxLength: {
                  value: 25,
                  message: t("auth_common:validation.max_length", { max: 25 })
                }
              })), {
                onChange: (value) => {
                  methods.setValue("passport_data.passport", value);
                },
                error: ((_d = (_c = methods.formState.errors.passport_data) == null ? void 0 : _c.passport) == null ? void 0 : _d.message) || ((_f = (_e = error == null ? void 0 : error.errors) == null ? void 0 : _e.passport) == null ? void 0 : _f[0])
              })
            ),
            /* @__PURE__ */ jsxs45("div", { className: "flex flex-col gap-4 py-1 sm:flex-row", children: [
              /* @__PURE__ */ jsx48(
                Input13,
                __spreadProps(__spreadValues({
                  type: "date",
                  groupClassName: "grow",
                  pattern: "\\d{4}-\\d{2}-\\d{2}",
                  max: (/* @__PURE__ */ new Date()).toISOString().split("T")[0],
                  label: t("auth_common:issue_date")
                }, methods.register("passport_data.issue_date", {
                  required: t("auth_common:validation.required")
                })), {
                  onChange: (value) => {
                    methods.setValue("passport_data.issue_date", value);
                  },
                  error: ((_h = (_g = methods.formState.errors.passport_data) == null ? void 0 : _g.issue_date) == null ? void 0 : _h.message) || ((_j = (_i = error == null ? void 0 : error.errors) == null ? void 0 : _i.issue_date) == null ? void 0 : _j[0])
                })
              ),
              /* @__PURE__ */ jsx48(
                Input13,
                __spreadProps(__spreadValues({
                  type: "date",
                  groupClassName: "grow",
                  min: (/* @__PURE__ */ new Date()).toISOString().split("T")[0],
                  pattern: "\\d{4}-\\d{2}-\\d{2}",
                  label: t("auth_common:expiry_date")
                }, methods.register("passport_data.expiry_date", {
                  required: t("auth_common:validation.required")
                })), {
                  onChange: (value) => {
                    methods.setValue("passport_data.expiry_date", value);
                  },
                  error: ((_l = (_k = methods.formState.errors.passport_data) == null ? void 0 : _k.expiry_date) == null ? void 0 : _l.message) || ((_n = (_m = error == null ? void 0 : error.errors) == null ? void 0 : _m.expiry_date) == null ? void 0 : _n[0])
                })
              )
            ] })
          ]
        }
      ) })
    ] })
  ] });
  if (flatLayout) {
    return Body;
  }
  return isMobile ? /* @__PURE__ */ jsx48(
    BottomSheet3,
    {
      fullscreen: true,
      show,
      onClose,
      stickyHeader: ({ close }) => /* @__PURE__ */ jsx48(Header, { onClose: close }),
      stickyFooter: () => /* @__PURE__ */ jsx48(Footer, { isPending }),
      body: () => {
        return /* @__PURE__ */ jsx48(FormProvider5, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx48(
          "form",
          {
            id: "checkout-complete-profile",
            onSubmit: methods.handleSubmit(onSubmit),
            className: twMerge10("space-y-4 px-4", className),
            children: Body
          }
        ) }));
      },
      className: "z-[999] pb-28"
    }
  ) : /* @__PURE__ */ jsx48(
    Modal3,
    {
      className: "overflow-y-auto pb-24 [&>header]:z-50",
      zIndex: 999,
      show,
      onClose,
      stickyHeader: ({ close }) => /* @__PURE__ */ jsx48(Header, { onClose: close }),
      stickyFooter: () => /* @__PURE__ */ jsx48(Footer, { isPending }),
      body: () => {
        return /* @__PURE__ */ jsx48(FormProvider5, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx48(
          "form",
          {
            id: "checkout-complete-profile",
            onSubmit: methods.handleSubmit(onSubmit),
            className: twMerge10("space-y-4 px-4", className),
            children: Body
          }
        ) }));
      }
    }
  );
};
var Header = ({ onClose }) => {
  const { t } = useTranslation42(["auth_common"]);
  return /* @__PURE__ */ jsx48("div", { className: "bg-body z-50 flex w-full items-center justify-between rounded-t-md p-2 pb-1", children: /* @__PURE__ */ jsx48(
    Back_default,
    {
      tabIndex: -1,
      className: "mb-0",
      text: t("auth_common:change_tickets"),
      onClick: onClose
    }
  ) });
};
var Footer = ({ isPending }) => {
  const { t } = useTranslation42(["auth_common"]);
  return /* @__PURE__ */ jsx48("div", { className: "p-4", children: /* @__PURE__ */ jsx48(Button31, { className: "w-full", type: "submit", form: "checkout-complete-profile", loading: isPending, children: /* @__PURE__ */ jsx48("span", { className: "text-xl font-bold", children: t("auth_common:confirm") }) }) });
};

export {
  FanIdCheckPoint,
  FormItems_default,
  ForceUpdateProfileCheckPoint,
  NafathCheckPoint,
  OtpCheckPoint,
  ProfileCheckPoint,
  useAuthOtp,
  AuthForm,
  AuthModal_default,
  LogoutModal_default,
  ProfileMenu,
  UserProfile,
  ProfileTop,
  WithAuth,
  AuthProvider
};
//# sourceMappingURL=chunk-OXIGTPLK.mjs.map